import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, DATA_CHANGED } from "./api/apiService";
import { shortTime, transferTimes, transferOnDay, driverCandidate } from "./scheduling";
import "./App.css";
import "./Mobile.css";
import "./Operational.css";
import "./Design.css";
import "./Forms.css";
import "./DesktopPolish.css";
import "./MobilePolish.css";
import TransferMap from "./components/TransferMap";
import "./ClientPortal.css";
import DriverPreview from "./driver/DriverPreview";
import { availabilityStates } from "./availabilityStates";
import { AuthGate, useAuth } from "./auth/AuthProvider";
import { keycloakClient } from "./auth/keycloak";
import { useI18n } from "./i18n/I18nProvider";
import LanguageSwitcher from "./i18n/LanguageSwitcher";
import Brand from "./Brand";
import SidebarProfile from "./SidebarProfile";
import BookingRequestReview from "./BookingRequestReview";

const navigation = [
  { label: "navigation.planning", items: [["navigation.dayByDay", "/dashboard"], ["navigation.calendar", "/calendar"], ["navigation.dispatch", "/dispatch"], ["navigation.bookingRequests", "/booking-requests"], ["navigation.transfers", "/transfers"], ["navigation.projects", "/projects"]] },
  { label: "navigation.resources", items: [["navigation.availability", "/availability"], ["navigation.cars", "/cars"]] },
  { label: "navigation.administration", items: [["navigation.organizations", "/organizations"], ["navigation.accounts", "/accounts"], ["navigation.audit", "/audit-log"]] },
  { label: "navigation.analysis", items: [["navigation.statistics", "/statistics"]] },
];

const dialogFocusableSelector = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/** Shared focus, keyboard and scroll behaviour for mounted modal dialogs. */
function useDialogAccessibility(onClose) {
  const dialogRef = useRef(null);
  const restoreFocusRef = useRef(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusFirst = () => {
      const focusable = dialog.querySelectorAll(dialogFocusableSelector);
      (focusable[0] || dialog).focus();
    };
    const frame = window.requestAnimationFrame(focusFirst);
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialog.querySelectorAll(dialogFocusableSelector));
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      restoreFocusRef.current?.focus?.();
    };
  }, [onClose]);
  return dialogRef;
}
const isoDate = (value = new Date()) => {
  if (typeof value === "string") return value.slice(0, 10);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const addDays = (value, days) => {
  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  const date = new Date(year, month - 1, day + days, 12);
  return isoDate(date);
};
const localeTag = (locale) => ({ pl: "pl-PL", en: "en-GB", de: "de-DE" }[locale] || "pl-PL");
const formatOfficeDate = (value, locale, options) =>
  new Intl.DateTimeFormat(localeTag(locale), options).format(new Date(`${value}T12:00:00`));
const projectReference = (projectId, annotation, t, fallback = "—") =>
  annotation || (projectId ? `${t("forms.project")} #${projectId}` : fallback);
const boundedDates = (start, end) => {
  const dates = [];
  let cursor = new Date(`${start}T12:00:00`);
  const last = new Date(`${end}T12:00:00`);
  for (let index = 0; index < 63 && cursor <= last; index += 1) {
    dates.push(isoDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

const initialRange = () => {
  const params = new URLSearchParams(window.location.search);
  const start = params.get("start") || isoDate();
  return { start, end: params.get("end") || addDays(start, 13) };
};
const projectRange = () => {
  const params = new URLSearchParams(window.location.search);
  const start = params.get("start");
  const end = params.get("end");
  return start && end ? { start, end } : { start: "", end: "" };
};

function useRequest(request, dependencies) {
  const [dataRevision, setDataRevision] = useState(0);
  useEffect(() => {
    const refresh = () => setDataRevision(value => value + 1);
    const storage = event => { if (event.key === DATA_CHANGED) refresh(); };
    window.addEventListener(DATA_CHANGED, refresh);
    window.addEventListener("storage", storage);
    return () => { window.removeEventListener(DATA_CHANGED, refresh); window.removeEventListener("storage", storage); };
  }, []);
  const [state, setState] = useState({
    loading: true,
    refreshing: false,
    data: null,
    error: null,
  });
  useEffect(() => {
    let active = true;
    // Preserve the last completed view while data is refreshed, instead of
    // replacing the page with a full loading surface on every filter change.
    setState(previous => ({ loading: !previous.data, refreshing: Boolean(previous.data), data: previous.data, error: null }));
    request()
      .then((data) => active && setState({ loading: false, refreshing: false, data, error: null }))
      .catch(
        (error) =>
          active &&
          setState(previous => ({ loading: false, refreshing: false, data: previous.data, error: previous.data ? null : error.message })),
      );
    return () => {
      active = false;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, dataRevision]);
  return { ...state, retry: () => setDataRevision(value => value + 1) };
}
function Notice({ state }) {
  const { t } = useI18n();
  return state.loading ? (
    <div className="loading-surface" role="status" aria-live="polite">
      <span className="sr-only">{t('common.loading')}</span>
      <i className="loading-line loading-line-title" />
      <i className="loading-line" />
      <i className="loading-line loading-line-short" />
    </div>
  ) : state.refreshing ? (
    <div className="inline-refresh" role="status" aria-live="polite"><span>{t('common.refreshing')}</span></div>
  ) : state.error ? (
    <div className="status error" role="alert"><span>{state.error}</span><button type="button" onClick={state.retry}>{t('common.retry')}</button></div>
  ) : null;
}
const pageGuides = {
  Dashboard: { copy: "Choose a day to review scheduled transfers. Expand assignments only when you need vehicle and driver detail.", legend: [["time", "Time", "planned transfer window"], ["amber", "Amber edge", "needs operational attention"]] },
  Calendar: { copy: "Days run left to right and projects stack vertically. Resource bands at the bottom highlight daily capacity requirements.", legend: [["blue", "Project row", "linked transfers"], ["green", "Green capacity", "resources available"], ["red", "Red capacity", "resource shortfall"]] },
  Statistics: { copy: "Use this view for volume, utilisation and daily capacity signals. A risk is a planning prompt; confirm timing in Calendar.", legend: [["blue", "Blue", "activity and volume"], ["green", "Green", "active capacity"], ["amber", "Amber", "capacity risk"]] },
  Dispatch: { copy: "Dispatch is the daily assignment queue. Choose a safe candidate, review the confirmation, then edit the haul from the transfer when needed.", legend: [["green", "Ready", "available with no time clash"], ["amber", "Confirm", "standby or no declaration"], ["red", "Conflict", "assigned while unavailable"]] },
  Availability: { copy: "Filter declarations, workload and licences. The board highlights assigned work and flags an assignment made while unavailable or on vacation.", legend: [["available", "A · Available", "declared available"], ["unavailable", "U · Unavailable", "not available"], ["standby", "S · Standby", "on standby"], ["vacation", "V · Vacation", "on leave"], ["assigned", "Number", "assigned transfers; orange edge"], ["conflict", "Red edge", "assigned while unavailable"]] },
  "Day by Day": { copy: "A chronological operational list for the selected day, useful for a quick dispatch handover.", legend: [["time", "Time", "transfer window"], ["blue", "Blue text", "route and assigned vehicle"]] },
  Projects: { copy: "Open a project to inspect its transfers in a date range or create a project.", legend: [["blue", "Project link", "open its details"], ["green", "Add", "create a project or transfer locally"]] },
  Cars: { copy: "Fleet directory with vehicle groups, capacity and required licence. The colour control stores the vehicle colour in the local database.", legend: [["color", "Color", "actual vehicle colour"], ["blue", "Group chip", "vehicle classification"], ["green", "Add vehicle", "creates a local fleet record"]] },
  Accounts: { copy: "Operational directory of users, clients and drivers.", legend: [["blue", "Role", "account security level"], ["time", "Licence", "driver qualification"]] },
};
const officePageTitleKeys = {
  Dashboard: "navigation.dashboard",
  Statistics: "navigation.statistics",
  "Day by Day": "navigation.dayByDay",
  Transfers: "navigation.transfers",
  Projects: "navigation.projects",
  Calendar: "navigation.calendar",
  Cars: "navigation.cars",
  Accounts: "navigation.accounts",
  "Audit log": "navigation.audit",
  Dispatch: "navigation.dispatch",
  Availability: "navigation.availability",
};
const officePageDescriptionKeys = {
  Statistics: "office.descriptions.statistics",
  "Day by Day": "office.descriptions.dayByDay",
  Transfers: "office.descriptions.transfers",
  Projects: "office.descriptions.projects",
  Calendar: "office.descriptions.calendar",
  Cars: "office.descriptions.cars",
  Accounts: "office.descriptions.accounts",
  "Audit log": "office.descriptions.audit",
  Dispatch: "office.descriptions.dispatch",
  Availability: "office.descriptions.availability",
};
const officePageGuideKeys = {
  Dashboard: "dashboard", Calendar: "calendar", Statistics: "statistics", Dispatch: "dispatch",
  Availability: "availability", "Day by Day": "dayByDay", Transfers: "transfers", Projects: "projects", Cars: "cars", Accounts: "accounts",
};
function Page({ title, description, guideKey, children, showFilters = true }) {
  const { t } = useI18n();
  const [activeFilters, setActiveFilters] = useState(0);
  useEffect(() => {
    const receive = event => setActiveFilters(Number(event.detail) || 0);
    window.addEventListener("driveros:filters-active", receive);
    return () => window.removeEventListener("driveros:filters-active", receive);
  }, []);
  const guide = pageGuides[title] || { copy: "Use the controls above to focus the operational data you need.", legend: [] };
  const translatedGuideLegend = ["Cars", "Accounts", "Projects", "Dispatch", "Availability"].includes(title)
    ? t(`office.guideLegends.${title.toLowerCase()}`)
    : guide.legend;
  const displayTitle = officePageTitleKeys[title] ? t(officePageTitleKeys[title]) : title;
  const displayDescription = officePageDescriptionKeys[title] ? t(officePageDescriptionKeys[title]) : description;
  return (
    <section className="page">
      <header className="page-heading">
        <div>
          <h1>{displayTitle}</h1>
          <p>{displayDescription}</p>
        </div>
        <div className="page-heading-actions">
          <details key={title} className="page-guide">
            <summary>{t('common.howItWorks')}</summary>
            <div className="page-guide-panel">
              <p>{guideKey === "client" ? t("client.guide") : guideKey ? t(`office.guides.${guideKey}`) : officePageGuideKeys[title] ? t(`office.guides.${officePageGuideKeys[title]}`) : guide.copy}</p>
              {translatedGuideLegend.length > 0 && <div className="page-guide-legend">{translatedGuideLegend.map(([tone, label, detail]) => <span key={label} className={`guide-key ${tone}`}><i /> <b>{label}</b> <small>{detail}</small></span>)}</div>}
            </div>
          </details>
          {showFilters && <button
            type="button"
            className="mobile-header-filter"
            onClick={() => window.dispatchEvent(new Event("driveros:toggle-filters"))}
          >
            <span aria-hidden="true" />
            {t('common.filters')}
            {activeFilters > 0 && <em className="mobile-filter-count">{activeFilters}</em>}
          </button>}
        </div>
      </header>
      {children}
    </section>
  );
}

function FilterPanel({ children, label = "Filters", activeCount = 0, onClear }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const toggle = () => setOpen((value) => !value);
    window.addEventListener("driveros:toggle-filters", toggle);
    return () => window.removeEventListener("driveros:toggle-filters", toggle);
  }, []);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("driveros:filters-active", { detail: activeCount }));
  }, [activeCount]);
  return <details className="filter-panel" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary><span className="filter-panel-icon" aria-hidden="true" /><b>{label === 'Filters' ? t('common.filters') : label}</b>{activeCount > 0 && <em className="filter-active-count">{activeCount}</em>}<small>{t('common.adjustView')}</small></summary>
    <div className="filter-panel-content">{activeCount > 0 && onClear && <button type="button" className="filter-panel-clear" onClick={onClear}>{t('common.clearFilters')}</button>}{children}</div>
  </details>;
}

function Dashboard() {
  const { t } = useI18n();
  const [selected, setSelected] = useState(() => initialRange().start);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [revision, setRevision] = useState(0);
  const state = useRequest(() => api.dashboard(selected), [selected, revision]);
  return (
    <Page
      title="Day by Day"
      description={t("office.dashboard.description", { date: selected })}
    >
      <FilterPanel label={t("office.dashboard.filters")} activeCount={selected !== isoDate() ? 1 : 0} onClear={() => setSelected(isoDate())}><div className="range-controls">
        <label>
          {t("office.dashboard.date")}{" "}
          <input
            type="date"
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
          />
        </label>
      </div></FilterPanel>
      <Notice state={state} />
      {state.data && (
        <>
          <DashboardCapacity date={selected} />
          <div className="transfer-list dashboard-list">
            {state.data.data.map((item) => (
              <article
                className={`transfer-card dashboard-transfer dashboard-transfer-open tone-${transferTone(item.project_status)}`}
                key={item.id}
              >
                <button
                  type="button"
                  className="dashboard-transfer-summary"
                  aria-label={`${t("forms.transferDetails")}: ${item.tweet || t("office.dashboard.untitled")}`}
                  onClick={() => setSelectedTransfer(item)}
                >
                  <time title={transferTimes(item)}>
                    <b>{shortTime(item.departure_time)}–{shortTime(item.arrival_time)}</b>
                    {item.arrival_date && item.arrival_date !== item.departure_date && <small>{item.departure_date} → {item.arrival_date}</small>}
                  </time>
                  <div>
                    <strong>{item.tweet || t("office.dashboard.untitled")}</strong>
                    <p>
                      {item.project_annotation ||
                        item.company_name ||
                        item.client_signature ||
                        projectReference(item.project_id, null, t, t("office.dashboard.noAnnotation"))}
                    </p>
                  </div>
                  <div className="transfer-facts">
                    <b title={t("office.dashboard.passengers")}>
                      {t("office.dashboard.passengers")}: {item.num_passengers ?? "—"}
                    </b>
                    <span>
                      {item.cars || t("office.dashboard.unassigned")}
                      {item.drivers ? ` · ${item.drivers}` : ""}
                    </span>
                  </div>
                </button>
              </article>
            ))}
            {state.data.count === 0 && (
              <p className="status">{t("office.dashboard.empty")}</p>
            )}
          </div>
          <FleetTimelinePanel date={selected} />
        </>
      )}
      {selectedTransfer &&
        createPortal(
          <ProjectTransferDialog
            transfer={state.data?.data.find((item) => item.id === selectedTransfer.id) || selectedTransfer}
            onClose={() => setSelectedTransfer(null)}
            onChanged={() => setRevision((value) => value + 1)}
            allowEdit
          />,
          document.body,
        )}
    </Page>
  );
}

function DashboardCapacity({ date }) {
  const { t } = useI18n();
  const state = useRequest(() => api.calendar(date, date), [date]);
  if (state.loading || state.error) return null;
  const resource = (state.data?.resources || []).find((item) => item.date === date);
  if (!resource) return null;
  const carShortage = resource.enough_for_required === false;
  const driverShortage = resource.enough === false;
  const shortage = carShortage || driverShortage;
  const cars = mobileResourceText(resource.cars, "group");
  const drivers = mobileResourceText(resource.drivers, "license");
  return (
    <NavLink
      className={`dashboard-capacity ${shortage ? "shortage" : "covered"}`}
      to={`/calendar?start=${date}&end=${date}`}
    >
      <span className="dashboard-capacity-icon">{shortage ? "!" : "✓"}</span>
      <div>
        <b>{shortage ? t("office.capacity.shortage") : t("office.capacity.covered")}</b>
        <small>{t("office.capacity.cars")} {cars} · {t("office.capacity.drivers")} {drivers}</small>
      </div>
      <em>{t("office.capacity.openCalendar")} →</em>
    </NavLink>
  );
}

const statisticsCopy = {
  pl: {
    completed: "Wykonane transfery", completedHint: "Zakończone przejazdy z nieanulowanych projektów.",
    month: "Ten miesiąc", year: "Ten rok", projects: "projekty", drivers: "kierowcy",
    vsMonth: "względem tego miesiąca rok temu", vsYtd: "względem analogicznego okresu rok temu",
    monthlyTitle: "Miesiące rok do roku", monthlyHint: "Wybierz dowolny rok i porównaj go z poprzednim.", chooseYear: "Wybierz rok",
    yearlyTitle: "Historia roczna", yearlyHint: "Skala wykonanych przejazdów we wszystkich dostępnych latach.",
    transfers: "Transfery", currentYear: "Bieżący rok", previousYear: "Poprzedni rok",
    rankingPeriod: "Okres rankingu", rankingYear: "Rok rankingu", rankingMonth: "Miesiąc rankingu", topSupervisors: "Najaktywniejsi opiekunowie", topDrivers: "Najczęściej przypisywani kierowcy",
    supervisorHint: "Według liczby wykonanych transferów w prowadzonych projektach.", driverHint: "Według liczby wykonanych transferów z przypisanym kierowcą.",
    noRank: "Brak przypisań w tym okresie.", days: "dni", vehicles: "pojazdów", noLicence: "Brak zapisanego prawa jazdy", unnamed: "Bez nazwy",
    annualDetails: "Szczegóły roczne", supervisors: "Opiekunowie",
    title: "Statystyki", description: "Transfery, wykorzystanie floty i dostępność kierowców.", filters: "Filtry statystyk", from: "Od", to: "Do", today: "Dzisiaj", last7: "Ostatnie 7 dni", last14: "Ostatnie 14 dni",
    operational: "Wybrany zakres operacyjny", vehiclesInUse: "Pojazdy w użyciu", driversAssigned: "Przypisani kierowcy", uniqueFleet: "unikalne przypisane / aktywna flota", uniqueDrivers: "unikalni przypisani / aktywni kierowcy", capacityRisks: "Ryzyko pojemności", risksExceeded: "dni z przekroczonym zapotrzebowaniem", allCovered: "wszystkie wymagania dzienne pokryte", capacityClear: "Pojemność bez ryzyka", capacityHint: "Wymagane grupy pojazdów i uprawnienia porównane z aktywnymi zasobami.", car: "pojazd", driver: "kierowca", ok: "OK",
    trend: "Trend transferów", trendHint: "Dzienna liczba transferów i projektów.", dailyTransfers: "Transfery dzienne", dailyVolume: "Liczba według dnia wyjazdu.", usedVehicles: "Najczęściej używane pojazdy", selectedTransfers: "Transfery w wybranym okresie", assignedDrivers: "Najczęściej przypisywani kierowcy", selectedAssignments: "Przypisania w wybranym okresie", dailyOperations: "Operacje dzienne", dailyCapacityHint: "Pojemność jest oceniana według grup pojazdów i uprawnień kierowców.", day: "Dzień", cars: "Pojazdy", unassigned: "Nieprzypisane", capacity: "Pojemność",
    health: "Stan operacyjny", healthHint: "Sygnały wymagające sprawdzenia przed rozpoczęciem pracy.", missingDrivers: "Bez kierowcy", missingVehicles: "Bez pojazdu", availabilityConflicts: "Konflikty dostępności", availabilityCoverage: "Pokrycie deklaracji", declarations: "deklaracji w wybranym zakresie", utilisation: "Wykorzystanie zasobów", utilisationHint: "Dni użycia w wybranym zakresie.", idleVehicles: "Niewykorzystane pojazdy", idleDrivers: "Niewykorzystani kierowcy", activeDays: "aktywnych dni",
    definition: "Wykonany transfer = planowana godzina przyjazdu już minęła, projekt nie jest anulowany, a rekord nie jest usunięty.",
  },
  en: {
    completed: "Completed transfers", completedHint: "Past journeys from non-cancelled projects.",
    month: "This month", year: "This year", projects: "projects", drivers: "drivers",
    vsMonth: "versus the same month last year", vsYtd: "versus the same period last year",
    monthlyTitle: "Monthly year-on-year", monthlyHint: "Choose any year and compare it with the previous year.", chooseYear: "Choose year",
    yearlyTitle: "Annual history", yearlyHint: "Completed journey volume across all available years.",
    transfers: "Transfers", currentYear: "Current year", previousYear: "Previous year",
    rankingPeriod: "Ranking period", rankingYear: "Ranking year", rankingMonth: "Ranking month", topSupervisors: "Most active supervisors", topDrivers: "Most assigned drivers",
    supervisorHint: "Ranked by completed transfers across supervised projects.", driverHint: "Ranked by completed transfers with a driver assignment.",
    noRank: "No assignments in this period.", days: "days", vehicles: "vehicles", noLicence: "No licence recorded", unnamed: "Unnamed",
    annualDetails: "Annual details", supervisors: "Supervisors",
    title: "Statistics", description: "Transfer, fleet utilisation and driver availability overview.", filters: "Statistics filters", from: "From", to: "To", today: "Today", last7: "Last 7 days", last14: "Last 14 days",
    operational: "Selected operational range", vehiclesInUse: "Vehicles in use", driversAssigned: "Drivers assigned", uniqueFleet: "unique assigned / active fleet", uniqueDrivers: "unique assigned / active drivers", capacityRisks: "Capacity risks", risksExceeded: "days with resource demand exceeded", allCovered: "all daily requirements covered", capacityClear: "Capacity check clear", capacityHint: "Required vehicle groups and driver licences compared with active resources.", car: "car", driver: "driver", ok: "OK",
    trend: "Transfer trend", trendHint: "Daily transfer and project volumes.", dailyTransfers: "Daily transfers", dailyVolume: "Volume by departure day.", usedVehicles: "Most used vehicles", selectedTransfers: "Transfers in the selected period", assignedDrivers: "Most assigned drivers", selectedAssignments: "Transfer assignments in the selected period", dailyOperations: "Daily operations", dailyCapacityHint: "Capacity is assessed by vehicle group and driver licence.", day: "Day", cars: "Cars", unassigned: "Unassigned", capacity: "Capacity",
    health: "Operational health", healthHint: "Signals to check before the work starts.", missingDrivers: "Without driver", missingVehicles: "Without vehicle", availabilityConflicts: "Availability conflicts", availabilityCoverage: "Declaration coverage", declarations: "declarations in selected range", utilisation: "Resource utilisation", utilisationHint: "Days used in the selected range.", idleVehicles: "Idle vehicles", idleDrivers: "Idle drivers", activeDays: "active days",
    definition: "Completed transfer = its planned arrival has passed, its project is not cancelled and the record is not deleted.",
  },
  de: {
    completed: "Durchgeführte Transfers", completedHint: "Beendete Fahrten aus nicht stornierten Projekten.",
    month: "Dieser Monat", year: "Dieses Jahr", projects: "Projekte", drivers: "Fahrer",
    vsMonth: "gegenüber demselben Monat im Vorjahr", vsYtd: "gegenüber dem Vorjahreszeitraum",
    monthlyTitle: "Monate im Jahresvergleich", monthlyHint: "Wählen Sie ein Jahr und vergleichen Sie es mit dem Vorjahr.", chooseYear: "Jahr wählen",
    yearlyTitle: "Jahresverlauf", yearlyHint: "Volumen der durchgeführten Fahrten in allen verfügbaren Jahren.",
    transfers: "Transfers", currentYear: "Aktuelles Jahr", previousYear: "Vorjahr",
    rankingPeriod: "Rankingzeitraum", rankingYear: "Rankingjahr", rankingMonth: "Rankingmonat", topSupervisors: "Aktivste Betreuer", topDrivers: "Meistzugewiesene Fahrer",
    supervisorHint: "Nach durchgeführten Transfers in betreuten Projekten.", driverHint: "Nach durchgeführten Transfers mit Fahrerzuweisung.",
    noRank: "Keine Zuweisungen in diesem Zeitraum.", days: "Tage", vehicles: "Fahrzeuge", noLicence: "Kein Führerschein hinterlegt", unnamed: "Ohne Namen",
    annualDetails: "Jahresdetails", supervisors: "Betreuer",
    title: "Statistiken", description: "Übersicht über Transfers, Fuhrparknutzung und Fahrerverfügbarkeit.", filters: "Statistikfilter", from: "Von", to: "Bis", today: "Heute", last7: "Letzte 7 Tage", last14: "Letzte 14 Tage",
    operational: "Gewählter Einsatzzeitraum", vehiclesInUse: "Eingesetzte Fahrzeuge", driversAssigned: "Zugewiesene Fahrer", uniqueFleet: "eindeutig zugewiesen / aktive Flotte", uniqueDrivers: "eindeutig zugewiesen / aktive Fahrer", capacityRisks: "Kapazitätsrisiken", risksExceeded: "Tage mit überschrittenem Bedarf", allCovered: "alle täglichen Anforderungen gedeckt", capacityClear: "Kapazitätsprüfung ohne Risiko", capacityHint: "Benötigte Fahrzeuggruppen und Führerscheine im Vergleich zu aktiven Ressourcen.", car: "Fahrzeug", driver: "Fahrer", ok: "OK",
    trend: "Transfertrend", trendHint: "Tägliches Transfer- und Projektvolumen.", dailyTransfers: "Tägliche Transfers", dailyVolume: "Volumen nach Abfahrtstag.", usedVehicles: "Meistgenutzte Fahrzeuge", selectedTransfers: "Transfers im gewählten Zeitraum", assignedDrivers: "Meistzugewiesene Fahrer", selectedAssignments: "Transferzuweisungen im gewählten Zeitraum", dailyOperations: "Täglicher Betrieb", dailyCapacityHint: "Kapazität wird nach Fahrzeuggruppe und Führerschein bewertet.", day: "Tag", cars: "Fahrzeuge", unassigned: "Nicht zugewiesen", capacity: "Kapazität",
    health: "Betriebsstatus", healthHint: "Signale, die vor Arbeitsbeginn geprüft werden sollten.", missingDrivers: "Ohne Fahrer", missingVehicles: "Ohne Fahrzeug", availabilityConflicts: "Verfügbarkeitskonflikte", availabilityCoverage: "Erklärungsabdeckung", declarations: "Erklärungen im gewählten Zeitraum", utilisation: "Ressourcenauslastung", utilisationHint: "Einsatztage im gewählten Zeitraum.", idleVehicles: "Ungenutzte Fahrzeuge", idleDrivers: "Ungenutzte Fahrer", activeDays: "aktive Tage",
    definition: "Durchgeführt = geplante Ankunft liegt zurück, Projekt ist nicht storniert und Datensatz ist nicht gelöscht.",
  },
};

const statisticsNouns = {
  pl: { project: ["projekt", "projekty", "projektów"], driver: ["kierowca", "kierowcy", "kierowców"], day: ["dzień", "dni", "dni"], vehicle: ["pojazd", "pojazdy", "pojazdów"] },
  en: { project: ["project", "projects", "projects"], driver: ["driver", "drivers", "drivers"], day: ["day", "days", "days"], vehicle: ["vehicle", "vehicles", "vehicles"] },
  de: { project: ["Projekt", "Projekte", "Projekte"], driver: ["Fahrer", "Fahrer", "Fahrer"], day: ["Tag", "Tage", "Tage"], vehicle: ["Fahrzeug", "Fahrzeuge", "Fahrzeuge"] },
};
const statisticsNoun = (locale, kind, value) => {
  const forms = (statisticsNouns[locale] || statisticsNouns.en)[kind];
  const number = Number(value || 0);
  if (locale !== "pl") return number === 1 ? forms[0] : forms[2];
  if (number === 1) return forms[0];
  const last = number % 10, lastTwo = number % 100;
  return last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14) ? forms[1] : forms[2];
};

function Statistics() {
  const { locale } = useI18n();
  const copy = statisticsCopy[locale] || statisticsCopy.en;
  const [rankingPeriod, setRankingPeriod] = useState("month");
  const [comparisonYear, setComparisonYear] = useState(null);
  const [rankingMonth, setRankingMonth] = useState(null);
  const [range, setRange] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const end = params.get("end") || isoDate();
    return { start: params.get("start") || addDays(end, -6), end };
  });
  const state = useRequest(
    () => api.statistics(range.start, range.end),
    [range.start, range.end],
  );
  const historyState = useRequest(() => api.statisticsHistory(), []);
  const summary = state.data?.summary;
  const daily = state.data?.daily || [];
  const total = (value) => Number(value || 0);
  const setDays = (days) => {
    const end = isoDate();
    setRange({ start: addDays(end, 1 - days), end });
  };
  const chartData = daily.map((row) => ({
    ...row,
    label: new Intl.DateTimeFormat(localeTag(locale), {
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(new Date(`${row.day}T12:00:00`)),
    transfers: total(row.transfers),
    projects: total(row.projects),
  }));
  const alerts = chartData.filter(
    (row) => total(row.car_shortage) || total(row.driver_shortage),
  );
  const history = historyState.data;
  const historySummary = history?.summary || {};
  const monthRows = history?.monthly || [];
  const historyYear = Number(history?.year || new Date().getFullYear());
  const availableYears = (history?.yearly || []).map((row) => Number(row.year)).sort((a, b) => b - a);
  const selectedHistoryYear = comparisonYear || historyYear;
  const selectedRankingMonth = rankingMonth || Number(history?.month || new Date().getMonth() + 1);
  const rankingKey = rankingPeriod === "year" ? String(selectedHistoryYear) : `${selectedHistoryYear}-${String(selectedRankingMonth).padStart(2, "0")}`;
  const supervisorRanking = history?.supervisors?.[rankingPeriod === "year" ? "years" : "months"]?.[rankingKey] || [];
  const driverRanking = history?.drivers?.[rankingPeriod === "year" ? "years" : "months"]?.[rankingKey] || [];
  const monthlyData = Array.from({ length: 12 }, (_, index) => {
    const label = new Intl.DateTimeFormat(localeTag(locale), { month: "short" }).format(new Date(2024, index, 1));
    const current = monthRows.find((row) => Number(row.year) === selectedHistoryYear && Number(row.month) === index + 1);
    const previous = monthRows.find((row) => Number(row.year) === selectedHistoryYear - 1 && Number(row.month) === index + 1);
    return { label, current: total(current?.transfers), previous: total(previous?.transfers) };
  });
  const yearlyData = (history?.yearly || []).map((row) => ({
    ...row, year: String(row.year), transfers: total(row.transfers), projects: total(row.projects), drivers: total(row.drivers),
  }));
  const growth = (current, previous) => previous ? `${current >= previous ? "+" : ""}${Math.round(((current - previous) / previous) * 100)}%` : "—";
  return (
    <Page
      title={copy.title}
      description={copy.description}
    >
      <FilterPanel label={copy.filters}><div className="stats-controls">
        <div className="range-controls">
          <label>
            {copy.from}{" "}
            <input
              type="date"
              value={range.start}
              onChange={(event) =>
                setRange(({ end }) => ({
                  start: event.target.value,
                  end: end < event.target.value ? event.target.value : end,
                }))
              }
            />
          </label>
          <label>
            {copy.to}{" "}
            <input
              type="date"
              value={range.end}
              min={range.start}
              onChange={(event) =>
                setRange(({ start }) => ({ start, end: event.target.value }))
              }
            />
          </label>
        </div>
        <div className="stats-quick-actions">
          <button onClick={() => setDays(1)}>{copy.today}</button>
          <button onClick={() => setDays(7)}>{copy.last7}</button>
          <button onClick={() => setDays(14)}>{copy.last14}</button>
        </div>
      </div></FilterPanel>
      <Notice state={state} />
      {summary && (
        <>
          <div className="stats-layout">
          <div className="stats-management">{history ? <>
            <section className="stats-section-heading">
              <div><span>{historyYear}</span><h2>{copy.completed}</h2><p>{copy.completedHint}</p></div>
              <small title={copy.definition}>{copy.definition}</small>
            </section>
            <section className="stats-kpis stats-history-kpis">
              <StatCard label={copy.month} value={total(historySummary.month_transfers)} detail={`${total(historySummary.month_projects)} ${statisticsNoun(locale, "project", historySummary.month_projects)} · ${total(historySummary.month_drivers)} ${statisticsNoun(locale, "driver", historySummary.month_drivers)}`} tone="blue" />
              <StatCard label={copy.vsMonth} value={growth(total(historySummary.month_transfers), total(historySummary.previous_month_transfers))} detail={`${total(historySummary.previous_month_transfers)} · ${historyYear - 1}`} tone="violet" />
              <StatCard label={copy.year} value={total(historySummary.year_transfers)} detail={`${total(historySummary.year_projects)} ${statisticsNoun(locale, "project", historySummary.year_projects)} · ${total(historySummary.year_drivers)} ${statisticsNoun(locale, "driver", historySummary.year_drivers)}`} tone="green" />
              <StatCard label={copy.vsYtd} value={growth(total(historySummary.year_transfers), total(historySummary.previous_ytd_transfers))} detail={`${total(historySummary.previous_ytd_transfers)} · ${historyYear - 1}`} tone="violet" />
            </section>
            <section className="stats-grid stats-history-charts">
              <article className="stats-panel stats-wide">
                <header><div><h2>{copy.monthlyTitle}</h2><p>{copy.monthlyHint}</p></div><label className="stats-year-select"><span>{copy.chooseYear}</span><select value={selectedHistoryYear} onChange={(event) => setComparisonYear(Number(event.target.value))}>{availableYears.map((year) => <option key={year} value={year}>{year - 1} / {year}</option>)}</select></label></header>
                <div className="stats-chart stats-history-chart"><ResponsiveContainer width="100%" height="100%"><BarChart data={monthlyData} margin={{ top: 8, right: 8, left: -18 }} barGap={2}>
                  <CartesianGrid stroke="#e7edf5" vertical={false} /><XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} /><YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} /><Tooltip />
                  <Bar dataKey="previous" name={`${copy.previousYear} ${selectedHistoryYear - 1}`} fill="#a8b7cc" radius={[4,4,0,0]} isAnimationActive={false} />
                  <Bar dataKey="current" name={`${copy.currentYear} ${selectedHistoryYear}`} fill="#3156b8" radius={[4,4,0,0]} isAnimationActive={false} />
                </BarChart></ResponsiveContainer></div>
                <div className="stats-chart-key"><span className="previous">{selectedHistoryYear - 1}</span><span className="current">{selectedHistoryYear}</span></div>
              </article>
              <article className="stats-panel">
                <header><div><h2>{copy.yearlyTitle}</h2><p>{copy.yearlyHint}</p></div></header>
                <div className="stats-chart stats-history-chart"><ResponsiveContainer width="100%" height="100%"><LineChart data={yearlyData} margin={{ top: 8, right: 8, left: -18 }}>
                  <CartesianGrid stroke="#e7edf5" vertical={false} /><XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} /><YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} /><Tooltip />
                  <Line type="monotone" dataKey="transfers" name={copy.transfers} stroke="#24a27a" strokeWidth={3} dot={{ r: 3 }} isAnimationActive={false} />
                </LineChart></ResponsiveContainer></div>
              </article>
            </section>
            <div className="stats-period-control" role="group" aria-label={copy.rankingPeriod}>
              <span>{copy.rankingPeriod}</span>
              <label className="stats-period-select"><span>{copy.rankingYear}</span><select value={selectedHistoryYear} onChange={(event) => setComparisonYear(Number(event.target.value))}>{availableYears.map((year) => <option key={year} value={year}>{year}</option>)}</select></label>
              {rankingPeriod === "month" && <label className="stats-period-select"><span>{copy.rankingMonth}</span><select value={selectedRankingMonth} onChange={(event) => setRankingMonth(Number(event.target.value))}>{Array.from({length: 12}, (_, index) => <option key={index + 1} value={index + 1}>{new Intl.DateTimeFormat(localeTag(locale), {month: "long"}).format(new Date(2024, index, 1))}</option>)}</select></label>}
              <button className={rankingPeriod === "month" ? "active" : ""} onClick={() => setRankingPeriod("month")}>{copy.month}</button><button className={rankingPeriod === "year" ? "active" : ""} onClick={() => setRankingPeriod("year")}>{copy.year}</button>
            </div>
            <section className="stats-grid stats-lists">
              <HistoricalRank title={copy.topSupervisors} subtitle={copy.supervisorHint} rows={supervisorRanking} type="supervisor" copy={copy} locale={locale} />
              <HistoricalRank title={copy.topDrivers} subtitle={copy.driverHint} rows={driverRanking} type="driver" copy={copy} locale={locale} />
            </section>
            <section className="stats-panel stats-daily-table stats-annual-table"><header><div><h2>{copy.annualDetails}</h2><p>{copy.yearlyHint}</p></div></header><div className="table-wrap"><table><thead><tr><th>{copy.year}</th><th>{copy.transfers}</th><th>{copy.projects}</th><th>{copy.drivers}</th><th>{copy.supervisors}</th></tr></thead><tbody>{[...yearlyData].reverse().map((row) => <tr key={row.year}><td><b>{row.year}</b></td><td>{row.transfers}</td><td>{row.projects}</td><td>{row.drivers}</td><td>{total(row.supervisors)}</td></tr>)}</tbody></table></div></section>
          </> : (historyState.loading ? <div className="loading-surface stats-history-loading" role="status"><span className="sr-only">{copy.completed}</span><i className="loading-line loading-line-title" /><i className="loading-line" /><i className="loading-line loading-line-short" /></div> : <p className="status error">{historyState.error || copy.noRank}</p>)}</div>
          <div className="stats-operations"><section className="stats-section-heading stats-operational-heading"><div><h2>{copy.operational}</h2><p>{range.start} — {range.end}</p></div></section>
          <section className="stats-kpis">
            <StatCard
              label={copy.transfers}
              value={total(summary.transfers)}
              detail={`${total(summary.projects)} ${copy.projects}`}
              tone="blue"
            />
            <StatCard
              label={copy.vehiclesInUse}
              value={`${total(summary.assigned_cars)} / ${total(summary.cars)}`}
              detail={copy.uniqueFleet}
              tone="green"
            />
            <StatCard
              label={copy.driversAssigned}
              value={`${total(summary.assigned_drivers)} / ${total(summary.drivers)}`}
              detail={copy.uniqueDrivers}
              tone="green"
            />
            <StatCard
              label={copy.capacityRisks}
              value={alerts.length}
              detail={
                alerts.length
                  ? copy.risksExceeded
                  : copy.allCovered
              }
              tone={alerts.length ? "amber" : "green"}
            />
          </section>
          <section className="stats-grid stats-health-grid">
            <article className="stats-panel stats-health-panel">
              <header><div><h2>{copy.health}</h2><p>{copy.healthHint}</p></div></header>
              <div className="stats-health-list">
                <div className={total(summary.missing_driver_transfers) ? "risk" : ""}><span>{copy.missingDrivers}</span><strong>{total(summary.missing_driver_transfers)}</strong></div>
                <div className={total(summary.missing_vehicle_transfers) ? "risk" : ""}><span>{copy.missingVehicles}</span><strong>{total(summary.missing_vehicle_transfers)}</strong></div>
                <div className={total(summary.availability_conflicts) ? "risk" : ""}><span>{copy.availabilityConflicts}</span><strong>{total(summary.availability_conflicts)}</strong></div>
              </div>
            </article>
            <article className="stats-panel stats-health-panel">
              <header><div><h2>{copy.utilisation}</h2><p>{copy.utilisationHint}</p></div></header>
              <div className="stats-health-list">
                <div><span>{copy.availabilityCoverage}</span><strong>{total(summary.availability_coverage)}%</strong><small>{total(summary.availability_declarations)} {copy.declarations}</small></div>
                <div><span>{copy.idleVehicles}</span><strong>{total(summary.idle_cars)}</strong></div>
                <div><span>{copy.idleDrivers}</span><strong>{total(summary.idle_drivers)}</strong></div>
              </div>
            </article>
          </section>
          <section
            className={`stats-capacity-strip ${alerts.length ? "has-alerts" : ""}`}
          >
            <div>
              <b>{alerts.length ? copy.capacityRisks : copy.capacityClear}</b>
              <span>{copy.capacityHint}</span>
            </div>
            <div className="stats-capacity-days">
              {chartData.map((row) => (
                <span
                  className={
                    total(row.car_shortage) || total(row.driver_shortage)
                      ? "risk"
                      : "ok"
                  }
                  key={row.day}
                >
                  <b>{row.label}</b>
                  <small>
                    {total(row.car_shortage) || total(row.driver_shortage)
                      ? `${total(row.car_shortage) ? `−${total(row.car_shortage)} ${copy.car} ` : ""}${total(row.driver_shortage) ? `−${total(row.driver_shortage)} ${copy.driver}` : ""}`
                      : copy.ok}
                  </small>
                </span>
              ))}
            </div>
          </section>
          <section className="stats-grid">
            <article className="stats-panel stats-wide">
              <header>
                <div>
                  <h2>{copy.trend}</h2>
                  <p>{copy.trendHint}</p>
                </div>
              </header>
              <div className="stats-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 8, right: 8, left: -20 }}
                  >
                    <CartesianGrid stroke="#e7edf5" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="transfers"
                      name={copy.transfers}
                      stroke="#3156b8"
                      strokeWidth={3}
                      dot={{ r: 3 }}
                      isAnimationActive={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="projects"
                      name={copy.projects}
                      stroke="#24a27a"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </article>
            <article className="stats-panel">
              <header>
                <div>
                  <h2>{copy.dailyTransfers}</h2>
                  <p>{copy.dailyVolume}</p>
                </div>
              </header>
              <div className="stats-chart compact">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{ top: 8, right: 0, left: -18 }}
                  >
                    <CartesianGrid stroke="#e7edf5" vertical={false} />
                    <XAxis dataKey="label" hide />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: "#64748b", fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip />
                    <Bar
                      dataKey="transfers"
                      name={copy.transfers}
                      fill="#7e69c7"
                      isAnimationActive={false}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>
          </section>
          <section className="stats-grid stats-lists">
            <StatsRank
              title={copy.usedVehicles}
              subtitle={copy.selectedTransfers}
              rows={state.data.fleet || []}
              type="fleet"
              copy={copy}
              locale={locale}
            />
            <StatsRank
              title={copy.assignedDrivers}
              subtitle={copy.selectedAssignments}
              rows={state.data.drivers || []}
              type="driver"
              copy={copy}
              locale={locale}
            />
          </section>
          <section className="stats-panel stats-daily-table">
            <header>
              <div>
                <h2>{copy.dailyOperations}</h2>
                <p>{copy.dailyCapacityHint}</p>
              </div>
            </header>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{copy.day}</th>
                    <th>{copy.transfers}</th>
                    <th>{copy.projects}</th>
                    <th>{copy.cars}</th>
                    <th>{copy.drivers}</th>
                    <th>{copy.unassigned}</th>
                    <th>{copy.capacity}</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((row) => (
                    <tr key={row.day}>
                      <td>{row.label}</td>
                      <td>{row.transfers}</td>
                      <td>{row.projects}</td>
                      <td>{total(row.assigned_cars)}</td>
                      <td>{total(row.assigned_drivers)}</td>
                      <td>{total(row.unassigned_transfers) || "—"}</td>
                      <td
                        className={
                          total(row.car_shortage) || total(row.driver_shortage)
                            ? "stats-warning"
                            : "stats-ok"
                        }
                      >
                        {total(row.car_shortage) || total(row.driver_shortage)
                          ? `${total(row.car_shortage) ? `−${total(row.car_shortage)} ${copy.car} ` : ""}${total(row.driver_shortage) ? `−${total(row.driver_shortage)} ${copy.driver}` : ""}`
                          : copy.ok}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          </div>
          </div>
        </>
      )}
    </Page>
  );
}

function StatCard({ label, value, detail, tone }) {
  return (
    <article className={`stat-card stat-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}
function StatsRank({ title, subtitle, rows, type, copy, locale }) {
  const max = Math.max(1, ...rows.map((row) => Number(row.transfers || 0)));
  return (
    <article className="stats-panel stats-rank">
      <header>
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </header>
      <div className="stats-rank-list">
        {rows.map((row) => {
          const name =
            type === "fleet"
              ? row.signature
              : row.signature ||
                [row.name, row.surname].filter(Boolean).join(" ") ||
                copy.unnamed;
          const sub =
            type === "fleet"
              ? [row.car_group, row.model_name, row.registration_number]
                  .filter(Boolean)
                  .join(" · ")
              : row.drivers_license || copy.noLicence;
          const count = Number(row.transfers || 0);
          return (
            <div className="stats-rank-row" key={row.id}>
              <div className="stats-rank-title">
                <b>{name}</b>
                <span>{sub}</span>
              </div>
              <div className="stats-rank-bar">
                <i style={{ width: `${Math.round((count / max) * 100)}%` }} />
              </div>
              <strong>{count}</strong>
              <small>{row.utilisation || 0}% · {row.active_days || 0} {copy.activeDays}</small>
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="status">{copy.noRank}</p>
        )}
      </div>
    </article>
  );
}

function HistoricalRank({ title, subtitle, rows, type, copy, locale }) {
  const max = Math.max(1, ...rows.map((row) => Number(row.transfers || 0)));
  return <article className="stats-panel stats-rank stats-history-rank"><header><div><h2>{title}</h2><p>{subtitle}</p></div></header><div className="stats-rank-list">
    {rows.map((row, index) => {
      const name = row.signature || [row.name, row.surname].filter(Boolean).join(" ") || "—";
      const details = type === "supervisor"
        ? `${row.projects || 0} ${statisticsNoun(locale, "project", row.projects)} · ${row.active_days || 0} ${statisticsNoun(locale, "day", row.active_days)}`
        : `${row.projects || 0} ${statisticsNoun(locale, "project", row.projects)} · ${row.active_days || 0} ${statisticsNoun(locale, "day", row.active_days)} · ${row.vehicles || 0} ${statisticsNoun(locale, "vehicle", row.vehicles)}`;
      return <div className="stats-rank-row stats-history-rank-row" key={row.id}><span className="stats-rank-position">{index + 1}</span><div className="stats-rank-title"><b>{name}</b><span>{details}</span></div><div className="stats-rank-bar"><i style={{ width: `${Math.round((Number(row.transfers || 0) / max) * 100)}%` }} /></div><strong>{row.transfers || 0}</strong></div>;
    })}
    {!rows.length && <p className="status">{copy.noRank}</p>}
  </div></article>;
}
const transferTone = (status) =>
  ({
    PENDING: "pending",
    CONFIRMED: "confirmed",
    CANCELLED: "cancelled",
    DONE: "done",
  })[status] || "planned";
const projectStatusLabel = (status, t) =>
  status && t(`office.projects.statuses.${status}`) !== `office.projects.statuses.${status}`
    ? t(`office.projects.statuses.${status}`)
    : status || "—";
const projectPaymentLabel = (status, t) =>
  status && t(`office.projects.payments.${status}`) !== `office.projects.payments.${status}`
    ? t(`office.projects.payments.${status}`)
    : status || "—";
const formatTime = (value, fallback = "—") => {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : fallback;
};
const contrastText = (color) => {
  const hex = String(color || "").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(hex)) return "#31425b";
  const [red, green, blue] = [0, 2, 4].map((index) =>
    Number.parseInt(hex.slice(index, index + 2), 16),
  );
  return red * 0.299 + green * 0.587 + blue * 0.114 > 164 ? "#203149" : "#fff";
};
const timeMinutes = (value, fallback) => {
  const [hour, minute] = String(value || fallback)
    .slice(0, 5)
    .split(":")
    .map(Number);
  return Number.isFinite(hour) && Number.isFinite(minute)
    ? hour * 60 + minute
    : fallback;
};
const timelinePosition = (assignment, date) => {
  const start =
    assignment.departure_date < date
      ? 0
      : timeMinutes(assignment.departure_time, 0);
  const end =
    assignment.arrival_date > date
      ? 1440
      : timeMinutes(assignment.arrival_time, 1440);
  const safeEnd = Math.max(start + 20, end);
  return {
    left: `${Math.min(100, start / 14.4)}%`,
    width: `${Math.min(100 - start / 14.4, (safeEnd - start) / 14.4)}%`,
  };
};
const compactTimelineAssignment = (assignment, date) =>
  Number.parseFloat(timelinePosition(assignment, date).width) < 4.1;
const timelineLanes = (assignments, date) => {
  const laneEnds = [];
  return assignments.map((assignment) => {
    const start =
      assignment.departure_date < date
        ? 0
        : timeMinutes(assignment.departure_time, 0);
    const end = Math.max(
      start + 20,
      assignment.arrival_date > date
        ? 1440
        : timeMinutes(assignment.arrival_time, 1440),
    );
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);
    if (lane < 0) lane = laneEnds.length;
    laneEnds[lane] = end;
    return {
      ...assignment,
      timeline_lane: lane,
      timeline_lanes: laneEnds.length,
    };
  });
};
const blankHaul = { resource: "none", car_id: "", car_group_id: "", external_car_info: "", driver_mode: "none", driver_id: "", external_driver_info: "", price: "", driver_cost: "", driver_payed: false, fv_num: "" };

function HaulList({ transferId, onChanged }) {
  const { t } = useI18n();
  const [revision, setRevision] = useState(0);
  const [editor, setEditor] = useState(null);
  const [removeError, setRemoveError] = useState("");
  const state = useRequest(() => api.hauls(transferId), [transferId, revision]);
  const options = useRequest(() => api.haulOptions(), []);
  const save = async (values) => {
    const payload = {
      driver_id: values.driver_mode === "internal" ? values.driver_id || null : null,
      external_driver_info: values.driver_mode === "external" ? values.external_driver_info : null,
      car_id: values.resource === "car" ? values.car_id || null : null,
      car_group_id: values.resource === "group" ? values.car_group_id || null : null,
      external_car_info: values.resource === "external" ? values.external_car_info : null,
      price: values.price || null, driver_cost: values.driver_cost || null,
      driver_payed: Boolean(values.driver_payed), fv_num: values.fv_num || null,
    };
    if (editor?.id) await api.updateHaul(editor.id, payload);
    else await api.createHaul(transferId, payload);
    setEditor(null); setRevision((value) => value + 1); onChanged?.();
  };
  const editValues = (haul) => ({
    ...blankHaul, id: haul.id,
    resource: haul.car_id ? "car" : haul.car_group_id ? "group" : haul.external_car_info ? "external" : "none",
    car_id: haul.car_id || "", car_group_id: haul.car_group_id || "", external_car_info: haul.external_car_info || "",
    driver_mode: haul.driver_id ? "internal" : haul.external_driver_info ? "external" : "none",
    driver_id: haul.driver_id || "", external_driver_info: haul.external_driver_info || "",
    price: haul.price ?? "", driver_cost: haul.driver_cost ?? "", driver_payed: Boolean(haul.driver_payed), fv_num: haul.fv_num || "",
  });
  if (state.loading || options.loading) return <p className="haul-list">{t('forms.loadingAssignments')}</p>;
  if (state.error || options.error) return <p className="haul-list error">{state.error || options.error}</p>;
  return <div className="haul-list">
    {removeError && <p className="form-error" role="alert">{removeError}</p>}
    <div className="haul-list-heading"><span>{state.data.count} {state.data.count === 1 ? t('forms.assignments').slice(0, -1) : t('forms.assignments')}</span><button type="button" onClick={() => setEditor(blankHaul)}>+ {t('forms.addAssignment')}</button></div>
    {state.data.data.map((haul) => <div className="haul-row" key={haul.id}>
      <b>{haul.driver_signature || haul.external_driver_info || `${t('forms.unassigned')} ${t('forms.driver').toLocaleLowerCase()}`}</b>
      <span>{haul.car_signature || haul.car_group_signature || haul.external_car_info || `${t('forms.unassigned')} ${t('forms.vehicle').toLocaleLowerCase()}`}</span>
      <small>{haul.driver_payed ? t('forms.paid') : t('forms.unpaid')}{haul.driver_cost !== null ? ` · ${t('forms.driverCost').toLocaleLowerCase()} ${haul.driver_cost}` : ""}</small>
      <button type="button" onClick={() => setEditor(editValues(haul))}>{t('forms.edit')}</button>
      <button type="button" className="haul-delete" onClick={async () => { if (window.confirm(t('forms.remove'))) { try { setRemoveError(""); await api.deleteHaul(haul.id); setRevision(v => v + 1); onChanged?.(); } catch (error) { setRemoveError(error.message); } } }}>{t('forms.remove')}</button>
    </div>)}
    {state.data.data.length === 0 && <p className="haul-empty">{t('forms.noAssignments')}</p>}
    {editor && <HaulEditor value={editor} options={options.data} onCancel={() => setEditor(null)} onSave={save} />}
  </div>;
}

function HaulEditor({ value, options, onCancel, onSave }) {
  const { t } = useI18n();
  const [form, setForm] = useState(value); const [error, setError] = useState(""); const [saving, setSaving] = useState(false);
  const update = (event) => setForm(current => ({ ...current, [event.target.name]: event.target.type === "checkbox" ? event.target.checked : event.target.value }));
  const submit = async (event) => { event.preventDefault(); setSaving(true); setError(""); try { await onSave(form); } catch (reason) { setError(reason.message); setSaving(false); } };
  return <form className="haul-editor" onSubmit={submit}>
    <div className="haul-editor-title"><b>{form.id ? t('forms.editAssignment') : t('forms.addAssignment')}</b></div>
    <div className="project-form-grid">
      <label>{t('forms.vehicle')}<select name="resource" value={form.resource} onChange={update}><option value="none">{t('forms.noVehicle')}</option><option value="car">{t('forms.vehicle')}</option><option value="group">{t('forms.vehicleGroup')}</option><option value="external">{t('forms.external')}</option></select></label>
      {form.resource === "car" && <label>{t('forms.vehicle')}<select required name="car_id" value={form.car_id} onChange={update}><option value="">{t('forms.selectVehicle')}</option>{options.cars.map(car => <option key={car.id} value={car.id}>{car.signature} · {car.num_passengers} seats · {car.car_group_signature || "other"}</option>)}</select></label>}
      {form.resource === "group" && <label>{t('forms.vehicleGroup')}<select required name="car_group_id" value={form.car_group_id} onChange={update}><option value="">{t('forms.selectGroup')}</option>{options.groups.map(group => <option key={group.id} value={group.id}>{group.signature}</option>)}</select></label>}
      {form.resource === "external" && <label>{t('forms.externalVehicle')}<input required name="external_car_info" value={form.external_car_info} onChange={update} placeholder="Operator / vehicle" /></label>}
      <label>{t('forms.driverSource')}<select name="driver_mode" value={form.driver_mode} onChange={update}><option value="none">{t('forms.noDriverYet')}</option><option value="internal">{t('forms.internalDriver')}</option><option value="external">{t('forms.externalDriver')}</option></select></label>
      {form.driver_mode === "internal" && <label>{t('forms.driver')}<select required name="driver_id" value={form.driver_id} onChange={update}><option value="">{t('forms.selectDriver')}</option>{options.drivers.map(driver => <option key={driver.id} value={driver.id}>{driver.signature} · {driver.drivers_license || "—"}</option>)}</select></label>}
      {form.driver_mode === "external" && <label>{t('forms.externalDriver')}<input required name="external_driver_info" value={form.external_driver_info} onChange={update} /></label>}
      <label>{t('forms.driverCost')}<input name="driver_cost" inputMode="numeric" value={form.driver_cost} onChange={update} /></label>
      <label>{t('forms.assignmentPrice')}<input name="price" inputMode="numeric" value={form.price} onChange={update} /></label>
      <label>{t('forms.invoiceNumber')}<input name="fv_num" value={form.fv_num} onChange={update} /></label>
      <label className="haul-paid"><input type="checkbox" name="driver_payed" checked={form.driver_payed} onChange={update} /> {t('forms.driverPaid')}</label>
    </div>
    {error && <p className="form-error">{error}</p>}<div className="project-form-actions"><button type="button" onClick={onCancel}>{t('forms.cancel')}</button><button className="project-create-button" disabled={saving}>{saving ? t('common.loading') : t('forms.saveAssignment')}</button></div>
  </form>;
}
function DayByDay() {
  const { t } = useI18n();
  const [selected, setSelected] = useState(() => initialRange().start);
  const state = useRequest(() => api.dayByDay(selected), [selected]);
  return (
    <Page
      title="Day by Day"
      description="Transfers active on the selected day, including multi-day work."
    >
      <FilterPanel label={t("office.dayByDay.filters")} activeCount={selected !== isoDate() ? 1 : 0} onClear={() => setSelected(isoDate())}><label className="date-control">
        {t("office.dayByDay.date")}{" "}
        <input
          type="date"
          value={selected}
          onChange={(event) => setSelected(event.target.value)}
        />
      </label></FilterPanel>
      <Notice state={state} />
      {state.data && (
        <div className="transfer-list">
          {state.data.data.map((item) => (
            <article className="transfer-card" key={item.id}>
              <time>
                {transferTimes(item)}
              </time>
              <div>
                <strong>{item.tweet || t("office.dayByDay.untitled")}</strong>
                <p>
                  {item.departure_meta || "—"} → {item.arrival_meta || "—"}
                </p>
              </div>
              <span>
                {item.company_name ||
                  item.client_signature ||
                  item.project_annotation ||
                  "—"}
                <br />
                {item.cars || t("office.dayByDay.unassigned")} · {item.drivers || ""}
              </span>
            </article>
          ))}
          {state.data.count === 0 && (
            <p className="status">{t("office.dayByDay.empty")}</p>
          )}
        </div>
      )}
    </Page>
  );
}
function FleetTimelinePanel({ date }) {
  const { t, locale } = useI18n();
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(null);
  const state = useRequest(() => api.fleetTimeline(date), [date]);
  const cars = (state.data?.data || []).filter(
    (car) => showAll || car.assignments.length > 0,
  );
  return (
    <section className="fleet-panel">
      <header className="fleet-heading">
        <div>
          <h2>{t("office.timeline.title")}</h2>
          <p>{t("office.timeline.description")}</p>
        </div>
        <label className="timeline-filter">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(event) => setShowAll(event.target.checked)}
          />{" "}
          {t("office.timeline.showIdle")}
        </label>
        <span>{cars.length} {t("office.timeline.vehicles")}</span>
      </header>
      <Notice state={state} />
      {state.data && (
        <div className="timeline-scroll">
          <div
            className="timeline-grid"
            style={{
              gridTemplateColumns:
                "minmax(132px, 1fr) repeat(24, minmax(76px, 1fr))",
            }}
          >
            <div className="timeline-corner">{t("office.timeline.vehicle")}</div>
            {Array.from({ length: 24 }, (_, hour) => (
              <div className="timeline-hour" key={hour}>
                {String(hour).padStart(2, "0")}:00
              </div>
            ))}
            {cars.map((car) => {
              const assignments = timelineLanes(car.assignments, date);
              const laneCount = Math.max(
                1,
                ...assignments.map((item) => item.timeline_lane + 1),
              );
              const rowStyle = {
                minHeight: `${Math.max(52, 10 + laneCount * 42)}px`,
              };
              return (
                <>
                  <div
                    className="timeline-car"
                    key={`car-${car.id}`}
                    style={{
                      ...rowStyle,
                      borderLeftColor: car.car_group_color
                        ? `#${car.car_group_color}`
                        : undefined,
                    }}
                  >
                    <b>{car.signature}</b>
                    <span>{car.num_passengers ?? "—"} {vehicleSeatsLabel(car.num_passengers, locale, t)}</span>
                  </div>
                  <div
                    className="timeline-track"
                    key={`track-${car.id}`}
                    style={rowStyle}
                  >
                    {assignments.map((item) => (
                      <button
                        type="button"
                        className={`timeline-bar tone-${transferTone(item.project_status)} ${compactTimelineAssignment(item, date) ? "compact" : ""}`}
                        title={`${shortTime(item.departure_time)}–${shortTime(item.arrival_time)}: ${item.tweet || t("office.timeline.transfer")}`}
                        aria-label={`${t("office.timeline.details")}: ${item.tweet || t("office.timeline.transfer")}`}
                        onClick={() => setSelected({ car, item })}
                        style={{
                          ...timelinePosition(item, date),
                          top: `${7 + item.timeline_lane * 42}px`,
                          bottom: "auto",
                        }}
                        key={item.transfer_id}
                      >
                        {compactTimelineAssignment(item, date) ? (
                          <span className="compact-time">
                            {shortTime(item.departure_time, "•")}
                          </span>
                        ) : (
                          <>
                            <b>{item.tweet || t("office.timeline.transfer")}</b>
                            <span>
                              {transferTimes(item)} ·{" "}
                              {item.driver_signature || "—"}
                            </span>
                          </>
                        )}
                        <em>
                          {item.tweet || t("office.timeline.transfer")}
                          <br />
                          {projectReference(item.project_id, item.project_annotation, t, t("office.timeline.noAnnotation"))}
                          <br />
                          {t("office.timeline.driver")}: {item.driver_signature || t("office.timeline.unassigned")}
                          <br />
                          {t("office.timeline.passengers")}: {item.num_passengers ?? "—"}
                        </em>
                      </button>
                    ))}
                  </div>
                </>
              );
            })}
          </div>
        </div>
      )}
      {selected &&
        createPortal(
          <ProjectTransferDialog
            transfer={{
              ...selected.item,
              id: selected.item.transfer_id,
              cars: selected.car.signature,
              drivers: selected.item.driver_signature,
            }}
            onClose={() => setSelected(null)}
            showAssignments={false}
          />,
          document.body,
        )}
    </section>
  );
}
function RegisterTable({ className = "", headings, mobileHint, children, emptyText }) {
  const hasRows = Array.isArray(children) ? children.flat().filter(Boolean).length > 0 : Boolean(children);
  return <>
    {mobileHint && <p className="mobile-table-hint">{mobileHint}</p>}
    <div className={`table-wrap register-table-wrap ${className}`.trim()}>
      <table>
        <thead><tr>{headings.map((heading) => <th key={heading}>{heading}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
    {!hasRows && emptyText && <p className="empty-state">{emptyText}</p>}
  </>;
}

function RegisterPagination({ page, totalPages, onPage, previous = "Previous", next = "Next", pageLabel }) {
  return <div className="pagination register-pagination">
    <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>← {previous}</button>
    <span>{pageLabel || `Page ${page} / ${totalPages}`}</span>
    <button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>{next} →</button>
  </div>;
}

function Projects() {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [revision, setRevision] = useState(0);
  const state = useRequest(
    () => api.projects(query, page),
    [query, page, revision],
  );
  const totalPages = Math.max(
    1,
    Math.ceil((state.data?.count || 0) / (state.data?.page_size || 50)),
  );
  return (
    <Page
      title="Projects"
      description={t('office.descriptions.projects')}
    >
      <div className="project-toolbar">
        <FilterPanel label={t('office.projects.filters')} activeCount={query.trim() ? 1 : 0} onClear={() => { setQuery(''); setPage(1); }}>
          <input
            className="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
            placeholder={t('office.projects.search')}
          />
        </FilterPanel>
        <button
          type="button"
          className="project-create-button"
          onClick={() => setCreateOpen(true)}
        >
          + {t('office.projects.add')}
        </button>
      </div>
      <Notice state={state} />
      {state.data && (
        <>
          <p className="project-page-count">
            {t('office.projects.count', { count: state.data.count, page: state.data.page, pages: totalPages })}
          </p>
          <RegisterTable className="projects-table-wrap" headings={[t('office.projects.id'), t('office.projects.client'), t('office.projects.annotation'), t('office.projects.status'), t('office.projects.details')]} mobileHint={t('office.projects.swipe')}>
                {state.data.data.map((row) => {
                  const projectLink = `/projects/${row.id}`;
                  return (
                    <tr key={row.id}>
                      <td><NavLink className="project-table-id" to={projectLink}>#{row.id}</NavLink></td>
                      <td>
                        <NavLink
                          className="project-table-link project-table-client"
                          to={projectLink}
                        >
                          {row.company_name ||
                            row.client_signature ||
                            row.external_client ||
                            t('office.projects.unassigned')}
                        </NavLink>
                      </td>
                      <td>
                        <NavLink
                          className="project-table-link"
                          to={projectLink}
                        >
                          {row.annotation || t('office.projects.noAnnotation')}
                        </NavLink>
                      </td>
                      <td>{projectStatusLabel(row.status, t)}</td>
                      <td>
                        <NavLink
                          className="project-open-button"
                          to={projectLink}
                        >
                          {t('office.projects.open')} →
                        </NavLink>
                      </td>
                    </tr>
                  );
                })}
          </RegisterTable>
          <RegisterPagination page={page} totalPages={totalPages} onPage={setPage} previous={t('office.projects.previous')} next={t('office.projects.next')} pageLabel={t('office.projects.page', { page, pages: totalPages })} />
        </>
      )}
      {createOpen &&
        createPortal(
          <ProjectCreateDialog
            onClose={() => setCreateOpen(false)}
            onCreated={() => {
              setCreateOpen(false);
              setPage(1);
              setRevision((value) => value + 1);
            }}
          />,
          document.body,
        )}
    </Page>
  );
}

function Transfers() {
  const { locale, t } = useI18n();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [range, setRange] = useState({ start: "", end: "" });
  const [sort, setSort] = useState("created");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const timer = window.setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  const state = useRequest(
    () => api.transfers(query, range.start, range.end, page, sort),
    [query, range.start, range.end, page, sort, revision],
  );
  const totalPages = Math.max(1, Math.ceil((state.data?.count || 0) / (state.data?.page_size || 50)));
  const changeRange = (field, value) => {
    setRange((current) => ({ ...current, [field]: value }));
    setPage(1);
  };
  return (
    <Page title="Transfers" description="Transfer register with routes, projects and assignments.">
      <div className="project-toolbar transfer-register-toolbar">
        <FilterPanel label={t('office.transfers.filters')} activeCount={[search.trim(), range.start, range.end, sort !== 'created'].filter(Boolean).length} onClear={() => { setSearch(''); setQuery(''); setRange({ start: '', end: '' }); setSort('created'); setPage(1); }}>
          <div className="transfer-register-filters">
            <label className="transfer-register-search">
              <span>{t('common.search')}</span>
              <input
                className="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('office.transfers.searchPlaceholder')}
              />
            </label>
            <label><span>{t('forms.from')}</span><input type="date" value={range.start} max={range.end || undefined} onChange={(event) => changeRange('start', event.target.value)} /></label>
            <label><span>{t('forms.to')}</span><input type="date" value={range.end} min={range.start || undefined} onChange={(event) => changeRange('end', event.target.value)} /></label>
            <label><span>{t('office.transfers.sort')}</span><select value={sort} onChange={(event) => { setSort(event.target.value); setPage(1); }}><option value="created">{t('office.transfers.newest')}</option><option value="departure_desc">{t('office.transfers.departureLatest')}</option><option value="departure_asc">{t('office.transfers.departureEarliest')}</option></select></label>
            {(search || range.start || range.end || sort !== 'created') && <button type="button" className="transfer-filter-clear" onClick={() => { setSearch(''); setQuery(''); setRange({ start: '', end: '' }); setSort('created'); setPage(1); }}>{t('office.transfers.clear')}</button>}
          </div>
        </FilterPanel>
      </div>
      <Notice state={state} />
      {state.data && <>
        <p className="project-page-count">{t('office.transfers.resultCount', { count: state.data.count, page: state.data.page, pages: totalPages })}</p>
        <RegisterTable className="transfers-table-wrap" headings={[t('office.transfers.when'), t('office.transfers.transfer'), t('forms.route'), t('forms.project'), t('forms.assignments'), t('office.transfers.details')]} mobileHint={t('office.transfers.mobileHint')} emptyText={t('office.transfers.empty')}>
              {state.data.data.map((row) => <tr key={row.id}>
                <td className="transfer-register-when"><button type="button" className="transfer-mobile-open" onClick={() => setSelected(row)}>
                  <b>{formatOfficeDate(row.departure_date, locale, { day: '2-digit', month: 'short', year: 'numeric' })}</b>
                  <span>{transferTimes(row)}</span>
                </button></td>
                <td>
                  <button type="button" className="transfer-table-link" onClick={() => setSelected(row)}>{row.tweet || t('office.dashboard.untitled')}</button>
                  <small>#{row.id}</small>
                </td>
                <td className="transfer-register-route"><b title={row.departure_meta || row.departure_address || '—'}>{row.departure_meta || row.departure_address || '—'}</b><span aria-hidden="true">→</span><b title={row.arrival_meta || row.arrival_address || '—'}>{row.arrival_meta || row.arrival_address || '—'}</b></td>
                <td>
                  <NavLink className="project-table-link" to={`/projects/${row.project_id}?start=${row.departure_date}&end=${row.arrival_date || row.departure_date}`}>{row.project_annotation || `${t('forms.project')} #${row.project_id}`}</NavLink>
                  <small>{row.client_name || t('forms.unassigned')}</small>
                </td>
                <td className="transfer-register-assignments"><b>{row.cars || t('forms.noVehicle')}</b><span>{row.drivers || t('forms.noDriverYet')}</span></td>
                <td><button type="button" className="project-open-button" onClick={() => setSelected(row)}>{t('office.transfers.open')} →</button></td>
              </tr>)}
        </RegisterTable>
        <RegisterPagination page={page} totalPages={totalPages} onPage={setPage} previous={t('office.transfers.previous')} next={t('office.transfers.next')} pageLabel={t('office.transfers.page', { page, pages: totalPages })} />
      </>}
      {selected && createPortal(<ProjectTransferDialog transfer={selected} onClose={() => setSelected(null)} onChanged={() => setRevision((value) => value + 1)} allowEdit />, document.body)}
    </Page>
  );
}

function DialogShell({ title, subtitle, children, onClose }) {
  const dialogRef = useDialogAccessibility(onClose);
  return (
    <div
      className="project-transfer-backdrop form-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <section
        ref={dialogRef}
        className="project-form-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex="-1"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="timeline-detail-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <p>{subtitle}</p>
        <h3>{title}</h3>
        {children}
      </section>
    </div>
  );
}

const projectClientLabel = (account, fallback = "") => {
  if (!account) return fallback;
  const primary = account.company_name || account.signature || account.username || `#${account.id}`;
  const name = [account.name, account.surname].filter(Boolean).join(" ");
  return name ? `${primary} · ${name}` : primary;
};

function ProjectClientPicker({ value, onChange, initialLabel, inputId, disabled = false }) {
  const { t } = useI18n();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  // Project edits need to display a historic/deactivated client too.  The API
  // returns that single selected record without exposing the entire catalogue.
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    let active = true;
    api.projectClients("", value, 1)
      .then((response) => {
        if (active) setSelected(response.data?.[0] || { id: value });
      })
      .catch(() => active && setSelected({ id: value }));
    return () => { active = false; };
  }, [value]);

  useEffect(() => {
    const search = query.trim();
    if (search.length < 2 || disabled) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      api.projectClients(search)
        .then((response) => active && setResults(response.data || []))
        .catch(() => active && setResults([]))
        .finally(() => active && setLoading(false));
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [query, disabled]);

  const choose = (account) => {
    setSelected(account);
    setResults([]);
    setQuery("");
    onChange(String(account.id));
  };
  const clear = () => {
    setSelected(null);
    setResults([]);
    setQuery("");
    onChange("");
  };
  const selectionLabel = projectClientLabel(selected, initialLabel || `#${value}`);

  return (
    <div className="project-client-picker">
      {value ? (
        <div className="project-client-selection">
          <span title={selectionLabel}>{selectionLabel}</span>
          <button type="button" onClick={clear} disabled={disabled} aria-label={t('forms.remove')}>
            ×
          </button>
        </div>
      ) : (
        <>
          <input
            id={inputId}
            type="search"
            value={query}
            disabled={disabled}
            autoComplete="off"
            placeholder={t('office.accounts.search')}
            aria-autocomplete="list"
            aria-expanded={results.length > 0}
            onChange={(event) => setQuery(event.target.value)}
          />
          {loading && <small className="project-client-status">…</small>}
          {results.length > 0 && (
            <div className="project-client-results" role="listbox" aria-label={t('roles.CLIENT')}>
              {results.map((account) => (
                <button type="button" role="option" aria-selected="false" key={account.id} onClick={() => choose(account)}>
                  <strong>{account.company_name || account.signature || account.username}</strong>
                  <span>{[account.name, account.surname, account.username].filter(Boolean).join(" · ")}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProjectCreateDialog({ onClose, onCreated }) {
  const { t } = useI18n();
  const options = useRequest(() => api.projectOptions(), []);
  const [form, setForm] = useState({
    creator_id: "",
    supervisor_id: "",
    client_id: "",
    external_client: "",
    annotation: "",
    status: "PENDING",
    payment_status: "UNPAYED",
    price: "0",
    additional_costs: "0",
    commission: "0",
    fv_num: "",
    group_lang: "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const first = options.data?.staff?.[0];
    if (first && !form.creator_id)
      setForm((value) => ({ ...value, creator_id: String(first.id) }));
  }, [options.data, form.creator_id]);
  const update = (event) =>
    setForm((value) => ({ ...value, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.createProject(form);
      onCreated();
    } catch (reason) {
      setError(reason.message);
      setSaving(false);
    }
  };
  return (
    <DialogShell
      title={t('forms.newProject')}
      subtitle={t('office.descriptions.projects')}
      onClose={onClose}
    >
      <form className="project-form" onSubmit={submit}>
        <p className="transfer-location-hint">{t('forms.locationMapHint')}</p>
        <div className="project-form-grid">
          <label>
            {t('forms.createdBy')}{" "}
            <select
              required
              name="creator_id"
              value={form.creator_id}
              onChange={update}
              disabled={options.loading}
            >
              {options.data?.staff?.map((account) => (
                <option value={account.id} key={account.id}>
                  {account.signature || account.username} · {account.name}{" "}
                  {account.surname}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('forms.status')}{" "}
            <select name="status" value={form.status} onChange={update}>
              {[
                "PENDING",
                "ACTIVE",
                "RUNNING",
                "ACCEPTED",
                "CONFIRMED",
                "RECONFIRMED",
                "ONSITE",
                "NOSHOW",
                "POB",
                "DROPPED",
                "PRICED",
                "CANCELLED",
              ].map((value) => (
                <option key={value} value={value}>{projectStatusLabel(value, t)}</option>
              ))}
            </select>
          </label>
          <div className="project-client-field">
            <label htmlFor="new-project-client">{t('roles.CLIENT')}</label>
            <ProjectClientPicker
              inputId="new-project-client"
              value={form.client_id}
              onChange={(clientId) => setForm((current) => ({ ...current, client_id: clientId }))}
            />
          </div>
          <label>
            {t('forms.supervisor')}{" "}
            <select
              name="supervisor_id"
              value={form.supervisor_id}
              onChange={update}
            >
              <option value="">{t('forms.unassigned')}</option>
              {options.data?.staff?.map((account) => (
                <option value={account.id} key={account.id}>{account.signature || account.username} · {account.name} {account.surname}</option>
              ))}
            </select>
          </label>
          <label>
            {t('forms.externalClient')}{" "}
            <input
              name="external_client"
              value={form.external_client}
              onChange={update}
              maxLength="140"
              placeholder={t('roles.CLIENT')}
            />
          </label>
          <label>
            {t('forms.projectName')}{" "}
            <input
              name="annotation"
              value={form.annotation}
              onChange={update}
              maxLength="140"
              placeholder={t('forms.newProject')}
            />
          </label>
          <label>
            {t('forms.price')}{" "}
            <input
              name="price"
              value={form.price}
              onChange={update}
              inputMode="numeric"
            />
          </label>
          <label>
            {t('forms.payment')}{" "}
            <select
              name="payment_status"
              value={form.payment_status}
              onChange={update}
            >
              {["UNPAYED", "BANK_TRANSACTION", "CREDIT_CARD", "CASH"].map(
                (value) => (
                  <option key={value} value={value}>{projectPaymentLabel(value, t)}</option>
                ),
              )}
            </select>
          </label>
          <label>
            {t('forms.additionalCosts')}{" "}
            <input
              name="additional_costs"
              value={form.additional_costs}
              onChange={update}
              inputMode="numeric"
            />
          </label>
          <label>
            {t('forms.commission')}{" "}
            <input
              name="commission"
              value={form.commission}
              onChange={update}
              inputMode="numeric"
            />
          </label>
          <label>
            {t('forms.invoice')}{" "}
            <input
              name="fv_num"
              value={form.fv_num}
              onChange={update}
              maxLength="140"
            />
          </label>
          <label>
            {t('forms.groupLanguage')}{" "}
            <input
              name="group_lang"
              value={form.group_lang}
              onChange={update}
              maxLength="2"
              placeholder="e.g. pl"
            />
          </label>
        </div>
        <label>
          {t('forms.description')}{" "}
          <textarea
            name="notes"
            value={form.notes}
            onChange={update}
            rows="3"
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="project-form-actions">
          <button type="button" onClick={onClose}>
            {t('forms.cancel')}
          </button>
          <button
            className="project-create-button"
            disabled={saving || options.loading}
          >
            {saving ? t('forms.creating') : t('forms.createProject')}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function ProjectEditDialog({ project, onClose, onUpdated, onRequestDelete }) {
  const { t } = useI18n();
  const options = useRequest(() => api.projectOptions(), []);
  const [form, setForm] = useState({
    supervisor_id: project.supervisor_id ? String(project.supervisor_id) : "",
    client_id: project.client_id ? String(project.client_id) : "",
    external_client: project.external_client || "",
    annotation: project.annotation || "",
    status: project.status || "PENDING",
    payment_status: project.payment_status || "UNPAYED",
    price: String(project.price ?? 0),
    additional_costs: String(project.additional_costs ?? 0),
    commission: String(project.commission ?? 0),
    fv_num: project.fv_num || "",
    group_lang: project.group_lang || "",
    notes: project.notes || "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const update = (event) =>
    setForm((value) => ({ ...value, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.updateProject(project.id, form);
      onUpdated();
    } catch (reason) {
      setError(reason.message);
      setSaving(false);
    }
  };
  return (
    <DialogShell
      title={t('forms.editProject')}
      subtitle={`${t('forms.project')} #${project.id}`}
      onClose={onClose}
    >
      <form className="project-form" onSubmit={submit}>
        <div className="project-form-grid">
          <label className="form-wide">
            {t('forms.projectName')}
            <input name="annotation" value={form.annotation} onChange={update} maxLength="140" />
          </label>
          <label>
            {t('forms.status')}
            <select name="status" value={form.status} onChange={update}>
              {["PENDING", "ACTIVE", "RUNNING", "ACCEPTED", "CONFIRMED", "RECONFIRMED", "ONSITE", "NOSHOW", "POB", "DROPPED", "PRICED", "CANCELLED", "CANCELLEDNOTCONFIRMED"].map((value) => <option key={value} value={value}>{projectStatusLabel(value, t)}</option>)}
            </select>
          </label>
          <label>
            {t('forms.payment')}
            <select name="payment_status" value={form.payment_status} onChange={update}>
              {["UNPAYED", "BANK_TRANSACTION", "CREDIT_CARD", "CASH"].map((value) => <option key={value} value={value}>{projectPaymentLabel(value, t)}</option>)}
            </select>
          </label>
          <div className="project-client-field">
            <label htmlFor="edit-project-client">{t('roles.CLIENT')}</label>
            <ProjectClientPicker
              inputId="edit-project-client"
              value={form.client_id}
              initialLabel={project.client_signature}
              disabled={options.loading}
              onChange={(clientId) => setForm((current) => ({ ...current, client_id: clientId }))}
            />
          </div>
          <label>
            {t('forms.supervisor')}
            <select name="supervisor_id" value={form.supervisor_id} onChange={update} disabled={options.loading}>
              <option value="">{t('forms.unassigned')}</option>
              {form.supervisor_id && !options.data?.staff?.some((account) => String(account.id) === form.supervisor_id) && (
                <option value={form.supervisor_id}>{project.supervisor_signature || `#${form.supervisor_id}`}</option>
              )}
              {options.data?.staff?.map((account) => (
                <option value={account.id} key={account.id}>{account.signature || account.username} · {account.name} {account.surname}</option>
              ))}
            </select>
          </label>
          <label className="form-wide">
            {t('forms.externalClient')}
            <input name="external_client" value={form.external_client} onChange={update} maxLength="140" />
          </label>
          <label>
            {t('forms.price')}
            <input name="price" value={form.price} onChange={update} inputMode="numeric" />
          </label>
          <label>
            {t('forms.additionalCosts')}
            <input name="additional_costs" value={form.additional_costs} onChange={update} inputMode="numeric" />
          </label>
          <label>
            {t('forms.commission')}
            <input name="commission" value={form.commission} onChange={update} inputMode="numeric" />
          </label>
          <label>
            {t('forms.invoice')}
            <input name="fv_num" value={form.fv_num} onChange={update} maxLength="140" />
          </label>
          <label>
            {t('forms.groupLanguage')}
            <input name="group_lang" value={form.group_lang} onChange={update} maxLength="2" />
          </label>
        </div>
        <label>
          {t('forms.description')}
          <textarea name="notes" value={form.notes} onChange={update} rows="5" />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="project-form-actions">
          <button type="button" className="project-danger-button" onClick={() => onRequestDelete?.({ type: "project", id: project.id, label: project.annotation || `${t('forms.project')} #${project.id}` })}>{t('forms.deleteProject')}</button>
          <button type="button" onClick={onClose}>{t('forms.cancel')}</button>
          <button className="project-create-button" disabled={saving}>
            {saving ? t('office.forms.saving') : t('office.forms.saveChanges')}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function RouteCreateDialog({ projectId, defaultDate, onClose, onCreated, onSingle }) {
  const { t } = useI18n();
  const options = useRequest(() => api.haulOptions(), []);
  const [points, setPoints] = useState([
    { place: "", address: "", departure_date: defaultDate, departure_time: "08:00" },
    { place: "", address: "", arrival_date: defaultDate, arrival_time: "09:00", departure_date: defaultDate, departure_time: "10:00" },
    { place: "", address: "", arrival_date: defaultDate, arrival_time: "11:00" },
  ]);
  const [details, setDetails] = useState({ num_passengers: "0", tour_leader_phone: "", price: "0", additional_costs: "0", notes: "" });
  const [assignment, setAssignment] = useState({ mode: "none", driver_id: "", car_id: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const updatePoint = (index, field, value) => setPoints((current) => current.map((point, pointIndex) => pointIndex === index ? { ...point, [field]: value } : point));
  const addStop = () => setPoints((current) => [
    ...current.slice(0, -1),
    { place: "", address: "", arrival_date: defaultDate, arrival_time: "", departure_date: defaultDate, departure_time: "" },
    current[current.length - 1],
  ]);
  const removeStop = (index) => setPoints((current) => current.filter((_, pointIndex) => pointIndex !== index));
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (assignment.mode === "same" && !assignment.driver_id && !assignment.car_id) {
      setError(t('forms.routeAssignmentRequired'));
      return;
    }
    const legs = points.slice(0, -1).map((point, index) => {
      const destination = points[index + 1];
      return {
        departure_meta: point.place,
        departure_address: point.address,
        departure_latitude: point.latitude || null,
        departure_longitude: point.longitude || null,
        departure_date: point.departure_date,
        departure_time: point.departure_time,
        arrival_meta: destination.place,
        arrival_address: destination.address,
        arrival_latitude: destination.latitude || null,
        arrival_longitude: destination.longitude || null,
        arrival_date: destination.arrival_date,
        arrival_time: destination.arrival_time,
      };
    });
    setSaving(true);
    try {
      await api.createRoute(projectId, {
        ...details,
        legs,
        assignment: assignment.mode === "same" ? {
          driver_id: assignment.driver_id || null,
          car_id: assignment.car_id || null,
        } : null,
      });
      onCreated();
    } catch (reason) {
      setError(reason.message);
      setSaving(false);
    }
  };
  return (
    <DialogShell title={t('forms.createRoute')} subtitle={`${t('forms.project')} #${projectId}`} onClose={onClose}>
      <div className="transfer-mode-switch" role="tablist" aria-label={t('forms.transferType')}>
        <button type="button" role="tab" aria-selected="false" onClick={onSingle}>{t('forms.singleTransfer')}</button>
        <button type="button" role="tab" aria-selected="true">{t('forms.multiStopRoute')}</button>
      </div>
      <form className="project-form route-form" onSubmit={submit}>
        <div className="route-points">
          {points.map((point, index) => {
            const first = index === 0;
            const last = index === points.length - 1;
            return (
              <fieldset className="route-point" key={index}>
                <legend>{first ? t('forms.startPoint') : last ? t('forms.endPoint') : `${t('forms.stop')} ${index}`}</legend>
                {!first && !last && points.length > 3 && <button type="button" className="route-remove-stop" onClick={() => removeStop(index)}>{t('forms.removeStop')}</button>}
                <div className="project-form-grid">
                  <label className="form-wide">{t('forms.place')}<input required value={point.place} onChange={(event) => updatePoint(index, "place", event.target.value)} /></label>
                  <label className="form-wide">{t('forms.address')}<input value={point.address} onChange={(event) => updatePoint(index, "address", event.target.value)} /></label>
                  <RoutePointSearch point={point} onSelect={(result) => {
                    updatePoint(index, "place", point.place || result.name);
                    updatePoint(index, "address", result.label);
                    updatePoint(index, "latitude", result.lat);
                    updatePoint(index, "longitude", result.lon);
                  }} />
                  {!first && <>
                    <label>{t('forms.arrivalDate')}<input required type="date" value={point.arrival_date} onChange={(event) => updatePoint(index, "arrival_date", event.target.value)} /></label>
                    <label>{t('forms.arrivalTime')}<input required type="time" value={point.arrival_time} onChange={(event) => updatePoint(index, "arrival_time", event.target.value)} /></label>
                  </>}
                  {!last && <>
                    <label>{t('forms.departureDate')}<input required type="date" value={point.departure_date} onChange={(event) => updatePoint(index, "departure_date", event.target.value)} /></label>
                    <label>{t('forms.departureTime')}<input required type="time" value={point.departure_time} onChange={(event) => updatePoint(index, "departure_time", event.target.value)} /></label>
                  </>}
                </div>
              </fieldset>
            );
          })}
        </div>
        <button type="button" className="route-add-stop" onClick={addStop}>+ {t('forms.addStop')}</button>
        <fieldset>
          <legend>{t('forms.routeDetails')}</legend>
          <div className="project-form-grid">
            <label>{t('forms.passengers')}<input name="num_passengers" value={details.num_passengers} onChange={(event) => setDetails((value) => ({ ...value, num_passengers: event.target.value }))} inputMode="numeric" /></label>
            <label>{t('forms.tourLeaderPhone')}<input name="tour_leader_phone" value={details.tour_leader_phone} onChange={(event) => setDetails((value) => ({ ...value, tour_leader_phone: event.target.value }))} /></label>
            <label>{t('forms.routePrice')}<input name="price" value={details.price} onChange={(event) => setDetails((value) => ({ ...value, price: event.target.value }))} inputMode="numeric" /><small>{t('forms.firstLegValue')}</small></label>
            <label>{t('forms.additionalCosts')}<input name="additional_costs" value={details.additional_costs} onChange={(event) => setDetails((value) => ({ ...value, additional_costs: event.target.value }))} inputMode="numeric" /><small>{t('forms.firstLegValue')}</small></label>
          </div>
          <label>{t('forms.notes')}<textarea value={details.notes} onChange={(event) => setDetails((value) => ({ ...value, notes: event.target.value }))} rows="3" /></label>
        </fieldset>
        <fieldset>
          <legend>{t('forms.routeAssignments')}</legend>
          <div className="route-assignment-choice">
            <label><input type="radio" checked={assignment.mode === "none"} onChange={() => setAssignment((value) => ({ ...value, mode: "none" }))} />{t('forms.assignLater')}</label>
            <label><input type="radio" checked={assignment.mode === "same"} onChange={() => setAssignment((value) => ({ ...value, mode: "same" }))} />{t('forms.sameResources')}</label>
          </div>
          {assignment.mode === "same" && (
            <div className="project-form-grid route-resource-fields">
              <label>{t('forms.driver')}<select value={assignment.driver_id} onChange={(event) => setAssignment((value) => ({ ...value, driver_id: event.target.value }))}><option value="">{t('forms.noDriverYet')}</option>{options.data?.drivers?.map((driver) => <option key={driver.id} value={driver.id}>{driver.signature} · {driver.drivers_license || "—"}</option>)}</select></label>
              <label>{t('forms.vehicle')}<select value={assignment.car_id} onChange={(event) => setAssignment((value) => ({ ...value, car_id: event.target.value }))}><option value="">{t('forms.noVehicle')}</option>{options.data?.cars?.map((car) => <option key={car.id} value={car.id}>{car.signature} · {car.num_passengers} {t('office.timeline.seats')}</option>)}</select></label>
            </div>
          )}
          <p className="route-assignment-help">{assignment.mode === "same" ? t('forms.sameResourcesHelp') : t('forms.assignLaterHelp')}</p>
        </fieldset>
        {error && <p className="form-error">{error}</p>}
        <div className="project-form-actions">
          <button type="button" onClick={onClose}>{t('forms.cancel')}</button>
          <button className="project-create-button" disabled={saving}>{saving ? t('office.forms.saving') : t('forms.createRoute')}</button>
        </div>
      </form>
    </DialogShell>
  );
}

function TransferCreateDialog({ projectId, defaultDate, transfer = null, onClose, onCreated, onRequestDelete }) {
  const { t } = useI18n();
  const editing = Boolean(transfer);
  const [createMode, setCreateMode] = useState("single");
  const [form, setForm] = useState({
    tweet: transfer?.tweet || "",
    num_passengers: String(transfer?.num_passengers ?? 0),
    tour_leader_phone: transfer?.tour_leader_phone || "",
    price: String(transfer?.price ?? 0),
    additional_costs: String(transfer?.additional_costs ?? 0),
    notes: transfer?.notes || "",
    departure_date: transfer?.departure_date?.slice(0, 10) || defaultDate,
    departure_time: transfer ? shortTime(transfer.departure_time, "08:00") : "08:00",
    departure_meta: transfer?.departure_meta || "",
    departure_address: transfer?.departure_address || "",
    departure_latitude: transfer?.departure_latitude ?? "",
    departure_longitude: transfer?.departure_longitude ?? "",
    arrival_date: transfer?.arrival_date?.slice(0, 10) || defaultDate,
    arrival_time: transfer ? shortTime(transfer.arrival_time, "09:00") : "09:00",
    arrival_meta: transfer?.arrival_meta || "",
    arrival_address: transfer?.arrival_address || "",
    arrival_latitude: transfer?.arrival_latitude ?? "",
    arrival_longitude: transfer?.arrival_longitude ?? "",
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const update = (event) =>
    setForm((value) => ({ ...value, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editing) await api.updateTransfer(transfer.id, form);
      else await api.createTransfer(projectId, form);
      onCreated();
    } catch (reason) {
      setError(reason.message);
      setSaving(false);
    }
  };
  if (!editing && createMode === "route") {
    return <RouteCreateDialog projectId={projectId} defaultDate={defaultDate} onClose={onClose} onCreated={onCreated} onSingle={() => setCreateMode("single")} />;
  }
  return (
    <DialogShell
      title={editing ? t('forms.editTransfer') : t('forms.insertTransfer')}
      subtitle={`${t('forms.project')} #${projectId}${editing ? ` · #${transfer.id}` : ""}`}
      onClose={onClose}
    >
      {!editing && (
        <div className="transfer-mode-switch" role="tablist" aria-label={t('forms.transferType')}>
          <button type="button" role="tab" aria-selected="true">{t('forms.singleTransfer')}</button>
          <button type="button" role="tab" aria-selected="false" onClick={() => setCreateMode("route")}>{t('forms.multiStopRoute')}</button>
        </div>
      )}
      <form className="project-form" onSubmit={submit}>
        <div className="project-form-grid">
          <label className="form-wide">
            {t('forms.transferName')}{" "}
            <input
              required
              name="tweet"
              value={form.tweet}
              onChange={update}
              placeholder="e.g. KRAKOW – BALICE"
            />
          </label>
          <label>
            {t('forms.passengers')}{" "}
            <input
              name="num_passengers"
              value={form.num_passengers}
              onChange={update}
              inputMode="numeric"
            />
          </label>
          <label>
            {t('forms.tourLeaderPhone')}{" "}
            <input
              name="tour_leader_phone"
              value={form.tour_leader_phone}
              onChange={update}
            />
          </label>
          <label>
            {t('forms.price')}{" "}
            <input
              name="price"
              value={form.price}
              onChange={update}
              inputMode="numeric"
            />
          </label>
          <label>
            {t('forms.additionalCosts')}{" "}
            <input
              name="additional_costs"
              value={form.additional_costs}
              onChange={update}
              inputMode="numeric"
            />
          </label>
        </div>
        <fieldset>
          <legend>{t('forms.departure')}</legend>
          <div className="project-form-grid">
            <label>
              {t('forms.date')}{" "}
              <input
                required
                type="date"
                name="departure_date"
                value={form.departure_date}
                onChange={update}
              />
            </label>
            <label>
              {t('forms.time')}{" "}
              <input
                required
                type="time"
                name="departure_time"
                value={form.departure_time}
                onChange={update}
              />
            </label>
            <label className="form-wide">
              {t('forms.place')}{" "}
              <input
                name="departure_meta"
                value={form.departure_meta}
                onChange={update}
                placeholder={t('forms.place')}
              />
            </label>
            <label className="form-wide">
              {t('forms.address')}{" "}
              <input
                name="departure_address"
                value={form.departure_address}
                onChange={update}
              />
            </label>
            <LocationSearch prefix="departure" form={form} setForm={setForm} />
          </div>
        </fieldset>
        <fieldset>
          <legend>{t('forms.arrival')}</legend>
          <div className="project-form-grid">
            <label>
              {t('forms.date')}{" "}
              <input
                required
                type="date"
                name="arrival_date"
                value={form.arrival_date}
                onChange={update}
              />
            </label>
            <label>
              {t('forms.time')}{" "}
              <input
                required
                type="time"
                name="arrival_time"
                value={form.arrival_time}
                onChange={update}
              />
            </label>
            <label className="form-wide">
              {t('forms.place')}{" "}
              <input
                name="arrival_meta"
                value={form.arrival_meta}
                onChange={update}
                placeholder={t('forms.place')}
              />
            </label>
            <label className="form-wide">
              {t('forms.address')}{" "}
              <input
                name="arrival_address"
                value={form.arrival_address}
                onChange={update}
              />
            </label>
            <LocationSearch prefix="arrival" form={form} setForm={setForm} />
          </div>
        </fieldset>
        <label>
          {t('forms.notes')}{" "}
          <textarea
            name="notes"
            value={form.notes}
            onChange={update}
            rows="3"
          />
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="project-form-actions">
          {editing && <button type="button" className="project-danger-button" onClick={() => onRequestDelete?.({ type: "transfer", id: transfer.id, label: transfer.tweet || `${t('forms.transfer')} #${transfer.id}` })}>{t('forms.deleteTransfer')}</button>}
          <button type="button" onClick={onClose}>
            {t('forms.cancel')}
          </button>
          <button className="project-create-button" disabled={saving}>
            {saving ? t('office.forms.saving') : editing ? t('office.forms.saveChanges') : t('forms.insertTransfer')}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
function ProjectDetail() {
  const { locale, t } = useI18n();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [range, setRange] = useState(projectRange);
  const [selected, setSelected] = useState(null);
  const [insertOpen, setInsertOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [revision, setRevision] = useState(0);
  const state = useRequest(
    () => api.project(projectId, range.start, range.end),
    [projectId, range.start, range.end, revision],
  );
  const project = state.data?.data;
  const transfers = state.data?.transfers || [];
  const money = (value) =>
    value === null || value === undefined || value === "" || Number(value) === 0
      ? null
      : `${value} PLN`;
  const updatedLabel = project?.updated_at
    ? (() => {
        const value = new Date(project.updated_at);
        return Number.isNaN(value.getTime())
          ? String(project.updated_at).slice(0, 16).replace("T", " ")
          : new Intl.DateTimeFormat(localeTag(locale), { dateStyle: "medium", timeStyle: "short" }).format(value);
      })()
    : null;
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDeleteError("");
    try {
      if (deleteTarget.type === "project") {
        await api.deleteProject(deleteTarget.id);
        navigate("/projects");
        return;
      }
      await api.deleteTransfer(deleteTarget.id);
      setDeleteTarget(null);
      setSelected(null);
      setRevision((value) => value + 1);
    } catch (reason) {
      setDeleteError(reason.message || t('forms.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };
  return (
    <Page
      title={project?.annotation || (project ? `${t('forms.project')} #${project.id}` : t('forms.project'))}
      description={project ? `${t('forms.project')} #${project.id} · ${project.company_name || project.client_signature || project.external_client || t('forms.unassigned')}` : t('forms.projectDetails')}
      guideKey="projectDetail"
    >
      <FilterPanel label={t('forms.transferRange')} activeCount={[range.start, range.end].filter(Boolean).length} onClear={() => setRange({ start: "", end: "" })}><div className="project-detail-toolbar">
        <div className="range-controls">
          <label>
            {t('forms.from')}{" "}
            <input
              type="date"
              value={range.start}
              onChange={(event) =>
                setRange(({ end }) => ({
                  start: event.target.value,
                  end: end < event.target.value ? event.target.value : end,
                }))
              }
            />
          </label>
          <label>
            {t('forms.to')}{" "}
            <input
              type="date"
              value={range.end}
              min={range.start}
              onChange={(event) =>
                setRange(({ start }) => ({ start, end: event.target.value }))
              }
            />
          </label>
        </div>
      </div></FilterPanel>
      <Notice state={state} />
      {project && (
        <>
          <div className="project-summary">
            <div className="project-summary-heading">
              <div>
                <small>{t('forms.project')} #{project.id}</small>
                <h2>{project.annotation || t('forms.noAnnotation')}</h2>
                <p className="project-summary-client">
                  <span>{t('roles.CLIENT')}</span>
                  <strong>{project.company_name || project.client_signature || project.external_client || t('forms.unassigned')}</strong>
                </p>
              </div>
              <div className="project-summary-actions">
                <button type="button" className="project-secondary-button" onClick={() => setEditOpen(true)}>
                  {t('forms.editProject')}
                </button>
                <button type="button" className="project-create-button" onClick={() => setInsertOpen(true)}>
                  + {t('forms.insertTransfer')}
                </button>
              </div>
            </div>
            {project.notes && (
              <div className="project-summary-description">
                <b>{t('forms.description')}</b>
                <p>{project.notes}</p>
              </div>
            )}
            <div className="project-summary-facts">
              <span className={`project-fact project-fact-status tone-${transferTone(project.status)}`}>{t('forms.status')} <b>{projectStatusLabel(project.status, t)}</b></span>
              <span className={`project-fact project-fact-payment ${project.payment_status === "UNPAYED" ? "is-unpaid" : "is-paid"}`}>{t('forms.payment')} <b>{projectPaymentLabel(project.payment_status, t)}</b></span>
              {project.supervisor_signature && (
                <span className="project-fact">
                  {t('forms.supervisor')} <b>{project.supervisor_signature}</b>
                </span>
              )}
              {project.creator_signature && (
                <span className="project-fact">
                  {t('forms.createdBy')} <b>{project.creator_signature}</b>
                </span>
              )}
              {money(project.price) && (
                <span className="project-fact">
                  {t('forms.price')} <b>{money(project.price)}</b>
                </span>
              )}
              {money(project.additional_costs) && (
                <span className="project-fact">
                  {t('forms.additionalCosts')} <b>{money(project.additional_costs)}</b>
                </span>
              )}
              {money(project.commission) && (
                <span className="project-fact">
                  {t('forms.commission')} <b>{money(project.commission)}</b>
                </span>
              )}
              {project.fv_num && (
                <span className="project-fact">
                  {t('forms.invoice')} <b>{project.fv_num}</b>
                </span>
              )}
              {project.group_lang && <span className="project-fact">{t('forms.groupLanguage')} <b>{project.group_lang}</b></span>}
              {updatedLabel && <span className="project-fact project-fact-updated">{t('forms.updated')} <b>{updatedLabel}</b></span>}
            </div>
          </div>
          <p className="project-transfer-count">
            {transfers.length}{" "}
            {transfers.length === 1 ? t('forms.transfer') : t('forms.transfers')}
          </p>
          <div className="project-transfer-list">
            {transfers.map((transfer) => (
              <button
                type="button"
                className="project-transfer-row"
                key={transfer.id}
                onClick={() => setSelected(transfer)}
              >
                <time>
                  <b>
                    {new Intl.DateTimeFormat(localeTag(locale), {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    }).format(new Date(`${transfer.departure_date}T12:00:00`))}
                  </b>
                  <span>
                    {transferTimes(transfer)}
                  </span>
                </time>
                <div className="project-transfer-route">
                  <strong>{transfer.tweet || t('forms.transfer')}</strong>
                  <span>
                    {transfer.departure_meta || "—"} <i>→</i>{" "}
                    {transfer.arrival_meta || "—"}
                  </span>
                </div>
                <div className="project-transfer-assignment">
                  <b>{transfer.cars || `${t('forms.unassigned')} ${t('forms.vehicle').toLocaleLowerCase(locale)}`}</b>
                  <span>{transfer.drivers || `${t('forms.unassigned')} ${t('forms.driver').toLocaleLowerCase(locale)}`}</span>
                </div>
              </button>
            ))}
            {transfers.length === 0 && (
              <p className="status">{t('forms.noTransfers')}</p>
            )}
          </div>
        </>
      )}
      {selected &&
        createPortal(
          <ProjectTransferDialog
            transfer={transfers.find(item => item.id === selected.id) || selected}
            onClose={() => setSelected(null)}
            onChanged={() => setRevision((value) => value + 1)}
            allowEdit
            onRequestDelete={(target) => { setSelected(null); setDeleteTarget(target); }}
          />,
          document.body,
        )}
      {insertOpen &&
        createPortal(
          <TransferCreateDialog
            projectId={projectId}
            defaultDate={range.start}
            onClose={() => setInsertOpen(false)}
            onCreated={() => {
              setInsertOpen(false);
              setRevision((value) => value + 1);
            }}
          />,
          document.body,
        )}
      {editOpen && project &&
        createPortal(
          <ProjectEditDialog
            project={project}
            onClose={() => setEditOpen(false)}
            onUpdated={() => {
              setEditOpen(false);
              setRevision((value) => value + 1);
            }}
            onRequestDelete={(target) => { setEditOpen(false); setDeleteTarget(target); }}
          />,
          document.body,
        )}
      {deleteTarget && createPortal(
        <DeleteConfirmDialog
          target={deleteTarget}
          saving={deleting}
          error={deleteError}
          onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteError(""); } }}
          onConfirm={confirmDelete}
        />,
        document.body,
      )}
    </Page>
  );
}

function LocationSearch({ prefix, form, setForm }) {
  const { locale, t } = useI18n();
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lookup = async () => {
    const query = (form[`${prefix}_address`] || form[`${prefix}_meta`] || "").trim();
    if (query.length < 3) { setError(t("office.maps.enterAddress")); return; }
    setBusy(true); setError(""); setResults([]);
    try { setResults((await api.geocode(query, locale)).data || []); }
    catch (reason) { setError(reason.message); }
    finally { setBusy(false); }
  };
  const select = (result) => {
    setForm((value) => ({ ...value,
      [`${prefix}_meta`]: value[`${prefix}_meta`] || result.name,
      [`${prefix}_address`]: result.label,
      [`${prefix}_latitude`]: result.lat,
      [`${prefix}_longitude`]: result.lon,
    }));
    setResults([]); setError("");
  };
  return <div className="location-search form-wide">
    <button type="button" className="location-search-button" onClick={lookup} disabled={busy}>{busy ? t("common.loading") : t("office.maps.search")}</button>
    {error && <small className="location-search-error">{error}</small>}
    {results.length > 0 && <div className="location-search-results" role="listbox" aria-label={t("office.maps.results")}>
      {results.map((result) => <button type="button" key={`${result.lat}-${result.lon}`} onClick={() => select(result)}><b>{result.name}</b><span>{result.label}</span></button>)}
    </div>}
  </div>;
}

function RoutePointSearch({ point, onSelect }) {
  const { locale, t } = useI18n();
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const lookup = async () => {
    const query = (point.address || point.place || "").trim();
    if (query.length < 3) { setError(t("office.maps.enterAddress")); return; }
    setBusy(true); setError(""); setResults([]);
    try { setResults((await api.geocode(query, locale)).data || []); }
    catch (reason) { setError(reason.message); }
    finally { setBusy(false); }
  };
  return <div className="location-search form-wide">
    <button type="button" className="location-search-button" onClick={lookup} disabled={busy}>{busy ? t("common.loading") : t("office.maps.search")}</button>
    {error && <small className="location-search-error">{error}</small>}
    {results.length > 0 && <div className="location-search-results" role="listbox" aria-label={t("office.maps.results")}>
      {results.map((result) => <button type="button" key={`${result.lat}-${result.lon}`} onClick={() => { onSelect(result); setResults([]); }}><b>{result.name}</b><span>{result.label}</span></button>)}
    </div>}
  </div>;
}

function ProjectTransferDialog({ transfer, onClose, onChanged, showAssignments = true, allowEdit = false, onRequestDelete }) {
  const { locale, t } = useI18n();
  const dialogRef = useDialogAccessibility(onClose);
  const [editing, setEditing] = useState(false);
  const formatTimestamp = (value) => {
    if (!value) return null;
    const parsed = new Date(String(value).replace(" ", "T"));
    return Number.isNaN(parsed.getTime())
      ? String(value).slice(0, 16).replace("T", " ")
      : new Intl.DateTimeFormat(localeTag(locale), { dateStyle: "medium", timeStyle: "short" }).format(parsed);
  };
  if (editing) {
    return (
      <TransferCreateDialog
        projectId={transfer.project_id}
        defaultDate={transfer.departure_date}
        transfer={transfer}
        onClose={() => setEditing(false)}
        onCreated={() => {
          onChanged?.();
          onClose();
        }}
        onRequestDelete={onRequestDelete}
      />
    );
  }
  const day = new Intl.DateTimeFormat(localeTag(locale), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(`${transfer.departure_date}T12:00:00`));
  return (
    <div
      className="project-transfer-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <section
        ref={dialogRef}
        className="project-transfer-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t("office.transferDetail.title")}
        tabIndex="-1"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="timeline-detail-close"
          onClick={onClose}
          aria-label={t("office.transferDetail.close")}
        >
          ×
        </button>
        <p>
          {day} · {transferTimes(transfer)}
        </p>
        <h3>{transfer.tweet || t("office.transferDetail.untitled")}</h3>
        <dl>
          <div>
            <dt>{t("office.transferDetail.route")}</dt>
            <dd>
              {transfer.departure_meta || "—"} → {transfer.arrival_meta || "—"}
            </dd>
          </div>
          <div>
            <dt>{t("office.transferDetail.address")}</dt>
            <dd>
              {transfer.departure_address || "—"} →{" "}
              {transfer.arrival_address || "—"}
            </dd>
          </div>
          <div>
            <dt>{t("office.transferDetail.vehicle")}</dt>
            <dd>{transfer.cars || t("office.transferDetail.unassigned")}</dd>
          </div>
          <div>
            <dt>{t("office.transferDetail.driver")}</dt>
            <dd>{transfer.drivers || t("office.transferDetail.unassigned")}</dd>
          </div>
          <div>
            <dt>{t("office.transferDetail.passengers")}</dt>
            <dd>{transfer.num_passengers ?? "—"}</dd>
          </div>
          {transfer.project_id && (
            <div>
              <dt>{t("office.transferDetail.project")}</dt>
              <dd>
                <Link
                  className="transfer-project-link"
                  to={`/projects/${transfer.project_id}?start=${transfer.departure_date}&end=${transfer.arrival_date || transfer.departure_date}`}
                  onClick={onClose}
                >
                  {projectReference(transfer.project_id, transfer.project_annotation, t)}
                </Link>
              </dd>
            </div>
          )}
          {transfer.tour_leader_phone && (
            <div>
              <dt>{t("office.transferDetail.tourLeader")}</dt>
              <dd>{transfer.tour_leader_phone}</dd>
            </div>
          )}
          {transfer.created_at && (
            <div>
              <dt>{t("office.transferDetail.createdAt")}</dt>
              <dd>{formatTimestamp(transfer.created_at)}</dd>
            </div>
          )}
          {transfer.updated_at && (
            <div>
              <dt>{t("office.transferDetail.updatedAt")}</dt>
              <dd>{formatTimestamp(transfer.updated_at)}</dd>
            </div>
          )}
          {transfer.project_creator_signature && (
            <div>
              <dt>{t("office.transferDetail.projectCreator")}</dt>
              <dd>{transfer.project_creator_signature}</dd>
            </div>
          )}
        </dl>
        {transfer.notes && (
          <div className="project-transfer-notes">
            <b>{t("office.transferDetail.notes")}</b>
            <p>{transfer.notes}</p>
          </div>
        )}
        <TransferMap transfer={transfer} />
        {allowEdit && (
          <div className="project-transfer-dialog-actions">
            <button type="button" className="project-secondary-button" onClick={() => setEditing(true)}>
              {t('forms.editTransfer')}
            </button>
          </div>
        )}
        {showAssignments && <>
          <h4>{t("office.transferDetail.assignments")}</h4>
          <HaulList transferId={transfer.id} onChanged={onChanged} />
        </>}
      </section>
    </div>
  );
}

function DeleteConfirmDialog({ target, saving, error, onClose, onConfirm }) {
  const { t } = useI18n();
  const project = target.type === "project";
  return (
    <DialogShell
      title={project ? t('forms.deleteProjectTitle') : t('forms.deleteTransferTitle')}
      subtitle={t('forms.deleteConfirmationSubtitle')}
      onClose={onClose}
    >
      <div className="delete-confirmation">
        <p>{project ? t('forms.deleteProjectWarning') : t('forms.deleteTransferWarning')}</p>
        <strong>{target.label}</strong>
        <small>{t('forms.deleteSoftArchive')}</small>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="project-form-actions">
          <button type="button" onClick={onClose} disabled={saving}>{t('forms.cancel')}</button>
          <button type="button" className="project-danger-button" onClick={onConfirm} disabled={saving}>
            {saving ? t('office.forms.saving') : t('forms.confirmDelete')}
          </button>
        </div>
      </div>
    </DialogShell>
  );
}

function CalendarResources({ dates, resources, showProjectColumn, columns, minimumWidth }) {
  const { t } = useI18n();
  return (
    <div
      className="calendar-resources"
      style={{ gridTemplateColumns: columns, minWidth: minimumWidth, width: "100%" }}
    >
      {showProjectColumn && (
        <div className="calendar-resource-corner">{t("office.calendar.dailyCapacity")}</div>
      )}
      {dates.map((day) => {
        const resource = resources.get(day);
        const cars = resource?.cars || [];
        const drivers = resource?.drivers || [];
        const carText = cars
          .map(
            (item) =>
              `${item.count}${String(item.group).slice(0, 1).toUpperCase()}`,
          )
          .join(" · ");
        const driverText = drivers
          .map((item) => `${item.count}${String(item.license).toUpperCase()}`)
          .join(" · ");
        const carTitle =
          cars
            .map(
              (item) =>
                `${item.group}: ${item.count} ${t("office.calendarExtra.needed")} / ${item.available} ${t("office.calendarExtra.active")}`,
            )
            .join("\n") || t("office.calendarExtra.noVehicleRequirement");
        const driverTitle =
          drivers
            .map(
              (item) =>
                `${item.license}: ${item.count} ${t("office.calendarExtra.needed")} / ${item.available} ${t("office.calendarExtra.active")}`,
            )
            .join("\n") || t("office.calendarExtra.noDriverRequirement");
        return (
          <div className="calendar-resource-day" key={day}>
            <div
              className={`calendar-resource-cars ${resource?.enough_for_required === false ? "shortage" : ""}`}
              title={carTitle}
            >
              <span>{t("office.calendar.cars")}</span>
              <b>{carText || "—"}</b>
            </div>
            <div
              className={`calendar-resource-drivers ${resource?.enough === false ? "shortage" : ""}`}
              title={driverTitle}
            >
              <span>{t("office.calendar.drivers")}</span>
              <b>{driverText || "—"}</b>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function calendarProjectTitle(project, fallback = "Unassigned project", projectLabel = "Project") {
  return (
    project.company_name ||
    [project.client_name, project.client_surname].filter(Boolean).join(" ") ||
    project.external_client ||
    project.project_annotation ||
    (project.project_id ? `${projectLabel} #${project.project_id}` : fallback)
  );
}

function mobileResourceText(items, field) {
  return (items || [])
    .map((item) => `${item.count}${String(item[field]).slice(0, field === "license" ? 2 : 1).toUpperCase()}`)
    .join(" · ") || "—";
}

function calendarTransferForDialog(transfer) {
  const hauls = transfer.hauls || [transfer];
  const unique = (values) => [...new Set(values.filter(Boolean))].join(", ");
  return {
    ...transfer,
    id: transfer.id || transfer.transfer_id,
    cars: transfer.cars || unique(hauls.map((haul) => haul.car_signature || haul.car_group_signature || haul.external_car_info)),
    drivers: transfer.drivers || unique(hauls.map((haul) => haul.driver_signature || haul.external_driver_info)),
  };
}

function CalendarMobile({ projects, dates, locale, onOpenTransfer }) {
  const { t } = useI18n();
  return (
    <div className="calendar-mobile" aria-label={t("office.calendarExtra.ariaProjects")}>
      <p className="calendar-mobile-hint">
        {t("office.calendarExtra.mobileHint")}
      </p>
      {projects.map((project) => {
        const title = calendarProjectTitle(project, t("office.calendarExtra.unassignedProject"), t("forms.project"));
        const transfers = [...project.transfers.values()];
        return (
          <article className="mobile-project" key={project.project_id}>
            <header>
              <div>
                <NavLink
                  className="mobile-project-title"
                  to={`/projects/${project.project_id}?start=${dates[0]}&end=${dates[dates.length - 1]}`}
                >
                  <strong>{title}</strong>
                </NavLink>
                <span>
                  {project.project_annotation || t("office.calendar.noAnnotation")}
                </span>
              </div>
              <small>
                {transfers.length}{" "}
                {transfers.length === 1 ? t("office.calendar.transfer") : t("office.calendar.transfers")}
              </small>
            </header>
            <div className="mobile-project-transfers">
              {dates.flatMap((day) =>
                transfers
                  .filter((transfer) => transferOnDay(transfer, day))
                  .map((transfer) => (
                    <div
                      className="mobile-transfer-row"
                      key={transfer.transfer_id}
                    >
                      <time>
                        <b>
                          {formatOfficeDate(day, locale, { weekday: "short", day: "numeric", month: "short" })}
                        </b>
                        <span>
                          {transferTimes(transfer)}
                        </span>
                      </time>
                      <CalendarTransfer
                        transfer={transfer}
                        day={day}
                        projectTitle={title}
                        projectAnnotation={project.project_annotation}
                        showProjectColumn
                        onOpen={onOpenTransfer}
                      />
                    </div>
                  )),
              )}
            </div>
          </article>
        );
      })}
      {projects.length === 0 && (
        <p className="status">{t("office.calendar.noProjects")}</p>
      )}
    </div>
  );
}

function Calendar() {
  const { locale, t } = useI18n();
  const [range, setRange] = useState(initialRange);
  const [revision, setRevision] = useState(0);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const { start, end } = range;
  const state = useRequest(() => api.calendar(start, end), [start, end, revision]);
  const [showProjectColumn, setShowProjectColumn] = useState(true);
  const [sortMode, setSortMode] = useState("time");
  const [query, setQuery] = useState("");
  const [onlyShortages, setOnlyShortages] = useState(false);
  const compact = window.matchMedia("(max-width: 720px)").matches;
  const calendarColumns = `${showProjectColumn ? `${compact ? 170 : 235}px ` : ""}repeat(${boundedDates(start, end).length}, minmax(${compact ? 124 : 180}px, 1fr))`;
  const dates = useMemo(() => boundedDates(start, end), [start, end]);
  const calendarMinimumWidth = `${(showProjectColumn ? (compact ? 170 : 235) : 0) + dates.length * (compact ? 124 : 180)}px`;
  const projects = useMemo(() => {
    const result = new Map();
    (state.data?.data || []).forEach((row) => {
      if (!result.has(row.project_id))
        result.set(row.project_id, { ...row, transfers: new Map() });
      const project = result.get(row.project_id);
      if (!project.transfers.has(row.transfer_id)) {
        project.transfers.set(row.transfer_id, { ...row, hauls: [row] });
      } else {
        project.transfers.get(row.transfer_id).hauls.push(row);
      }
    });
    return [...result.values()].sort((left, right) => {
      const leftTransfers = [...left.transfers.values()];
      const rightTransfers = [...right.transfers.values()];
      if (sortMode === "name")
        return calendarProjectTitle(left, "Unassigned project", t("forms.project")).localeCompare(
          calendarProjectTitle(right, "Unassigned project", t("forms.project")),
          localeTag(locale),
          { sensitivity: "base" },
        );
      if (sortMode === "size")
        return (
          rightTransfers.length - leftTransfers.length ||
          calendarProjectTitle(left, "Unassigned project", t("forms.project")).localeCompare(calendarProjectTitle(right, "Unassigned project", t("forms.project")), localeTag(locale))
        );
      const first = (items) =>
        items
          .map(
            (item) =>
              `${item.departure_date || "9999-12-31"}T${formatTime(item.departure_time, "23:59")}`,
          )
          .sort()[0];
      return (
        first(leftTransfers).localeCompare(first(rightTransfers)) ||
        calendarProjectTitle(left, "Unassigned project", t("forms.project")).localeCompare(calendarProjectTitle(right, "Unassigned project", t("forms.project")), localeTag(locale))
      );
    });
  }, [state.data, sortMode, locale, t]);
  const resources = useMemo(
    () =>
      new Map(
        (state.data?.resources || []).map((resource) => [
          resource.date,
          resource,
        ]),
      ),
    [state.data],
  );
  const visibleProjects = useMemo(() => {
    const needle = query
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
    return projects.filter((project) => {
      const transfers = [...project.transfers.values()];
      const searchable = [
        calendarProjectTitle(project, "Unassigned project", t("forms.project")),
        project.project_annotation,
        ...transfers.flatMap((transfer) => [
          transfer.tweet,
          transfer.departure_meta,
          transfer.arrival_meta,
          transfer.car_signature,
          transfer.car_group_signature,
          transfer.driver_signature,
        ]),
      ]
        .filter(Boolean)
        .join(" ")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
      const matchesSearch = !needle || searchable.includes(needle);
      const hasShortage = transfers.some((transfer) => {
        const resource = resources.get(transfer.departure_date);
        return (
          resource?.enough_for_required === false || resource?.enough === false
        );
      });
      return matchesSearch && (!onlyShortages || hasShortage);
    });
  }, [projects, query, onlyShortages, resources]);
  const changeStart = (value) =>
    setRange({ start: value, end: end < value ? value : end });
  const setQuickRange = (value, days) =>
    setRange({ start: value, end: addDays(value, days) });
  return (
    <Page
      title="Calendar"
      description="Days run left to right; projects and transfers stack top to bottom."
    >
      <div className="calendar-quick-actions">
        <button
          type="button"
          onClick={() => setQuickRange(addDays(isoDate(), -6), 6)}
        >
          {t('office.calendar.last7')}
        </button>
        <button type="button" onClick={() => setQuickRange(isoDate(), 0)}>
          {t('common.today')}
        </button>
        <button
          type="button"
          onClick={() => setQuickRange(addDays(isoDate(), 1), 0)}
        >
          {t('common.tomorrow')}
        </button>
        <button type="button" onClick={() => setQuickRange(isoDate(), 6)}>
          {t('office.calendar.next7')}
        </button>
      </div>
      <FilterPanel label={`${t('navigation.calendar')} ${t('common.filters').toLocaleLowerCase(locale)}`} activeCount={[query.trim(), onlyShortages, !showProjectColumn, sortMode !== 'time', start !== isoDate(), end !== addDays(start, 13)].filter(Boolean).length} onClear={() => { const today = isoDate(); setRange({ start: today, end: addDays(today, 13) }); setSortMode('time'); setQuery(''); setOnlyShortages(false); setShowProjectColumn(true); }}><div className="range-controls">
        <label>
          {t('client.from')} {" "}
          <input
            type="date"
            value={start}
            onChange={(event) => changeStart(event.target.value)}
          />
        </label>
        <label>
          {t('client.to')} {" "}
          <input
            type="date"
            value={end}
            min={start}
            max={addDays(start, 62)}
            onChange={(event) => setRange({ start, end: event.target.value })}
          />
        </label>
        <label className="calendar-sort">
          {t('office.calendar.order')} {" "}
          <select
            value={sortMode}
            onChange={(event) => setSortMode(event.target.value)}
          >
            <option value="time">{t('office.calendar.earliest')}</option>
            <option value="name">{t('office.calendar.projectName')}</option>
            <option value="size">{t('office.calendar.mostTransfers')}</option>
          </select>
        </label>
        <label className="calendar-filter-search">
          {t('office.calendar.find')} {" "}
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('office.calendar.search')}
          />
        </label>
        <label className="calendar-shortages">
          <input
            type="checkbox"
            checked={onlyShortages}
            onChange={(event) => setOnlyShortages(event.target.checked)}
          />{" "}
          {t('office.calendar.onlyShortages')}
        </label>
        <label className="project-column-toggle">
          <input
            type="checkbox"
            checked={showProjectColumn}
            onChange={(event) => setShowProjectColumn(event.target.checked)}
          />{" "}
          {t('office.calendar.showProjectColumn')}
        </label>
        <details className="calendar-legend">
          <summary>{t('office.calendar.legend')}</summary>
          <span>{t('office.calendar.legendText')}</span>
        </details>
      </div></FilterPanel>
      <Notice state={state} />
      {state.data && (
        <p className="calendar-result-count">
          {t('office.calendar.showing', { shown: visibleProjects.length, total: projects.length })}
        </p>
      )}
      {state.data && (
        <div
          className={`planner-frame ${showProjectColumn ? "" : "projects-hidden"}`}
        >
          <div
            className="calendar-scroll"
            style={{
              maxHeight: "calc(100vh - 250px)",
              overscrollBehavior: "contain",
            }}
          >
            <div
              className="calendar-grid"
              style={{ gridTemplateColumns: calendarColumns, minWidth: calendarMinimumWidth, width: "100%" }}
            >
              {showProjectColumn && (
                <div
                  className="calendar-corner"
                  aria-label={t('office.calendar.pinnedColumn')}
                >
                  {t('office.calendar.projectTransfer')}
                </div>
              )}
              {dates.map((day) => {
                const value = new Date(`${day}T12:00:00`);
                const weekend = value.getDay() === 0 || value.getDay() === 6;
                const transferCount = new Set(
                  (state.data.data || [])
                    .filter((item) => transferOnDay(item, day))
                    .map((item) => item.transfer_id),
                ).size;
                return (
                  <div
                    className={`calendar-day ${weekend ? "weekend" : ""} ${day === isoDate() ? "today" : ""}`}
                    key={day}
                    aria-label={day}
                  >
                    <span>
                      {new Intl.DateTimeFormat(localeTag(locale), {
                        weekday: "short",
                      }).format(value)}
                    </span>
                    <strong>
                      {new Intl.DateTimeFormat(localeTag(locale), {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }).format(value)}
                    </strong>
                    <small>
                      {transferCount || t('office.calendar.noTransfers')}{" "}
                      {transferCount === 1 ? t('office.calendar.transfer') : t('office.calendar.transfers')}
                    </small>
                  </div>
                );
              })}
              {visibleProjects.map((project) => (
                <CalendarProject
                  key={project.project_id}
                  project={project}
                  dates={dates}
                  showProjectColumn={showProjectColumn}
                  onOpenTransfer={setSelectedTransfer}
                />
              ))}
              {visibleProjects.length === 0 && (
                <div
                  className="planner-empty"
                  style={{
                    gridColumn: `1 / ${dates.length + (showProjectColumn ? 2 : 1)}`,
                  }}
                >
                  {t('office.calendar.noProjects')}
                </div>
              )}
            </div>
            <CalendarResources
              dates={dates}
              resources={resources}
              showProjectColumn={showProjectColumn}
              columns={calendarColumns}
              minimumWidth={calendarMinimumWidth}
            />
          </div>
        </div>
      )}
      {state.data && (
        <CalendarMobile projects={visibleProjects} dates={dates} locale={locale} onOpenTransfer={setSelectedTransfer} />
      )}
      {selectedTransfer && createPortal(
        <ProjectTransferDialog
          transfer={calendarTransferForDialog(selectedTransfer)}
          onClose={() => setSelectedTransfer(null)}
          onChanged={() => setRevision((value) => value + 1)}
          allowEdit
        />,
        document.body,
      )}
    </Page>
  );
}
function ProjectLabel({
  projectId,
  title,
  annotation,
  transfers,
  activeDays,
  continuesBefore,
  continuesAfter,
  start,
  end,
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  return (
    <div className={`project-label ${expanded ? "expanded" : ""}`}>
      <NavLink
        className="project-label-title"
        to={`/projects/${projectId}?start=${start}&end=${end}`}
        title={t("office.calendar.openProject")}
      >
        <strong>{title}</strong>
      </NavLink>
      <button
        type="button"
        className="project-label-expand"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
        title={t("office.calendar.toggleProject")}
      >
        <span>{annotation || t("office.calendar.noAnnotation")}</span>
        <small className="project-meta">
          {transfers} {transfers === 1 ? t("office.calendar.transfer") : t("office.calendar.transfers")} · {activeDays} {activeDays === 1 ? t("office.calendar.activeDay") : t("office.calendar.activeDays")}
        </small>
        {(continuesBefore || continuesAfter) && (
          <small className="project-continuation">
            {continuesBefore ? t("office.calendar.earlier") : ""}
            {continuesBefore && continuesAfter ? " · " : ""}
            {continuesAfter ? t("office.calendar.later") : ""}
          </small>
        )}
      </button>
    </div>
  );
}
function CalendarTransfer({
  transfer,
  day,
  projectTitle,
  projectAnnotation,
  showProjectColumn,
  onOpen,
}) {
  const { t } = useI18n();
  const haulEntries = transfer.hauls || [transfer];
  const hauls = haulEntries.map((item) => ({
    label: item.car_signature || item.car_group_signature || item.external_car_info || t("office.calendar.unassigned"),
    driver: item.driver_signature || item.external_driver_info || t("office.calendar.unassigned"),
    color: item.car_color || item.car_group_color,
  }));
  return (
    <button
      type="button"
      className="calendar-transfer"
      aria-label={t("office.calendar.showTransfer", { transfer: transfer.tweet || t("office.calendar.transfer") })}
      onClick={() => onOpen?.(transfer)}
    >
      <>
        {!showProjectColumn && (
          <small className="calendar-project-reference">{projectTitle}</small>
        )}
        <b>{day && transfer.departure_date < day ? t("office.calendar.ongoing") : formatTime(transfer.departure_time)}{day && transfer.arrival_date > day ? " →" : ""}</b>
        <span>{transfer.tweet || t("office.calendar.untitled")}</span>
        <div className="calendar-hauls">
          {hauls.map((haul, index) => (
            <div className="calendar-haul" key={`${haul.label}-${haul.driver}-${index}`} style={haul.color ? { backgroundColor: `#${String(haul.color).replace("#", "")}`, color: contrastText(haul.color) } : undefined}>
              {haul.driver !== t("office.calendar.unassigned") ? `${haul.driver} · ` : ""}{haul.label}
            </div>
          ))}
        </div>
      </>
    </button>
  );
}
function CalendarProject({ project, dates, showProjectColumn, onOpenTransfer }) {
  const { t } = useI18n();
  const title = calendarProjectTitle(project, t("office.calendarExtra.unassignedProject"), t("forms.project"));
  const transfers = [...project.transfers.values()];
  const activeDays = dates.filter(day => transfers.some(transfer => transferOnDay(transfer, day))).length;
  const continuesBefore = project.project_start < dates[0];
  const continuesAfter = project.project_end > dates[dates.length - 1];
  return (
    <>
      {showProjectColumn && (
        <ProjectLabel
          projectId={project.project_id}
          title={title}
          annotation={project.project_annotation}
          transfers={transfers.length}
          activeDays={activeDays}
          continuesBefore={continuesBefore}
          continuesAfter={continuesAfter}
          start={dates[0]}
          end={dates[dates.length - 1]}
        />
      )}
      {dates.map((day, index) => (
        <div className="calendar-cell" key={day}>
          {index === 0 && continuesBefore && (
            <span
              className="calendar-edge calendar-edge-left"
              title={t("office.calendar.continuesBefore")}
              aria-label={t("office.calendar.continuesBefore")}
            />
          )}
          {index === dates.length - 1 && continuesAfter && (
            <span
              className="calendar-edge calendar-edge-right"
              title={t("office.calendar.continuesAfter")}
              aria-label={t("office.calendar.continuesAfter")}
            />
          )}
          {transfers
            .filter((transfer) => transferOnDay(transfer, day))
            .map((transfer) => (
              <CalendarTransfer
                key={transfer.transfer_id}
                transfer={transfer}
                day={day}
                projectTitle={title}
                projectAnnotation={project.project_annotation}
                showProjectColumn={showProjectColumn}
                onOpen={onOpenTransfer}
              />
            ))}
        </div>
      ))}
    </>
  );
}

const vehicleColor = (value) =>
  /^#?[0-9a-f]{6}$/i.test(value || "")
    ? `#${String(value).replace("#", "").toUpperCase()}`
    : "#CBD5E1";

function vehicleSeatsLabel(value, locale, t) {
  if (locale !== "pl") return t("office.cars.seats");
  const seats = Number(value);
  if (seats === 1) return "miejsce";
  if (seats % 10 >= 2 && seats % 10 <= 4 && !(seats % 100 >= 12 && seats % 100 <= 14)) return "miejsca";
  return "miejsc";
}

const vehicleLicenceOptions = ["AM", "A1", "A2", "A", "B", "BE", "C", "C1", "C1E", "CE", "D", "D1", "D1E", "DE"];

function CarFormDialog({ car, groups, onClose, onSaved }) {
  const { t } = useI18n();
  const dialogRef = useDialogAccessibility(onClose);
  const isNew = !car;
  const [form, setForm] = useState(() => ({
    signature: car?.signature || "",
    model_name: car?.model_name || "",
    registration_number: car?.registration_number || "",
    num_passengers: car?.num_passengers ?? "",
    car_group_id: car?.car_group_id ?? "",
    required_license: car?.required_license || "B",
    color: vehicleColor(car?.color),
    deactivated: Boolean(car?.deactivated),
  }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const licenceOptions = [...new Set([form.required_license, ...vehicleLicenceOptions].filter(Boolean))];
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const selectGroup = (value) => {
    const group = groups.find((item) => String(item.id) === value);
    setForm((current) => ({ ...current, car_group_id: value, required_license: group?.default_license || current.required_license }));
  };
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = { ...form, car_group_id: form.car_group_id || null, num_passengers: form.num_passengers === "" ? null : Number(form.num_passengers) };
      if (isNew) await api.createCar(payload); else await api.updateCar(car.id, payload);
      onSaved();
    } catch (requestError) {
      setError(requestError.message);
    } finally { setSaving(false); }
  };
  return createPortal(
    <div className="project-form-backdrop" role="presentation" onMouseDown={onClose}>
      <section ref={dialogRef} className="project-form-dialog car-form-dialog" role="dialog" aria-modal="true" aria-labelledby="car-form-title" tabIndex="-1" onMouseDown={(event) => event.stopPropagation()}>
        <p>{isNew ? t("office.forms.fleetSetup") : `Vehicle #${car.id}`}</p>
        <h3 id="car-form-title">{isNew ? t("office.forms.addVehicle") : t("office.forms.vehicleDetails")}</h3>
        <form className="project-form" onSubmit={submit}>
          <div className="project-form-grid">
            <label>{t("office.forms.signature")}<input required maxLength="16" value={form.signature} onChange={(event) => update("signature", event.target.value)} placeholder={t("office.forms.vehicleSignatureExample")} /></label>
            <label>{t("office.forms.registration")}<input required maxLength="16" value={form.registration_number} onChange={(event) => update("registration_number", event.target.value)} placeholder={t("office.forms.registrationExample")} /></label>
            <label className="form-wide">{t("office.forms.model")}<input required maxLength="140" value={form.model_name} onChange={(event) => update("model_name", event.target.value)} placeholder={t("office.forms.model")} /></label>
            <label>{t("office.forms.vehicleGroup")}<select value={form.car_group_id} onChange={(event) => selectGroup(event.target.value)}><option value="">{t("office.forms.noGroup")}</option>{groups.map((group) => <option key={group.id} value={group.id}>{group.signature}</option>)}</select></label>
            <label>{t("office.forms.seats")}<input type="number" min="0" max="200" step="1" required value={form.num_passengers} onChange={(event) => update("num_passengers", event.target.value)} /></label>
            <label>{t("office.forms.requiredLicence")}<select value={form.required_license} onChange={(event) => update("required_license", event.target.value)}>{licenceOptions.map((licence) => <option key={licence} value={licence}>{licence}</option>)}</select></label>
            <label>{t("office.forms.vehicleColor")}<span className="car-form-colour"><input type="color" value={form.color} onChange={(event) => update("color", event.target.value)} /><code>{form.color.toUpperCase()}</code></span></label>
            {!isNew && <label className="car-active-toggle"><input type="checkbox" checked={!form.deactivated} onChange={(event) => update("deactivated", !event.target.checked)} /> {t("office.forms.activeVehicle")}</label>}
          </div>
          {error && <p className="form-error">{error}</p>}
          <div className="project-form-actions"><button type="button" onClick={onClose}>{t("office.forms.cancel")}</button><button className="project-create-button" type="submit" disabled={saving}>{saving ? t("office.forms.saving") : isNew ? t("office.forms.addVehicle") : t("office.forms.saveChanges")}</button></div>
        </form>
      </section>
    </div>, document.body,
  );
}

function Cars() {
  const { t, locale } = useI18n();
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("active");
  const [refresh, setRefresh] = useState(0);
  const [savingId, setSavingId] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [selectedCar, setSelectedCar] = useState(null);
  const [creating, setCreating] = useState(false);
  const state = useRequest(() => api.cars(query, status), [query, status, refresh]);
  const rows = state.data?.data || [];
  const saveColor = async (car, color) => {
    setSavingId(car.id);
    setSaveError("");
    try {
      await api.updateCarColor(car.id, color);
      setRefresh((value) => value + 1);
    } catch (error) {
      setSaveError(`${car.signature || t("office.cars.vehicle")}: ${error.message}`);
    } finally {
      setSavingId(null);
    }
  };
  const saved = () => {
    setSelectedCar(null); setCreating(false); setRefresh((value) => value + 1);
  };
  return (
    <Page title="Cars" description="Fleet directory with vehicle groups, capacity and licence requirements.">
      <div className="directory-toolbar cars-toolbar">
        <FilterPanel label={t("office.cars.filters")} activeCount={[query.trim(), status !== 'active'].filter(Boolean).length} onClear={() => { setQuery(''); setStatus('active'); }}><div className="directory-filter-fields"><input className="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("office.cars.search")} /><select className="cars-status-filter" value={status} onChange={(event) => setStatus(event.target.value)} aria-label={t("office.cars.filters")}><option value="active">{t("office.cars.active")}</option><option value="inactive">{t("office.cars.inactive")}</option><option value="all">{t("office.cars.all")}</option></select></div></FilterPanel>
        <div className="directory-actions">{state.data && <span className="directory-count">{t("office.cars.count", { count: state.data.count })}</span>}<button className="project-create-button" onClick={() => setCreating(true)}>{t("office.cars.add")}</button></div>
      </div>
      {saveError && <p className="status error">{saveError}</p>}
      <Notice state={state} />
      {state.data && (
        <>
        <p className="mobile-table-hint">{t("office.cars.swipe")}</p>
        <div className="table-wrap directory-table cars-table-wrap">
          <table>
            <thead><tr><th>{t("office.cars.vehicle")}</th><th>{t("office.cars.registration")}</th><th>{t("office.cars.capacity")}</th><th>{t("office.cars.licence")}</th><th>{t("office.cars.group")}</th><th>{t("office.cars.colour")}</th><th><span className="sr-only">{t("office.cars.actions")}</span></th></tr></thead>
            <tbody>
              {rows.map((car) => {
                const color = vehicleColor(car.color);
                return <tr key={car.id}>
                  <td><button className="car-identity car-open-button" onClick={() => setSelectedCar(car)}><strong>{car.signature || "—"}</strong><span>{car.model_name || t("office.cars.noModel")}</span></button></td>
                  <td className="registration-code">{car.registration_number || "—"}</td>
                  <td><span className="capacity-value">{car.num_passengers ?? "—"}</span><small> {vehicleSeatsLabel(car.num_passengers, locale, t)}</small></td>
                  <td><span className="licence-chip">{car.required_license || "—"}</span></td>
                  <td>{car.car_group ? <span className="car-group-chip"><i style={{ backgroundColor: vehicleColor(car.car_group_color) }} />{car.car_group}</span> : "—"}</td>
                  <td><label className="car-color-control" title={t("office.cars.setColour", { vehicle: car.signature || `#${car.id}` })}><input type="color" value={color} aria-label={t("office.cars.setColour", { vehicle: car.signature || `#${car.id}` })} disabled={savingId === car.id} onChange={(event) => saveColor(car, event.target.value)} /><span className="car-color-swatch" style={{ backgroundColor: color }} /><code>{color}</code>{savingId === car.id && <em>{t("office.cars.saving")}</em>}</label></td>
                  <td><button className="table-action" onClick={() => setSelectedCar(car)}>{t("office.cars.details")}</button></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
      {creating && <CarFormDialog groups={state.data?.groups || []} onClose={() => setCreating(false)} onSaved={saved} />}
      {selectedCar && <CarFormDialog car={selectedCar} groups={state.data?.groups || []} onClose={() => setSelectedCar(null)} onSaved={saved} />}
    </Page>
  );
}

function Directory() {
  const { t } = useI18n(); const auth = useAuth();
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("active");
  const [sort, setSort] = useState("name");
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const state = useRequest(() => api.accounts(query, role, status, sort, page), [query, role, status, sort, page, revision]);
  const rows = state.data?.data || [];
  const totalPages = Math.max(1, Math.ceil((state.data?.count || 0) / (state.data?.page_size || 50)));
  const headings = ["account", "person", "contact", "organisation", "access", "details"].map((key) => t(`office.accounts.${key}`));
  return (
    <Page
      title="Accounts"
      description="Operational account directory."
    >
      <div className="project-toolbar accounts-toolbar">
        <FilterPanel label={t("office.accounts.filters")} activeCount={[query.trim(), role, status !== 'active', sort !== 'name'].filter(Boolean).length} onClear={() => { setQuery(''); setRole(''); setStatus('active'); setSort('name'); setPage(1); }}><div className="account-filter-fields"><input
          className="search"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setPage(1); }}
          placeholder={t("office.accounts.search")}
        />
        <select value={role} onChange={(event) => { setRole(event.target.value); setPage(1); }} aria-label={t("office.accounts.allTypes")}>
          <option value="">{t("office.accounts.allTypes")}</option><option value="ADMIN">{t("roles.ADMIN")}</option><option value="OFFICE">{t("roles.OFFICE")}</option><option value="CLIENT">{t("roles.CLIENT")}</option><option value="DRIVER">{t("roles.DRIVER")}</option>
        </select>
        <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }} aria-label={t("office.accounts.allStates")}>
          <option value="active">{t("office.accounts.activeOnly")}</option><option value="inactive">{t("office.accounts.inactiveOnly")}</option><option value="all">{t("office.accounts.allStates")}</option>
        </select>
        <select value={sort} onChange={(event) => { setSort(event.target.value); setPage(1); }} aria-label={t("office.accounts.sortName")}>
          <option value="name">{t("office.accounts.sortName")}</option><option value="newest">{t("office.accounts.sortNewest")}</option><option value="role">{t("office.accounts.sortRole")}</option><option value="company">{t("office.accounts.sortCompany")}</option><option value="licence">{t("office.accounts.sortLicence")}</option>
        </select></div></FilterPanel>
        {auth.hasRole("ADMIN") && <button className="project-create-button" type="button" onClick={() => setCreating(true)}>{t("office.accounts.add")}</button>}
      </div>
      <Notice state={state} />
      {state.data && (
        <>
          <p className="project-page-count">{t("office.accounts.count", { count: state.data.count, page: state.data.page, pages: totalPages })}</p>
          <RegisterTable className="accounts-table-wrap" headings={headings} mobileHint={t("office.accounts.swipe")} emptyText={t("office.accounts.empty")}>
            {rows.map((row) => {
              const person = [row.name, row.surname].filter(Boolean).join(" ") || t("office.accounts.noName");
              return <tr key={row.id}>
                <td className="account-register-identity">
                  <button type="button" className="account-table-link" onClick={() => setSelected(row)}>{row.username || `${t("office.accounts.account")} #${row.id}`}</button>
                  <small>#{row.id}{row.signature ? ` · ${row.signature}` : ""}</small>
                </td>
                <td className="account-register-person"><b>{person}</b><small>{row.home_address || row.annotation || "—"}</small></td>
                <td className="account-register-contact">{row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : <span>—</span>}<small>{row.phone || t("office.accounts.noPhone")}</small></td>
                <td className="account-register-organisation"><b>{row.company_name || t("office.accounts.independent")}</b><small>{row.signature || t("office.accounts.noSignature")}</small></td>
                <td className="account-register-access"><span className={`role-chip role-${row.security_level?.toLowerCase()}`}>{t(`roles.${row.security_level}`)}</span><small>{row.deactivated ? t("office.accounts.inactive") : t("office.accounts.active")}{row.drivers_license ? ` · ${t("office.accounts.licence")} ${row.drivers_license}` : ""}</small></td>
                <td><button type="button" className="project-open-button" onClick={() => setSelected(row)}>{t("office.accounts.open")} →</button></td>
              </tr>;
            })}
          </RegisterTable>
          <RegisterPagination page={page} totalPages={totalPages} onPage={setPage} previous={t("office.accounts.previous")} next={t("office.accounts.next")} pageLabel={t("office.accounts.page", { page, pages: totalPages })} />
        </>
      )}
      {creating && createPortal(<AccountCreateDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); setRevision(value => value + 1); }} />, document.body)}
      {selected && createPortal(<AccountDetailsDialog account={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); setRevision(value => value + 1); }} />, document.body)}
    </Page>
  );
}

function AuditLog() {
  const { locale, t } = useI18n();
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const state = useRequest(() => api.auditLog(entityType, action, page), [entityType, action, page]);
  const rows = state.data?.data || [];
  const pages = Math.max(1, Math.ceil((state.data?.count || 0) / (state.data?.page_size || 50)));
  const entityTypes = ['project','transfer','assignment','haul','vehicle','account','availability','route','organization'];
  const actions = ['created','updated','archived','changed','invitation_sent'];
  const localized = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const eventTitle = (row) => `${localized(`office.audit.${row.action}`, row.action)} ${localized(`office.audit.${row.entity_type}`, row.entity_type)}${row.entity_id ? ` #${row.entity_id}` : ''}`;
  const changedFields = (row) => {
    try {
      const value = typeof row.metadata_json === 'string' ? JSON.parse(row.metadata_json) : row.metadata_json;
      return Array.isArray(value?.changed_fields) ? value.changed_fields : [];
    } catch (_) { return []; }
  };
  const fieldSummary = (row) => {
    const fields = changedFields(row);
    if (!fields.length) return localized(`office.audit.recorded`, 'Zdarzenie zapisane w historii operacyjnej');
    const label = locale === 'de' ? 'Geänderte Felder' : locale === 'en' ? 'Changed fields' : 'Zmienione pola';
    return `${label}: ${fields.join(', ')}`;
  };
  return <Page title="Audit log" description={t('office.descriptions.audit')}>
    <FilterPanel label={t('office.audit.filters')} activeCount={[entityType, action].filter(Boolean).length} onClear={() => { setEntityType(''); setAction(''); setPage(1); }}><div className="account-filter-fields">
      <select value={entityType} onChange={event => { setEntityType(event.target.value); setPage(1); }}><option value="">{t('office.audit.allEntities')}</option>{entityTypes.map(value => <option key={value} value={value}>{localized(`office.audit.${value}`, value)}</option>)}</select>
      <select value={action} onChange={event => { setAction(event.target.value); setPage(1); }}><option value="">{t('office.audit.allActions')}</option>{actions.map(value => <option key={value} value={value}>{localized(`office.audit.${value}`, value)}</option>)}</select>
    </div></FilterPanel>
    <Notice state={state} />
    {state.data && <><p className="project-page-count">{t('office.audit.count', { count: state.data.count })}</p><section className="audit-log-list" aria-label={t('office.audit.title')}>
      {rows.map(row => <article className="audit-log-item" key={row.id}>
        <span className={`audit-action action-${row.action}`}>{localized(`office.audit.${row.action}`, row.action)}</span>
        <div className="audit-event"><b>{eventTitle(row)}</b><small>{fieldSummary(row)}</small></div>
        <div className="audit-actor"><b>{row.actor_signature || row.actor_username || localized('office.audit.system', 'System')}</b><small>{row.actor_role ? t(`roles.${row.actor_role}`) : '—'}</small></div>
        <time dateTime={row.occurred_at}><b>{formatOfficeDate(row.occurred_at.slice(0, 10), locale, { day:'2-digit', month:'short', year:'numeric' })}</b><small>{String(row.occurred_at).slice(11,16)}</small></time>
      </article>)}
      {rows.length === 0 && <p className="empty-state">{t('common.noResults')}</p>}
    </section><RegisterPagination page={page} totalPages={pages} onPage={setPage} /></>}
  </Page>;
}

function Organizations() {
  const { locale, t } = useI18n();
  const [query, setQuery] = useState(""); const [page, setPage] = useState(1); const [revision, setRevision] = useState(0);
  const [selected, setSelected] = useState(null); const [creating, setCreating] = useState(false);
  const state = useRequest(() => api.organizations(query, page), [query, page, revision]);
  const rows = state.data?.data || []; const pages = Math.max(1, Math.ceil((state.data?.count || 0) / (state.data?.page_size || 50)));
  return <Page title={t("office.organizations.title")} description={t("office.organizations.description")}>
    <div className="project-toolbar accounts-toolbar"><FilterPanel label={t("office.organizations.filters")} activeCount={query.trim() ? 1 : 0} onClear={() => { setQuery(""); setPage(1); }}><div className="account-filter-fields"><input className="search" value={query} onChange={event => { setQuery(event.target.value); setPage(1); }} placeholder={t("office.organizations.search")} /></div></FilterPanel><button className="project-create-button" type="button" onClick={() => setCreating(true)}>{t("office.organizations.add")}</button></div>
    <Notice state={state} />
    {state.data && <><p className="project-page-count">{t("office.organizations.count", { count: state.data.count, page, pages })}</p><RegisterTable className="accounts-table-wrap organization-table" headings={[t("office.organizations.organization"), t("office.organizations.contacts"), t("office.organizations.projects"), t("office.organizations.lastActivity"), t("office.organizations.details")]} mobileHint={t("office.organizations.swipe")} emptyText={t("office.organizations.empty")}>
      {rows.map(row => <tr key={row.id}><td className="account-register-identity"><button className="account-table-link" type="button" onClick={() => setSelected(row.id)}>{row.name}</button><small>#{row.id}{row.signature ? ` · ${row.signature}` : ""}</small></td><td><b>{row.contacts_count || 0}</b><small>{t("office.organizations.contactsHint")}</small></td><td><b>{row.projects_count || 0}</b><small>{t("office.organizations.projectsHint")}</small></td><td>{row.last_project_at ? <><b>{formatOfficeDate(String(row.last_project_at).slice(0, 10), locale)}</b><small>{String(row.last_project_at).slice(11,16)}</small></> : "—"}</td><td><button className="project-open-button" type="button" onClick={() => setSelected(row.id)}>{t("office.organizations.open")} →</button></td></tr>)}
    </RegisterTable><RegisterPagination page={page} totalPages={pages} onPage={setPage} previous={t("office.accounts.previous")} next={t("office.accounts.next")} pageLabel={t("office.accounts.page", { page, pages })} /></>}
    {selected && createPortal(<OrganizationDetailsDialog organizationId={selected} onClose={() => setSelected(null)} onChanged={() => { setSelected(null); setRevision(value => value + 1); }} />, document.body)}
    {creating && createPortal(<OrganizationFormDialog onClose={() => setCreating(false)} onSaved={() => { setCreating(false); setRevision(value => value + 1); }} />, document.body)}
  </Page>;
}

function OrganizationDetailsDialog({ organizationId, onClose, onChanged }) {
  const { t } = useI18n(); const [editing, setEditing] = useState(false); const [addingClient, setAddingClient] = useState(false); const [assigningClient, setAssigningClient] = useState(false);
  const state = useRequest(() => api.organization(organizationId), [organizationId]); const data = state.data;
  if (state.loading) return <DialogShell title={t("office.organizations.title")} onClose={onClose}><p className="status">{t("common.loading")}</p></DialogShell>;
  if (state.error || !data) return <DialogShell title={t("office.organizations.title")} onClose={onClose}><p className="form-error">{state.error?.message || t("common.apiUnavailable")}</p></DialogShell>;
  if (editing) return <OrganizationFormDialog organization={data.organization} onClose={() => setEditing(false)} onSaved={onChanged} />;
  if (addingClient) return <AccountCreateDialog companyId={organizationId} initialRole="CLIENT" onClose={() => setAddingClient(false)} onSaved={() => { setAddingClient(false); onChanged(); }} />;
  if (assigningClient) return <OrganizationClientAssignDialog organizationId={organizationId} onClose={() => setAssigningClient(false)} onSaved={() => { setAssigningClient(false); onChanged(); }} />;
  const organization = data.organization;
  return <DialogShell title={organization.name} subtitle={`${t("office.organizations.organization")} #${organization.id}${organization.signature ? ` · ${organization.signature}` : ""}`} onClose={onClose}>
    <div className="organization-detail-grid"><section><span>{t("office.organizations.contacts")}</span><strong>{data.contacts.length}</strong><small>{t("office.organizations.contactRecords")}</small></section><section><span>{t("office.organizations.projects")}</span><strong>{data.projects.length}</strong><small>{t("office.organizations.projectRecords")}</small></section><section><span>{t("office.organizations.transfers")}</span><strong>{data.transfers_count || 0}</strong><small>{t("office.organizations.latestTransfers")}</small></section></div>
    <section className="organization-detail-section"><header><h4>{t("office.organizations.contacts")}</h4></header>{data.contacts.length ? <div className="organization-contact-list">{data.contacts.map(contact => <div key={contact.id}><b>{[contact.name, contact.surname].filter(Boolean).join(" ") || contact.username}</b><span className={`role-chip role-${contact.security_level?.toLowerCase()}`}>{t(`roles.${contact.security_level}`)}</span><small>{contact.email || contact.phone || contact.signature || "—"}</small></div>)}</div> : <p>{t("office.organizations.noContacts")}</p>}</section>
    <section className="organization-detail-section"><header><h4>{t("office.organizations.projects")}</h4></header>{data.projects.length ? <div className="organization-project-list">{data.projects.map(project => <Link key={project.id} to={`/projects/${project.id}`} onClick={onClose}><b>{project.annotation || `${t("forms.project")} #${project.id}`}</b><small>#{project.id} · {project.transfers_count || 0} {t("office.organizations.transfers")} · {t(`office.projects.statuses.${project.status}`) || project.status}</small></Link>)}</div> : <p>{t("office.organizations.noProjects")}</p>}</section>
    <section className="organization-detail-section"><header><h4>{t("office.organizations.transfers")}</h4><small>{t("office.organizations.latestTransfers")}</small></header>{data.transfers?.length ? <div className="organization-transfer-list">{data.transfers.map(transfer => <Link key={transfer.id} to={`/projects/${transfer.project_id}`} onClick={onClose}><time>{String(transfer.departure_date).slice(0,10)} · {String(transfer.departure_time || "").slice(0,5)}</time><b>{transfer.tweet || `${t("forms.transfer")} #${transfer.id}`}</b><small>{transfer.project_annotation || `${t("forms.project")} #${transfer.project_id}`}</small></Link>)}</div> : <p>{t("office.organizations.noTransfers")}</p>}</section>
    <div className="project-form-actions"><button type="button" onClick={onClose}>{t("office.accounts.close")}</button><button type="button" onClick={() => setAssigningClient(true)}>{t("office.organizations.assignClient")}</button><button type="button" onClick={() => setAddingClient(true)}>{t("office.organizations.addClient")}</button><button type="button" className="project-create-button" onClick={() => setEditing(true)}>{t("office.organizations.edit")}</button></div>
  </DialogShell>;
}

function OrganizationFormDialog({ organization, onClose, onSaved }) {
  const { t } = useI18n(); const [form, setForm] = useState({ name: organization?.name || "", signature: organization?.signature || "", tax_id:organization?.tax_id || "", billing_name:organization?.billing_name || "", billing_address:organization?.billing_address || "", billing_email:organization?.billing_email || "", billing_phone:organization?.billing_phone || "", payment_terms_days:organization?.payment_terms_days ?? "", relationship_status:organization?.relationship_status || "ACTIVE", internal_notes:organization?.internal_notes || "" }); const [saving, setSaving] = useState(false); const [error, setError] = useState("");
  const save = async event => { event.preventDefault(); setSaving(true); setError(""); try { if (organization) await api.updateOrganization(organization.id, form); else await api.createOrganization(form); onSaved(); } catch (reason) { setError(reason.message); setSaving(false); } };
  const update = event => setForm(value => ({ ...value, [event.target.name]:event.target.value }));
  return <DialogShell title={organization ? t("office.organizations.edit") : t("office.organizations.add")} subtitle={t("office.organizations.formHint")} onClose={onClose}><form className="project-form" onSubmit={save}><div className="project-form-grid"><label>{t("office.organizations.name")}<input required name="name" maxLength="128" value={form.name} onChange={update} /></label><label>{t("office.organizations.signature")}<input name="signature" maxLength="16" value={form.signature} onChange={update} /></label><label>{t("office.organizations.taxId")}<input name="tax_id" maxLength="32" value={form.tax_id} onChange={update} /></label><label>{t("office.organizations.status")}<select name="relationship_status" value={form.relationship_status} onChange={update}><option value="ACTIVE">{t("office.organizations.active")}</option><option value="PROSPECT">{t("office.organizations.prospect")}</option><option value="INACTIVE">{t("office.organizations.inactive")}</option></select></label><label>{t("office.organizations.billingName")}<input name="billing_name" maxLength="160" value={form.billing_name} onChange={update} /></label><label>{t("office.organizations.paymentTerms")}<input name="payment_terms_days" type="number" min="0" max="365" value={form.payment_terms_days} onChange={update} /></label><label>{t("office.forms.email")}<input name="billing_email" type="email" value={form.billing_email} onChange={update} /></label><label>{t("office.forms.phone")}<input name="billing_phone" value={form.billing_phone} onChange={update} /></label><label className="project-form-full">{t("office.organizations.billingAddress")}<textarea name="billing_address" value={form.billing_address} onChange={update} /></label><label className="project-form-full">{t("office.organizations.internalNotes")}<textarea name="internal_notes" value={form.internal_notes} onChange={update} /></label></div>{error && <p className="form-error">{error}</p>}<div className="project-form-actions"><button type="button" onClick={onClose}>{t("office.forms.cancel")}</button><button className="project-create-button" disabled={saving}>{saving ? t("office.forms.saving") : t("office.forms.saveChanges")}</button></div></form></DialogShell>;
}

function OrganizationClientAssignDialog({ organizationId, onClose, onSaved }) {
  const { t } = useI18n(); const [clientId, setClientId] = useState(""); const [saving, setSaving] = useState(false); const [error, setError] = useState("");
  const submit = async event => { event.preventDefault(); if (!clientId) return; setSaving(true); setError(""); try { await api.assignOrganizationClient(organizationId, clientId); onSaved(); } catch (reason) { setError(reason.message); setSaving(false); } };
  return <DialogShell title={t("office.organizations.assignClient")} subtitle={t("office.organizations.assignClientHint")} onClose={onClose}><form className="project-form" onSubmit={submit}><ProjectClientPicker value={clientId} onChange={setClientId} inputId="organization-client-picker" />{error && <p className="form-error">{error}</p>}<div className="project-form-actions"><button type="button" onClick={onClose}>{t("office.forms.cancel")}</button><button className="project-create-button" disabled={saving || !clientId}>{t("office.organizations.assignClient")}</button></div></form></DialogShell>;
}

function AccountDetailsDialog({ account, onClose, onSaved }) {
  const { t } = useI18n(); const auth = useAuth();
  const [role, setRole] = useState(account.security_level || "CLIENT");
  const [saving, setSaving] = useState(false); const [sendingInvite, setSendingInvite] = useState(false); const [inviteSent, setInviteSent] = useState(false); const [error, setError] = useState("");
  const canManageRole = auth.hasRole("ADMIN") && auth.account?.id !== account.id;
  const canSendInvite = auth.enabled && auth.hasRole("ADMIN") && Boolean(account.email) && !account.deactivated;
  const saveRole = async () => { if (role === account.security_level) return; setSaving(true); setError(""); try { await api.updateAccountRole(account.id, role); onSaved?.(); } catch (reason) { setError(reason.message); setSaving(false); } };
  const sendInvite = async () => { setSendingInvite(true); setInviteSent(false); setError(""); try { await api.sendAccountSetupEmail(account.id); setInviteSent(true); } catch (reason) { setError(reason.message); } finally { setSendingInvite(false); } };
  const name = [account.name, account.surname].filter(Boolean).join(" ") || t("office.accounts.noName");
  return <DialogShell title={name} subtitle={`${t("office.accounts.account")} #${account.id} · ${account.username || t("office.accounts.noUsername")}`} onClose={onClose}>
    <div className="account-detail-summary">
      <section><span>{t("office.accounts.access")}</span><strong className={`role-chip role-${account.security_level?.toLowerCase()}`}>{t(`roles.${account.security_level}`)}</strong><small>{account.deactivated ? t("office.accounts.inactiveAccount") : t("office.accounts.activeAccount")}</small></section>
      <section><span>{t("office.accounts.contact")}</span><strong>{account.email || "—"}</strong><small>{account.phone || t("office.accounts.noPhone")}</small></section>
      <section><span>{t("office.accounts.organisation")}</span><strong>{account.company_name || t("office.accounts.independent")}</strong><small>{account.signature || t("office.accounts.noSignature")}</small></section>
      <section><span>{t("office.accounts.driverProfile")}</span><strong>{account.drivers_license ? `${t("office.accounts.licence")} ${account.drivers_license}` : t("office.accounts.notDriver")}</strong><small>{account.home_address || t("office.accounts.noHomeAddress")}</small></section>
    </div>
    {auth.hasRole("ADMIN") && <section className="role-management-card">
      <div><span>{t("office.roleManagement.title")}</span><p>{canManageRole ? t("office.roleManagement.description") : t("office.roleManagement.selfLocked")}</p></div>
      <label>{t("office.roleManagement.chooseRole")}<select value={role} disabled={!canManageRole || saving} onChange={event => setRole(event.target.value)}><option value="ADMIN">{t("roles.ADMIN")}</option><option value="OFFICE">{t("roles.OFFICE")}</option><option value="DRIVER">{t("roles.DRIVER")}</option><option value="CLIENT">{t("roles.CLIENT")}</option></select></label>
      <small>{t(`office.roleManagement.${role.toLowerCase()}`)}</small>
      {error && <p className="form-error">{error}</p>}
    </section>}
    {canSendInvite && <section className="account-invitation-card"><div><span>{t("office.forms.secureAccess")}</span><strong>{t("office.forms.invitationTitle")}</strong><p>{t("office.forms.invitationHint", { email: account.email })}</p></div><button type="button" className="project-create-button" disabled={sendingInvite} onClick={sendInvite}>{sendingInvite ? t("office.forms.sendingInvitation") : t("office.forms.sendInvitation")}</button>{inviteSent && <small className="form-success">{t("office.forms.invitationSent")}</small>}{error && <p className="form-error">{error}</p>}</section>}
    {account.annotation && <div className="account-detail-note"><span>{t("office.accounts.internalNote")}</span><p>{account.annotation}</p></div>}
    <div className="project-form-actions"><button type="button" disabled={saving} onClick={onClose}>{t("office.accounts.close")}</button>{canManageRole && <button type="button" className="project-create-button" disabled={saving || role === account.security_level} onClick={saveRole}>{saving ? t("office.roleManagement.saving") : t("office.roleManagement.apply")}</button>}</div>
  </DialogShell>;
}

function AccountCreateDialog({ onClose, onSaved, companyId = "", initialRole = "DRIVER" }) {
  const auth = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({ username:"", password:"", security_level:initialRole, name:"", surname:"", signature:"", phone:"", email:"", drivers_license:"D", company_id:companyId ? String(companyId) : "", send_setup_email:true });
  const [error,setError]=useState(""); const [saving,setSaving]=useState(false);
  const update=(event)=>setForm(value=>({...value,[event.target.name]:event.target.value}));
  const submit=async(event)=>{event.preventDefault();setSaving(true);setError("");try{await api.createAccount(form);onSaved();}catch(reason){setError(reason.message);setSaving(false);}};
  return <DialogShell title={t("office.forms.account")} subtitle={auth.enabled ? t("office.forms.secureIdentity") : t("office.forms.localIdentity")} onClose={onClose}>
    <form className="project-form account-create-form" onSubmit={submit}>
      <div className="account-form-sections">
        <fieldset>
          <legend>{t("office.accounts.accessSection")}</legend>
          <div className="project-form-grid">
            <label>{t("office.forms.accountType")}<select name="security_level" value={form.security_level} onChange={update}><option value="DRIVER">{t("roles.DRIVER")}</option><option value="OFFICE">{t("roles.OFFICE")}</option><option value="CLIENT">{t("roles.CLIENT")}</option><option value="ADMIN">{t("roles.ADMIN")}</option></select></label>
            <label>{t("office.forms.signature")}<input required name="signature" value={form.signature} onChange={update} placeholder={t("office.forms.signatureExample")} /></label>
            <label>{t("office.forms.username")}<input required name="username" value={form.username} onChange={update} autoComplete="off" /></label>
            {!auth.enabled && <label>{t("office.forms.password")}<input required type="password" name="password" value={form.password} onChange={update} autoComplete="new-password" /></label>}
            {form.security_level === "DRIVER" && <label>{t("office.forms.drivingLicence")}<input name="drivers_license" value={form.drivers_license} onChange={update} /></label>}
          </div>
        </fieldset>
        <fieldset>
          <legend>{t("office.accounts.contactDetails")}</legend>
          <div className="project-form-grid">
            <label>{t("office.forms.firstName")}<input required name="name" value={form.name} onChange={update} /></label>
            <label>{t("office.forms.surname")}<input required name="surname" value={form.surname} onChange={update} /></label>
            <label>{t("office.forms.phone")}<input name="phone" value={form.phone} onChange={update} inputMode="tel" /></label>
            <label>{t("office.forms.email")}<input type="email" name="email" value={form.email} onChange={update} /></label>
            <label>{t("office.accounts.organisation")}<input type="number" min="1" name="company_id" value={form.company_id} onChange={update} placeholder={t("forms.optionalOrganisationId")} readOnly={Boolean(companyId)} /></label>
          </div>
        </fieldset>
      </div>
      {auth.enabled && <label className="checkbox-field account-setup-email"><input type="checkbox" checked={form.send_setup_email} onChange={(event)=>setForm(value=>({...value,send_setup_email:event.target.checked}))} /><span><b>{t("office.forms.setupEmail")}</b><small>{form.send_setup_email ? t("office.forms.setupEmailHint") : t("office.forms.noSetupEmailHint")}</small></span></label>}
      {error&&<p className="form-error">{error}</p>}
      <div className="project-form-actions"><button type="button" onClick={onClose}>{t("office.forms.cancel")}</button><button className="project-create-button" disabled={saving}>{saving?t("office.forms.creating"):auth.enabled?t("office.forms.createSecureAccount"):t("office.forms.createAccount")}</button></div>
    </form>
  </DialogShell>;
}
function localizedCandidateReason(reason, t) {
  if (!reason) return "";
  if (reason.startsWith("Busy ")) return t("office.availabilityExtra.busy", { time: reason.slice(5) });
  return ({
    "No declaration": "noDeclaration",
    Standby: "standby",
    "Declared unavailable": "unavailable",
    "On vacation": "vacation",
    "Invalid transfer dates": "invalidDates",
  }[reason] && t(`office.availabilityExtra.${({ "No declaration": "noDeclaration", Standby: "standby", "Declared unavailable": "unavailable", "On vacation": "vacation", "Invalid transfer dates": "invalidDates" })[reason]}`)) || reason;
}

function Dispatch() {
  const { locale, t } = useI18n();
  const [range, setRange] = useState(initialRange);
  const [revision, setRevision] = useState(0);
  const [view, setView] = useState("NEEDS_ASSIGNMENT");
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const [contactRequest, setContactRequest] = useState(null);
  const [assigningKey, setAssigningKey] = useState("");
  const [assignmentError, setAssignmentError] = useState("");
  const [query, setQuery] = useState("");
  const { start, end } = range;
  const state = useRequest(() => api.availability(start, end), [start, end, revision]);
  const entries = useMemo(() => new Map((state.data?.data || []).map((entry) => [`${entry.account_id}-${entry.date.slice(0, 10)}`, entry])), [state.data]);
  const workers = useMemo(() => state.data?.workers || [], [state.data]);
  const driversById = useMemo(() => new Map(workers.map((worker) => [worker.account_id, worker])), [workers]);
  const candidatesFor = (transfer) => {
    const candidates = workers.map(worker => driverCandidate(transfer, worker, entries, state.data?.assignments || [])).filter(Boolean);
    const group = (name) => candidates.filter(item => item.group === name)
      .sort((a, b) => (a.worker.signature || a.worker.surname || "").localeCompare(b.worker.signature || b.worker.surname || "", localeTag(locale))).slice(0, 5);
    return { ready: group("ready"), confirm: group("confirm") };
  };
  const unassigned = (state.data?.unassigned_transfers || []).map((transfer) => ({ transfer, ...candidatesFor(transfer) }));
  const conflicts = useMemo(() => (state.data?.assignments || []).filter((item) => {
    let cursor = new Date(`${item.departure_date}T12:00:00`); const last = new Date(`${item.arrival_date || item.departure_date}T12:00:00`);
    while (cursor <= last) { const status = entries.get(`${item.account_id}-${isoDate(cursor)}`)?.availability_state; if (["UNAVAILABLE", "VACATION"].includes(status)) return true; cursor.setDate(cursor.getDate() + 1); }
    return false;
  }), [state.data, entries]);
  const assignDriver = async (transfer, worker) => {
    setAssigningKey(`${transfer.transfer_id}-${worker.account_id}`); setAssignmentError("");
    try { await api.assignSuggestedDriver(transfer.transfer_id, worker.account_id); setRevision((value) => value + 1); return true; }
    catch (reason) { setAssignmentError(reason.message || t("office.dispatchUi.assignError")); return false; }
    finally { setAssigningKey(""); }
  };
  const selectHorizon = (days) => {
    const today = isoDate(new Date());
    setRange({ start: today, end: addDays(today, days - 1) });
  };
  const normalizedQuery = query.trim().toLocaleLowerCase(localeTag(locale));
  const matchesQuery = (transfer) => {
    if (!normalizedQuery) return true;
    return [
      transfer.transfer_id,
      transfer.tweet,
      transfer.project_id,
      transfer.project_annotation,
      transfer.departure_meta,
      transfer.arrival_meta,
      transfer.departure_address,
      transfer.arrival_address,
      driversById.get(transfer.account_id)?.signature,
      driversById.get(transfer.account_id)?.name,
      driversById.get(transfer.account_id)?.surname,
    ].filter(Boolean).join(" ").toLocaleLowerCase(localeTag(locale)).includes(normalizedQuery);
  };
  const card = ({ transfer, ready = [], confirm = [] }) => <article className="dispatch-card" key={transfer.transfer_id}>
    <header><time>{transfer.departure_date?.slice(5)} · {shortTime(transfer.departure_time)}</time><span>{transfer.required_licences ? `${t("office.dispatchUi.licence")} ${transfer.required_licences}` : t("office.dispatchUi.noVehicleRequirement")}</span></header>
    <strong>{transfer.tweet || t("office.dispatchUi.untitled")}</strong>
    {transfer.project_id ? <Link to={`/projects/${transfer.project_id}?start=${start}&end=${end}`}>{projectReference(transfer.project_id, transfer.project_annotation, t, t("office.dispatchUi.openProject"))}</Link> : <span>{t("office.dispatchUi.noProject")}</span>}
    <div className="dispatch-candidates">{ready.length > 0 && <><b>{t("office.dispatchUi.ready")}</b>{ready.map(({ worker, status }) => <button type="button" key={worker.account_id} onClick={() => setPendingAssignment({ transfer, worker, status })}>{worker.signature || worker.name} <em>{worker.drivers_license || "—"}</em></button>)}</>}{confirm.length > 0 && <><b className="dispatch-confirm-label">{t("office.dispatchUi.confirm")}</b>{confirm.map(({ worker, status, reason }) => <span className="dispatch-candidate" key={worker.account_id}><button type="button" className="dispatch-confirm" onClick={() => setPendingAssignment({ transfer, worker, status, reason })}>{worker.signature || worker.name} <em>{localizedCandidateReason(reason, t)}</em></button>{status === "UNKNOWN" && <button type="button" className="dispatch-contact" onClick={() => setContactRequest({ transfer, worker })} aria-label={t("office.dispatchUi.requestAria", { driver: worker.signature || worker.name })}>{t("office.dispatchUi.request")}</button>}</span>)}</>}{ready.length === 0 && confirm.length === 0 && <i>{t("office.dispatchUi.noDriver")}</i>}</div>
  </article>;
  const assignedCard = (item, conflicted = false) => <article className={`dispatch-card dispatch-assigned ${conflicted ? "dispatch-conflict" : ""}`} key={`${item.transfer_id}-${item.account_id}`}><header><time>{item.departure_date?.slice(5)} · {shortTime(item.departure_time)}</time><span>{conflicted ? t("office.dispatchUi.availabilityConflict") : t("office.dispatchUi.assigned")}</span></header><strong>{item.tweet || t("office.dispatchUi.untitled")}</strong><small>{driversById.get(item.account_id)?.signature || t("office.dispatchUi.driver")} · {item.project_id ? <Link to={`/projects/${item.project_id}?start=${start}&end=${end}`}>{projectReference(item.project_id, item.project_annotation, t, t("office.dispatchUi.openProject"))}</Link> : t("office.dispatchUi.noProject")}</small></article>;
  const filteredUnassigned = unassigned.filter(({ transfer }) => matchesQuery(transfer));
  const filteredConflicts = conflicts.filter(matchesQuery);
  const filteredAssignments = (state.data?.assignments || []).filter(matchesQuery);
  const missingLocations = (state.data?.exceptions?.missing_locations || []).filter(matchesQuery);
  const recentChanges = (state.data?.exceptions?.recent_changes || []).filter(matchesQuery);
  const exceptionCard = (item, label, detail) => <article className="dispatch-card dispatch-conflict" key={`${label}-${item.transfer_id}`}><header><time>{item.departure_date?.slice(5)} · {shortTime(item.departure_time)}</time><span>{label}</span></header><strong>{item.tweet || t("office.dispatchUi.untitled")}</strong><small>{detail}{item.project_id ? <> · <Link to={`/projects/${item.project_id}?start=${start}&end=${end}`}>{projectReference(item.project_id, item.project_annotation, t, t("office.dispatchUi.openProject"))}</Link></> : ` · ${t("office.dispatchUi.noProject")}`}</small></article>;
  const activeItems = view === "CONFLICTS" ? filteredConflicts.map((item) => assignedCard(item, true)) : view === "ASSIGNED" ? filteredAssignments.map((item) => assignedCard(item)) : view === "CONFIRMATION" ? filteredUnassigned.filter((item) => !item.ready.length && item.confirm.length).map(card) : view === "MISSING_LOCATION" ? missingLocations.map((item) => exceptionCard(item, t("office.dispatchUi.missingLocation"), `${t("office.dispatchUi.missing")}: ${item.missing_location}`)) : view === "RECENT" ? recentChanges.map((item) => exceptionCard(item, t("office.dispatchUi.recentChanges"), `${t("office.dispatchUi.changedAt")} ${item.updated_at?.replace("T", " ") || "—"}`)) : filteredUnassigned.map(card);
  return <Page title="Dispatch" description="A focused queue for assigning drivers to transfers.">
    <FilterPanel label={t("office.dispatchUi.filters")} activeCount={[query.trim(), start !== isoDate(), end !== addDays(isoDate(), 13)].filter(Boolean).length} onClear={() => { const today = isoDate(); setRange({ start: today, end: addDays(today, 13) }); setQuery(''); }}><div className="dispatch-toolbar"><div className="range-controls"><label>{t("office.filters.from")} <input type="date" value={start} onChange={(event) => setRange({ start: event.target.value, end: end < event.target.value ? event.target.value : end })} /></label><label>{t("office.filters.to")} <input type="date" value={end} min={start} max={addDays(start, 62)} onChange={(event) => setRange({ start, end: event.target.value })} /></label></div><div className="dispatch-toolbar-actions" aria-label={t("office.dispatchUi.filters")}><button type="button" onClick={() => selectHorizon(1)}>{t("office.dispatchUi.today")}</button><button type="button" onClick={() => selectHorizon(3)}>{t("office.dispatchUi.next3Days")}</button><button type="button" onClick={() => selectHorizon(7)}>{t("office.dispatchUi.week")}</button></div><label className="dispatch-search"><span>{t("office.dispatchUi.search")}</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("office.dispatchUi.searchPlaceholder")} /></label></div></FilterPanel>
    <Notice state={state} />
    {state.data && <><div className="dispatch-tabs"><button className={view === "NEEDS_ASSIGNMENT" ? "active" : ""} onClick={() => setView("NEEDS_ASSIGNMENT")}>{t("office.dispatchUi.needsAssignment")} <span>{unassigned.length}</span></button><button className={view === "CONFIRMATION" ? "active" : ""} onClick={() => setView("CONFIRMATION")}>{t("office.dispatchUi.confirm")} <span>{unassigned.filter((item) => !item.ready.length && item.confirm.length).length}</span></button><button className={view === "CONFLICTS" ? "active" : ""} onClick={() => setView("CONFLICTS")}>{t("office.dispatchUi.conflicts")} <span>{conflicts.length}</span></button><button className={view === "MISSING_LOCATION" ? "active" : ""} onClick={() => setView("MISSING_LOCATION")}>{t("office.dispatchUi.missingLocation")} <span>{missingLocations.length}</span></button><button className={view === "RECENT" ? "active" : ""} onClick={() => setView("RECENT")}>{t("office.dispatchUi.recentChanges")} <span>{recentChanges.length}</span></button><button className={view === "ASSIGNED" ? "active" : ""} onClick={() => setView("ASSIGNED")}>{t("office.dispatchUi.assigned")} <span>{(state.data.assignments || []).length}</span></button></div><p className="dispatch-hint">{t("office.dispatchUi.hint")}</p><p className="dispatch-licence-guide">{t("office.dispatchUi.licenceCoverage", { licence: "D", required: "B" })}</p><div className="dispatch-list">{activeItems.length ? activeItems : <p className="dispatch-empty">{t("office.dispatchUi.empty")}</p>}</div></>}
    {pendingAssignment && createPortal(<AssignmentConfirmDialog assignment={pendingAssignment} saving={assigningKey === `${pendingAssignment.transfer.transfer_id}-${pendingAssignment.worker.account_id}`} error={assignmentError} onClose={() => { if (!assigningKey) { setPendingAssignment(null); setAssignmentError(""); } }} onConfirm={async () => { const saved = await assignDriver(pendingAssignment.transfer, pendingAssignment.worker); if (saved) setPendingAssignment(null); }} />, document.body)}
    {contactRequest && createPortal(<AvailabilityRequestDialog request={contactRequest} onClose={() => setContactRequest(null)} />, document.body)}
  </Page>;
}

function Availability() {
  const { locale, t } = useI18n();
  const [range, setRange] = useState(initialRange);
  const [revision, setRevision] = useState(0);
  const [editingCell, setEditingCell] = useState(null);
  // The availability board is most useful as a declaration review by default.
  // Office can still clear this switch to inspect every driver.
  const [declaredOnly, setDeclaredOnly] = useState(true);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [assignmentFilter, setAssignmentFilter] = useState("ALL");
  const [licenceFilter, setLicenceFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("CONFLICTS");
  const [assigningKey, setAssigningKey] = useState("");
  const [assignmentError, setAssignmentError] = useState("");
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const { start, end } = range;
  const state = useRequest(() => api.availability(start, end), [start, end, revision]);
  const dates = useMemo(() => boundedDates(start, end), [start, end]);
  const selectWeek = (offset) => {
    const today = new Date();
    const mondayOffset = (today.getDay() + 6) % 7;
    const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - mondayOffset + offset * 7);
    setRange({ start: isoDate(monday), end: addDays(isoDate(monday), 6) });
  };
  const entries = useMemo(
    () =>
      new Map(
        (state.data?.data || []).map((entry) => [
          `${entry.account_id}-${entry.date.slice(0, 10)}`,
          entry,
        ]),
      ),
    [state.data],
  );
  const workloads = useMemo(() => {
    const result = new Map();
    (state.data?.assignments || []).forEach((item) => {
      let cursor = new Date(`${item.departure_date}T12:00:00`);
      const last = new Date(
        `${item.arrival_date || item.departure_date}T12:00:00`,
      );
      while (cursor <= last) {
        const key = `${item.account_id}-${isoDate(cursor)}`;
        result.set(key, [...(result.get(key) || []), item]);
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return result;
  }, [state.data]);
  const workers = useMemo(() => state.data?.workers || [], [state.data]);
  const unassignedTransfers = state.data?.unassigned_transfers || [];
  const licences = [
    ...new Set(workers.map((worker) => worker.drivers_license || "—")),
  ].sort();
  const profile = (worker) => {
    const statuses = dates.map(
      (day) =>
        entries.get(`${worker.account_id}-${day}`)?.availability_state ||
        "UNKNOWN",
    );
    const assignmentCount = dates.reduce(
      (count, day) =>
        count + (workloads.get(`${worker.account_id}-${day}`)?.length || 0),
      0,
    );
    const conflictCount = dates.reduce((count, day) => {
      const state =
        entries.get(`${worker.account_id}-${day}`)?.availability_state ||
        "UNKNOWN";
      return (
        count +
        (["UNAVAILABLE", "VACATION"].includes(state) &&
        (workloads.get(`${worker.account_id}-${day}`)?.length || 0)
          ? 1
          : 0)
      );
    }, 0);
    return {
      statuses,
      assignmentCount,
      conflictCount,
      declared: statuses.some((value) => value !== "UNKNOWN"),
    };
  };
  const visibleWorkers = workers
    .map((worker) => ({ worker, profile: profile(worker) }))
    .filter(({ worker, profile }) => {
      const label =
        `${worker.signature || ""} ${worker.name || ""} ${worker.surname || ""}`.toLowerCase();
      return (
        (!declaredOnly || profile.declared) &&
        (statusFilter === "ALL" || profile.statuses.includes(statusFilter)) &&
        (assignmentFilter === "ALL" ||
          (assignmentFilter === "ASSIGNED" && profile.assignmentCount > 0) ||
          (assignmentFilter === "CONFLICTS" && profile.conflictCount > 0)) &&
        (licenceFilter === "ALL" ||
          (worker.drivers_license || "—") === licenceFilter) &&
        (!query || label.includes(query.toLowerCase()))
      );
    })
    .sort((left, right) =>
      sort === "NAME"
        ? (left.worker.signature || left.worker.surname || "").localeCompare(
            right.worker.signature || right.worker.surname || "",
            localeTag(locale),
          )
        : sort === "WORKLOAD"
          ? right.profile.assignmentCount - left.profile.assignmentCount
          : right.profile.conflictCount - left.profile.conflictCount ||
            right.profile.assignmentCount - left.profile.assignmentCount,
    );
  const daySummary = (day) =>
    visibleWorkers.reduce(
      (summary, { worker }) => {
        const status =
          entries.get(`${worker.account_id}-${day}`)?.availability_state ||
          "UNKNOWN";
        summary[status] = (summary[status] || 0) + 1;
        const assigned =
          workloads.get(`${worker.account_id}-${day}`)?.length || 0;
        if (assigned && ["UNAVAILABLE", "VACATION"].includes(status))
          summary.conflicts += 1;
        return summary;
      },
      { AVAILABLE: 0, STANDBY: 0, conflicts: 0 },
    );
  const candidatesFor = (transfer) => {
    const groups = { ready: [], confirm: [], blocked: [], licenceMismatch: 0 };
    workers.forEach(worker => {
      const candidate = driverCandidate(transfer, worker, entries, state.data?.assignments || []);
      if (candidate) groups[candidate.group].push(candidate); else groups.licenceMismatch += 1;
    });
    ["ready", "confirm", "blocked"].forEach(key => {
      groups[key] = groups[key].sort((a, b) => (a.worker.signature || a.worker.surname || "").localeCompare(b.worker.signature || b.worker.surname || "", localeTag(locale))).slice(0, key === "blocked" ? 8 : 6);
    });
    return groups;
  };
  const assignDriver = async (transfer, worker) => {
    const key = `${transfer.transfer_id}-${worker.account_id}`;
    setAssigningKey(key);
    setAssignmentError("");
    try {
      await api.assignSuggestedDriver(transfer.transfer_id, worker.account_id);
      setRevision((value) => value + 1);
      return true;
    } catch (reason) {
      setAssignmentError(reason.message || "Could not assign the driver.");
      return false;
    } finally {
      setAssigningKey("");
    }
  };
  return (
    <Page
      title="Availability"
      description={t("office.descriptions.availability")}
    >
      <FilterPanel label={t("office.availability.filters")} activeCount={[start !== isoDate(), end !== addDays(start, 13), !declaredOnly, statusFilter !== 'ALL', assignmentFilter !== 'ALL', licenceFilter !== 'ALL', query.trim(), sort !== 'CONFLICTS'].filter(Boolean).length} onClear={() => { const today = isoDate(); setRange({ start: today, end: addDays(today, 13) }); setDeclaredOnly(true); setStatusFilter('ALL'); setAssignmentFilter('ALL'); setLicenceFilter('ALL'); setQuery(''); setSort('CONFLICTS'); }}><div className="availability-toolbar">
        <div className="range-controls">
          <label>
            {t("office.availability.from")}{" "}
            <input
              type="date"
              value={start}
              onChange={(event) =>
                setRange({
                  start: event.target.value,
                  end: end < event.target.value ? event.target.value : end,
                })
              }
            />
          </label>
          <label>
            {t("office.availability.to")}{" "}
            <input
              type="date"
              value={end}
              min={start}
              max={addDays(start, 62)}
              onChange={(event) => setRange({ start, end: event.target.value })}
            />
          </label>
          <label className="availability-declared-toggle">
            <input
              type="checkbox"
              checked={declaredOnly}
              onChange={(event) => setDeclaredOnly(event.target.checked)}
            />{" "}
            {t("office.availability.hasDeclaration")}
          </label>
          <div className="availability-quick-actions"><button type="button" onClick={() => selectWeek(-1)}>{t("office.availability.lastWeek")}</button><button type="button" onClick={() => selectWeek(0)}>{t("office.availability.thisWeek")}</button><button type="button" onClick={() => selectWeek(1)}>{t("office.availability.nextWeek")}</button></div>
        </div>
        <div className="availability-filters">
          <input
            className="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("office.availability.search")}
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">{t("office.availability.anyStatus")}</option>
            <option value="AVAILABLE">{t("office.availability.available")}</option>
            <option value="STANDBY">{t("office.availability.standby")}</option>
            <option value="UNAVAILABLE">{t("office.availability.unavailable")}</option>
            <option value="VACATION">{t("office.availability.vacation")}</option>
            <option value="UNKNOWN">{t("office.availability.unknown")}</option>
          </select>
          <select
            value={assignmentFilter}
            onChange={(event) => setAssignmentFilter(event.target.value)}
          >
            <option value="ALL">{t("office.availability.allWorkload")}</option>
            <option value="ASSIGNED">{t("office.availability.assignedOnly")}</option>
            <option value="CONFLICTS">{t("office.availability.conflictsOnly")}</option>
          </select>
          <select
            value={licenceFilter}
            onChange={(event) => setLicenceFilter(event.target.value)}
          >
            <option value="ALL">{t("office.availability.allLicences")}</option>
            {licences.map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
          <select
            value={sort}
            onChange={(event) => setSort(event.target.value)}
          >
            <option value="CONFLICTS">{t("office.availability.conflictsFirst")}</option>
            <option value="WORKLOAD">{t("office.availability.workloadFirst")}</option>
            <option value="NAME">{t("office.availability.name")}</option>
          </select>
        </div>
      </div></FilterPanel>
      <Notice state={state} />
      {state.data && (
        <>
          <p className="availability-count">
            {t("office.availability.driversShown", { shown: visibleWorkers.length, total: workers.length, conflicts: visibleWorkers.reduce(
              (count, item) => count + item.profile.conflictCount,
              0,
            ) })}
          </p>
          {unassignedTransfers.length > 0 && (
            <details className="assignment-suggestions">
              <summary><span>{unassignedTransfers.length}</span><b>{t("office.availability.needsDriver")}</b><small>{t("office.guides.dispatch")}</small></summary>
              <div className="assignment-suggestions-list">
                {assignmentError && <p className="assignment-error" role="alert">{assignmentError}</p>}
                {unassignedTransfers.slice(0, 24).map((transfer) => {
                  const candidates = candidatesFor(transfer);
                  return <article key={transfer.transfer_id} className="assignment-suggestion">
                    <div><time>{transfer.departure_date?.slice(5)} · {shortTime(transfer.departure_time)}</time><strong>{transfer.tweet || t("office.availabilityExtra.untitled")}</strong><small>{transfer.project_id ? <Link to={`/projects/${transfer.project_id}?start=${start}&end=${end}`}>{projectReference(transfer.project_id, transfer.project_annotation, t, t("office.availabilityExtra.openProject"))}</Link> : t("office.availabilityExtra.noProject")}{transfer.required_licences ? ` · ${t("office.availabilityExtra.licence")} ${transfer.required_licences}` : ''}</small></div>
                    <div className="assignment-candidates">
                      {candidates.ready.length > 0 && <div className="candidate-group candidate-ready"><b>{t("office.availabilityExtra.ready")}</b>{candidates.ready.map(({ worker, status }) => <button type="button" key={worker.account_id} disabled={Boolean(assigningKey)} onClick={() => setPendingAssignment({ transfer, worker, status })}>{worker.signature || `${worker.name || ''} ${worker.surname || ''}`.trim()} <em>{worker.drivers_license || '—'}</em></button>)}</div>}
                      {candidates.confirm.length > 0 && <div className="candidate-group candidate-confirm"><b>{t("office.availabilityExtra.confirm")}</b>{candidates.confirm.map(({ worker, status, reason }) => <button type="button" key={worker.account_id} disabled={Boolean(assigningKey)} onClick={() => setPendingAssignment({ transfer, worker, status, reason })}>{worker.signature || `${worker.name || ''} ${worker.surname || ''}`.trim()} <em>{localizedCandidateReason(reason, t)}</em></button>)}</div>}
                      {(candidates.blocked.length > 0 || candidates.licenceMismatch > 0) && <details className="candidate-alternatives"><summary>{t("office.availabilityExtra.constraints")}{candidates.licenceMismatch ? ` · ${candidates.licenceMismatch} ${t("office.availabilityExtra.licenceMismatch")}` : ''}</summary>{candidates.blocked.map(({ worker, reason }) => <span key={worker.account_id}>{worker.signature || `${worker.name || ''} ${worker.surname || ''}`.trim()} <em>{localizedCandidateReason(reason, t)}</em></span>)}</details>}
                      {candidates.ready.length === 0 && candidates.confirm.length === 0 && <i>{t("office.availabilityExtra.noMatch")}</i>}
                    </div>
                  </article>;
                })}
                {unassignedTransfers.length > 24 && <p className="assignment-suggestions-more">{t("office.availabilityExtra.first24")}</p>}
              </div>
            </details>
          )}
          <div className="availability-scroll">
            <div
              className="availability-grid"
              style={{
                gridTemplateColumns: `var(--availability-driver-column, minmax(220px, 1.4fr)) repeat(${dates.length}, var(--availability-day-column, minmax(64px, 1fr)))`,
              }}
            >
              <div className="availability-corner">{t("office.availability.driver")}</div>
              {dates.map((day) => (
                <DayAvailabilityHeader
                  key={day}
                  day={day}
                  summary={daySummary(day)}
                  locale={locale}
                />
              ))}
              {visibleWorkers.map(({ worker }) => (
                <AvailabilityRow
                  key={worker.account_id}
                  worker={worker}
                  dates={dates}
                  entries={entries}
                  workloads={workloads}
                  onEdit={(worker, day) => setEditingCell({ worker, day, entry: entries.get(`${worker.account_id}-${day}`) })}
                />
              ))}
              {visibleWorkers.length === 0 && (
                <div
                  className="planner-empty"
                  style={{ gridColumn: `1 / ${dates.length + 2}` }}
                >
                  {t("office.availability.noMatch")}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      {editingCell && createPortal(<AvailabilityDialog cell={editingCell} onClose={() => setEditingCell(null)} onSaved={() => { setEditingCell(null); setRevision(value => value + 1); }} />, document.body)}
      {pendingAssignment && createPortal(<AssignmentConfirmDialog
        assignment={pendingAssignment}
        saving={assigningKey === `${pendingAssignment.transfer.transfer_id}-${pendingAssignment.worker.account_id}`}
        error={assignmentError}
        onClose={() => { if (!assigningKey) { setPendingAssignment(null); setAssignmentError(""); } }}
        onConfirm={async () => { const saved = await assignDriver(pendingAssignment.transfer, pendingAssignment.worker); if (saved) setPendingAssignment(null); }}
      />, document.body)}
    </Page>
  );
}

function AssignmentConfirmDialog({ assignment, saving, error, onClose, onConfirm }) {
  const { t } = useI18n();
  const { transfer, worker, status, reason } = assignment;
  const driverName = worker.signature || `${worker.name || ""} ${worker.surname || ""}`.trim();
  const availability = status === "AVAILABLE" ? t("office.forms.available") : status === "STANDBY" ? t("office.forms.standby") : t("office.forms.noDeclaration");
  return <DialogShell title={t("office.forms.assignmentTitle")} subtitle={t("office.forms.assignmentSubtitle")} onClose={onClose}>
    <div className="assignment-confirm">
      <p>{t("office.forms.reviewAssignment")}</p>
      <dl>
        <div><dt>{t("office.forms.transfer")}</dt><dd>{transfer.tweet || t("office.dashboard.untitled")}</dd></div>
      <div><dt>{t("office.forms.when")}</dt><dd>{transfer.departure_date?.slice(0, 10)} · {transferTimes(transfer)}</dd></div>
        <div><dt>{t("office.forms.driver")}</dt><dd>{driverName} · {t("office.forms.licence")} {worker.drivers_license || "—"}</dd></div>
        <div><dt>{t("office.forms.availability")}</dt><dd className={status === "AVAILABLE" ? "assignment-confirm-ok" : "assignment-confirm-caution"}>{availability}{reason ? ` (${localizedCandidateReason(reason, t)})` : ""}</dd></div>
      </dl>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="project-form-actions"><button type="button" onClick={onClose} disabled={saving}>{t("office.forms.cancel")}</button><button type="button" className="project-create-button" onClick={onConfirm} disabled={saving}>{saving ? t("office.forms.assigning") : t("office.forms.assignDriver")}</button></div>
    </div>
  </DialogShell>;
}

function AvailabilityRequestDialog({ request, onClose }) {
  const { t } = useI18n();
  const [channel, setChannel] = useState("WHATSAPP");
  const [sent, setSent] = useState(false);
  const { worker, transfer } = request;
  const name = worker.name || worker.signature || t("office.dispatchUi.driver");
  const subject = `Availability request · ${shortTime(transfer.departure_time)} ${transfer.tweet || "transfer"}`;
  const message = `Hi ${name}, are you available for ${transfer.departure_date?.slice(0, 10)} ${transferTimes(transfer)}? ${transfer.tweet || ""}`;
  return <DialogShell title={t("office.availabilityExtra.requestTitle")} subtitle={t("office.availabilityExtra.requestSubtitle")} onClose={onClose}>
    <div className="availability-request">
      <p>{t("office.availabilityExtra.requestBody")}</p>
      <div className="availability-request-channels" role="group" aria-label={t("office.availabilityExtra.channel")}><button type="button" className={channel === "WHATSAPP" ? "active" : ""} onClick={() => { setChannel("WHATSAPP"); setSent(false); }}>WhatsApp</button><button type="button" className={channel === "EMAIL" ? "active" : ""} onClick={() => { setChannel("EMAIL"); setSent(false); }}>Email</button></div>
      <dl><div><dt>{t("office.availabilityExtra.to")}</dt><dd>{worker.signature || name}{(channel === "EMAIL" ? worker.email : worker.phone) ? ` · ${channel === "EMAIL" ? worker.email : worker.phone}` : ` · ${t("office.availabilityExtra.contactMissing")}`}</dd></div>{channel === "EMAIL" && <div><dt>{t("office.availabilityExtra.subject")}</dt><dd>{subject}</dd></div>}<div><dt>{t("office.availabilityExtra.message")}</dt><dd className="availability-request-copy">{message}</dd></div></dl>
      {sent && <p className="availability-request-sent" role="status">{t("office.availabilityExtra.simulated")}</p>}
      <div className="project-form-actions"><button type="button" onClick={onClose}>{t("office.availabilityExtra.close")}</button><button type="button" className="project-create-button" onClick={() => setSent(true)}>{t("office.availabilityExtra.simulate", { channel: channel === "EMAIL" ? "e-mail" : "WhatsApp" })}</button></div>
    </div>
  </DialogShell>;
}

function DayAvailabilityHeader({ day, summary, locale }) {
  const { t } = useI18n();
  return (
    <div className="availability-day availability-day-summary">
      <b>
        {formatOfficeDate(day, locale, { day: "2-digit", month: "short" })}
      </b>
      <span>
        {summary.AVAILABLE} A · {summary.STANDBY} S
      </span>
      {summary.conflicts > 0 && (
        <em>
          {summary.conflicts} {t("office.availabilityExtra.conflicts")}
        </em>
      )}
    </div>
  );
}

const availabilityColor = Object.fromEntries(Object.entries(availabilityStates).map(([value, state]) => [value, state.background]));
function AvailabilityRow({ worker, dates, entries, workloads, onEdit }) {
  const label =
    worker.signature ||
    [worker.name, worker.surname].filter(Boolean).join(" ") ||
    "Unnamed driver";
  return (
    <>
      <div className="worker-label">
        <strong>{label}</strong>
        <span>{worker.drivers_license || "—"}</span>
      </div>
      {dates.map((day) => {
        const state =
          entries.get(`${worker.account_id}-${day}`)?.availability_state ||
          "UNKNOWN";
        const assigned = workloads.get(`${worker.account_id}-${day}`) || [];
        const conflict =
          assigned.length > 0 && ["UNAVAILABLE", "VACATION"].includes(state);
        const transferNames = assigned
          .map(
            (item) =>
              `${shortTime(item.departure_time)} ${item.tweet || "Transfer"}`,
          )
          .join("\n");
        return (
          <button type="button"
            className={`availability-cell state-${state.toLowerCase()} ${assigned.length ? "has-workload" : ""} ${conflict ? "has-conflict" : ""}`}
            style={{ backgroundColor: availabilityColor[state] || "#f3f5f8" }}
            title={`${label}: ${state}${conflict ? " · CONFLICT" : ""}${transferNames ? `\n${transferNames}` : ""}`}
            key={day}
            onClick={() => onEdit(worker, day)}
            aria-label={`Set availability for ${label} on ${day}`}
          >
            <span>{state === "UNKNOWN" ? "" : state.slice(0, 1)}</span>
            {assigned.length > 0 && (
              <b className="availability-workload">{assigned.length}</b>
            )}
          </button>
        );
      })}
    </>
  );
}

function AvailabilityDialog({ cell, onClose, onSaved }) {
  const { t } = useI18n();
  const [state, setState] = useState(cell.entry?.availability_state || "UNKNOWN");
  const [comment, setComment] = useState(cell.entry?.comment || "");
  const [error, setError] = useState(""); const [saving, setSaving] = useState(false);
  const save = async (event) => { event.preventDefault(); setSaving(true); setError(""); try { await api.setAvailability({ account_id: cell.worker.account_id, date: cell.day, availability_state: state, comment }); onSaved(); } catch (reason) { setError(reason.message); setSaving(false); } };
  return <DialogShell title={t("office.availability.setAvailability")} subtitle={`${cell.worker.signature || cell.worker.name} · ${cell.day}`} onClose={onClose}><form className="project-form" onSubmit={save}><label>{t("office.availability.status")}<select value={state} onChange={event => setState(event.target.value)}><option value="UNKNOWN">{t("office.availability.unknown")}</option><option value="AVAILABLE">{t("office.availability.available")}</option><option value="UNAVAILABLE">{t("office.availability.unavailable")}</option><option value="STANDBY">{t("office.availability.standby")}</option><option value="VACATION">{t("office.availability.vacation")}</option></select></label><label>{t("office.availability.comment")}<textarea rows="3" value={comment} onChange={event => setComment(event.target.value)} placeholder={t("office.availability.optionalNote")} /></label>{error && <p className="form-error">{error}</p>}<div className="project-form-actions"><button type="button" onClick={onClose}>{t("common.cancel")}</button><button className="project-create-button" disabled={saving}>{saving ? t("office.forms.saving") : t("office.availability.saveAvailability")}</button></div></form></DialogShell>;
}
const assistantActionFields = {
  create_account: [["username", "username"], ["name", "firstName"], ["surname", "lastName"], ["signature", "signature"], ["email", "email", "email"], ["drivers_license", "licence", "text", "B"], ["security_level", "role", "select", "DRIVER", ["DRIVER", "CLIENT", "OFFICE", "ADMIN"]]],
  create_car: [["signature", "vehicleCode"], ["model_name", "model"], ["registration_number", "registration"], ["num_passengers", "seats", "number"], ["required_license", "licence", "text", "B"], ["color", "colour", "color", "#1f6feb"]],
  create_project: [["annotation", "projectTitle"], ["external_client", "externalClient"], ["status", "status", "text", "PENDING"]],
  create_transfer: [["project_id", "projectId", "number"], ["tweet", "transferTitle"], ["departure_date", "departureDate", "date"], ["departure_time", "departureTime", "time"], ["departure_meta", "from"], ["arrival_meta", "to"]],
  assign_driver: [["transfer_id", "transferId", "number"], ["driver_id", "driverId", "number"]],
  set_availability: [["account_id", "accountId", "number"], ["date", "date", "date"], ["availability_state", "status", "select", "AVAILABLE", ["AVAILABLE", "UNAVAILABLE", "STANDBY", "VACATION"]], ["comment", "note"]],
};
const assistantActionLabels = { create_account: "newAccount", create_car: "newVehicle", create_project: "newProject", create_transfer: "newTransfer", assign_driver: "assignDriver", set_availability: "setAvailability" };

function AssistantMessage({ message }) {
  if (message.role === "user" || message.error || !message.format || message.format === "guided-action") return <p className={message.role + (message.error ? " error" : "")}>{message.text}</p>;
  const lines = message.text.split("\n").filter(Boolean);
  if (message.format === "transfer-list" || message.format === "dispatch-list") {
    const records = message.text.split("\n\n"); const heading = records.shift();
    return <article className="assistant-result"><b>{heading}</b><div className="assistant-result-table">{records.map((record, index) => { const [title, route, assignment] = record.split("\n"); return <div key={index}><strong>{title}</strong><span>{route || "—"}</span><small>{assignment || "—"}</small></div>; })}</div></article>;
  }
  const heading = lines.shift();
  return <article className="assistant-result"><b>{heading}</b><div className="assistant-result-table">{lines.map((line, index) => { const [main, ...detail] = line.split(" · "); return <div key={index}><strong>{main}</strong><span>{detail.join(" · ")}</span></div>; })}</div></article>;
}

function OperationsAssistant() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false), [question, setQuestion] = useState(""), [messages, setMessages] = useState([]), [busy, setBusy] = useState(false);
  const [action, setAction] = useState(""), [draft, setDraft] = useState({}), [proposal, setProposal] = useState(null), [actionBusy, setActionBusy] = useState(false), [actionError, setActionError] = useState("");
  const ask = async (event) => { event.preventDefault(); const value = question.trim(); if (!value || busy) return; setQuestion(""); setMessages(items => [...items, { role: "user", text: value }]); setBusy(true); try { const reply = await api.assistantChat(value); setMessages(items => [...items, { role: "assistant", text: reply.answer, format: reply.format }]); if (reply.suggested_action) chooseAction(reply.suggested_action); } catch (error) { setMessages(items => [...items, { role: "assistant", text: error.message, error: true }]); } finally { setBusy(false); } };
  const chooseAction = (value) => { setAction(value); setDraft({}); setProposal(null); setActionError(""); };
  const previewAction = async (event) => { event.preventDefault(); if (!action) return; setActionBusy(true); setActionError(""); try { const data = Object.fromEntries(fields.flatMap(([key, , , initial]) => { const value = draft[key]; return value !== undefined && value !== "" ? [[key, value]] : initial !== undefined ? [[key, initial]] : []; })); setProposal(await api.assistantActionPreview(action, data)); } catch (error) { setActionError(error.message); } finally { setActionBusy(false); } };
  const confirmAction = async () => { if (!proposal || actionBusy) return; setActionBusy(true); setActionError(""); try { const result = await api.assistantActionConfirm(proposal.confirmation_token); setMessages(items => [...items, { role: "assistant", text: t("office.assistant.confirmed", { summary: result.summary }) }]); setProposal(null); setDraft({}); setAction(""); } catch (error) { setActionError(error.message); } finally { setActionBusy(false); } };
  const fields = assistantActionFields[action] || [];
  const required = new Set(["username", "name", "surname", "email", "signature", "model_name", "registration_number", "project_id", "tweet", "departure_date", "departure_time", "departure_meta", "arrival_meta", "transfer_id", "driver_id", "account_id", "date"]);
  const compact = !messages.length && !action;
  return <aside className={"operations-assistant" + (open ? " open" : "")} aria-label={t("office.assistant.title")}><button className="assistant-toggle" type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-controls="operations-assistant-panel"><svg className="assistant-toggle-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 1.5c.9 6.4 4.1 9.6 10.5 10.5-6.4.9-9.6 4.1-10.5 10.5C11.1 16.1 7.9 12.9 1.5 12 7.9 11.1 11.1 7.9 12 1.5Z" /></svg><span>{t("office.assistant.open")}</span></button>{open && <section id="operations-assistant-panel" className={"assistant-panel" + (compact ? " compact" : "")} role="dialog" aria-modal="false" aria-labelledby="assistant-title"><header><div><b id="assistant-title">{t("office.assistant.title")}</b><span>{t("office.assistant.scope")}</span></div><button className="assistant-close" type="button" onClick={() => setOpen(false)} aria-label={t("office.assistant.close")}>×</button></header><div className={"assistant-messages" + (!messages.length ? " empty" : "")} aria-live="polite" aria-busy={busy}>{messages.length ? messages.map((message, index) => <AssistantMessage message={message} key={index} />) : <p>{t("office.assistant.empty")}</p>}</div><form onSubmit={ask}><label className="sr-only" htmlFor="assistant-question">{t("office.assistant.question")}</label><input id="assistant-question" value={question} maxLength="600" onChange={event => setQuestion(event.target.value)} placeholder={t("office.assistant.placeholder")} /><button disabled={busy || !question.trim()}>{busy ? t("office.assistant.thinking") : t("office.assistant.ask")}</button></form>{action && <section className="assistant-action-card"><header><b>{t(`office.assistant.${assistantActionLabels[action]}`)}</b><button type="button" onClick={() => chooseAction("")} aria-label={t("office.assistant.discard")}>×</button></header><p>{t("office.assistant.reviewHelp")}</p><form className="assistant-action-form" onSubmit={previewAction}>{fields.map(([key, label, type = "text", initial, options]) => <label key={key}>{t(`office.assistant.${label}`)}{type === "select" ? <select value={draft[key] || initial || ""} onChange={event => setDraft(value => ({ ...value, [key]: event.target.value }))}>{options.map(option => <option key={option} value={option}>{option}</option>)}</select> : <input type={type} value={draft[key] || ""} placeholder={initial || ""} required={required.has(key)} onChange={event => setDraft(value => ({ ...value, [key]: event.target.value }))} />}</label>)}<button disabled={actionBusy}>{actionBusy ? t("office.assistant.preparing") : t("office.assistant.reviewAction")}</button></form>{proposal && <div className="assistant-confirm"><b>{t("office.assistant.reviewRequired")}</b><span>{proposal.summary}</span><div><button type="button" onClick={() => setProposal(null)}>{t("office.assistant.cancel")}</button><button type="button" className="project-create-button" onClick={confirmAction} disabled={actionBusy}>{actionBusy ? t("office.assistant.saving") : t("office.assistant.confirmSave")}</button></div></div>}{actionError && <p className="form-error">{actionError}</p>}</section>}</section>}</aside>;
}
function AdminApp() {
  const health = useRequest(api.health, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const { t } = useI18n(); const auth = useAuth();
  return (
    <div className="app-shell">
      <aside className={menuOpen ? "menu-open" : ""}>
        <div className="mobile-nav-bar">
          <div className="brand"><Brand /></div>
          <LanguageSwitcher compact />
          <button
            type="button"
            className="mobile-menu-toggle"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <i />
            <i />
            <i />
          </button>
        </div>
        <SidebarProfile />
        <nav>
          {navigation.map(({ label, items }) => <section className="nav-group" key={label} aria-label={t(label)}>
            <b>{t(label)}</b>
            {items.filter(([, path]) => auth.hasRole("ADMIN") || !["/accounts", "/audit-log"].includes(path)).map(([labelKey, path]) => <NavLink key={path} to={path} onClick={() => setMenuOpen(false)}>{t(labelKey)}</NavLink>)}
          </section>)}
        </nav>
        {auth.enabled && <button type="button" className="shell-logout mobile-only-logout" onClick={auth.logout}>{t('common.signOut')}</button>}
        {health.error && <p className="connection offline">{t('common.apiUnavailable')}</p>}
      </aside>
      <main>
        <OperationsAssistant />
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/statistics" element={<Statistics />} />
          <Route path="/dispatch" element={<Dispatch />} />
          <Route path="/booking-requests" element={<BookingRequestReview />} />
          <Route path="/availability" element={<Availability />} />
          <Route path="/day-by-day" element={<Navigate to="/dashboard" replace />} />
          <Route path="/transfers" element={<Transfers />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:projectId" element={<ProjectDetail />} />
          <Route path="/cars" element={<Cars />} />
          <Route path="/organizations" element={<Organizations />} />
          <Route path="/accounts" element={<Directory />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function ClientTransferDetails({ transfer, onClose }) {
  const { locale, t } = useI18n();
  const date = new Intl.DateTimeFormat(localeTag(locale), { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(`${transfer.departure_date}T12:00:00`));
  return <DialogShell title={transfer.tweet || `${transfer.departure_meta || t('client.departure')} → ${transfer.arrival_meta || t('client.arrival')}`} subtitle={`${date} · ${shortTime(transfer.departure_time)} – ${shortTime(transfer.arrival_time)}`} onClose={onClose}>
    <div className="client-transfer-details">
      <dl>
        <div><dt>{t('client.departure')}</dt><dd><b>{transfer.departure_meta || '—'}</b><small>{transfer.departure_address || '—'}</small></dd></div>
        <div><dt>{t('client.arrival')}</dt><dd><b>{transfer.arrival_meta || '—'}</b><small>{transfer.arrival_address || '—'}</small></dd></div>
        <div><dt>{t('forms.passengers')}</dt><dd>{transfer.num_passengers ?? '—'}</dd></div>
        <div><dt>{t('forms.status')}</dt><dd>{transfer.status || '—'}</dd></div>
      </dl>
      <TransferMap transfer={transfer} />
    </div>
  </DialogShell>;
}

function ClientTransferRow({ transfer, onOpen }) {
  const { t, locale } = useI18n();
  const route = transfer.tweet || `${transfer.departure_meta || transfer.departure_address || t("client.departure")} → ${transfer.arrival_meta || transfer.arrival_address || t("client.arrival")}`;
  return <button type="button" className="transfer-row client-transfer-row" onClick={() => onOpen(transfer)}>
    <time><b>{new Intl.DateTimeFormat(localeTag(locale), { day: 'numeric', month: 'short' }).format(new Date(`${transfer.departure_date}T12:00:00`))}</b><span>{shortTime(transfer.departure_time)} – {shortTime(transfer.arrival_time)}</span></time>
    <span><b>{route}</b><small>{transfer.departure_meta || transfer.departure_address || '—'} → {transfer.arrival_meta || transfer.arrival_address || '—'}</small></span>
    <i aria-hidden="true">›</i>
  </button>;
}

function ClientProjectList({ projects, onOpen }) {
  const { t } = useI18n();
  return projects.length ? <div className="project-detail-list">{projects.map(project => <section className="project-detail-card" key={project.project_id}>
    <h2>{project.annotation || `${t("forms.project")} #${project.project_id}`}</h2>
    <p className="muted">{project.transfers.length} {project.transfers.length === 1 ? t("client.transfer") : t("client.transfers")}</p>
    <div className="transfer-list">{project.transfers.map(transfer => <ClientTransferRow key={transfer.transfer_id} transfer={transfer} onOpen={onOpen} />)}</div>
  </section>)}</div> : <p className="empty-state">{t("client.empty")}</p>;
}

function ClientBookingRequestList({ state, onCreate }) {
  const { locale, t } = useI18n();
  const requests = state.data?.data || state.data?.requests || [];
  const statusLabel = (status) => {
    const normalized = String(status || "SUBMITTED").toLowerCase();
    return ["submitted", "in_review", "needs_information", "approved", "declined", "cancelled"].includes(normalized)
      ? t(`client.requestStatus.${normalized}`)
      : String(status || "SUBMITTED");
  };
  if (state.loading) return <p className="status">{t("common.loading")}</p>;
  if (state.error) return <p className="status error">{state.error}</p>;
  return <><div className="client-request-heading"><p>{t("client.requestDraft")}</p><button type="button" className="project-create-button" onClick={onCreate}>{t("client.newRequest")}</button></div>{requests.length ? <div className="client-request-list">{requests.map(request => <article key={request.request_id} className="client-request-row"><header><span className={`client-request-status status-${String(request.status || "SUBMITTED").toLowerCase()}`}>{statusLabel(request.status)}</span><time>{request.created_at ? new Intl.DateTimeFormat(localeTag(locale), { day: "numeric", month: "short", year: "numeric" }).format(new Date(request.created_at)) : "—"}</time></header><strong>{request.title || request.tweet || "—"}</strong><p>{request.departure_meta || request.departure_address || "—"} → {request.arrival_meta || request.arrival_address || "—"}</p><small>{request.departure_date || "—"} · {shortTime(request.departure_time)} · {request.num_passengers ?? "—"} {t("client.passengers")}</small></article>)}</div> : <p className="empty-state">{t("client.requestEmpty")}</p>}</>;
}

function ClientBookingRequestDialog({ onClose, onSaved }) {
  const { t } = useI18n();
  const today = isoDate();
  const emptyLeg = (start = {}) => ({
    departure_date: start.arrival_date || today,
    departure_time: start.arrival_time || "09:00",
    arrival_date: start.arrival_date || today,
    arrival_time: start.arrival_time || "10:00",
    departure_meta: start.arrival_meta || "",
    departure_address: start.arrival_address || "",
    departure_latitude: start.arrival_latitude ?? "",
    departure_longitude: start.arrival_longitude ?? "",
    arrival_meta: "", arrival_address: "", arrival_latitude: "", arrival_longitude: "",
  });
  const [form, setForm] = useState({ title: "", num_passengers: "", contact_phone: "", notes: "" });
  const [legs, setLegs] = useState([emptyLeg()]);
  const [saving, setSaving] = useState(false), [error, setError] = useState("");
  const update = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));
  const updateLeg = (index, name, value) => setLegs(current => current.map((leg, legIndex) => {
    if (legIndex !== index && legIndex !== index + 1) return leg;
    if (legIndex === index) {
      const next = { ...leg, [name]: value };
      if (name === "departure_meta" || name === "departure_address") { next.departure_latitude = ""; next.departure_longitude = ""; }
      if (name === "arrival_meta" || name === "arrival_address") { next.arrival_latitude = ""; next.arrival_longitude = ""; }
      return next;
    }
    const linkedField = { arrival_date: "departure_date", arrival_time: "departure_time", arrival_meta: "departure_meta", arrival_address: "departure_address", arrival_latitude: "departure_latitude", arrival_longitude: "departure_longitude" }[name];
    return linkedField ? { ...leg, [linkedField]: value } : leg;
  }));
  const selectLocation = (index, side, result) => setLegs(current => {
    const selectedLabel = current[index]?.[`${side}_meta`] || result.name;
    return current.map((leg, legIndex) => {
      if (legIndex === index) {
        const prefix = side === "departure" ? "departure" : "arrival";
        return { ...leg, [`${prefix}_meta`]: selectedLabel, [`${prefix}_address`]: result.label, [`${prefix}_latitude`]: result.lat, [`${prefix}_longitude`]: result.lon };
      }
      if (legIndex === index + 1 && side === "arrival") return { ...leg, departure_meta: selectedLabel, departure_address: result.label, departure_latitude: result.lat, departure_longitude: result.lon };
      return leg;
    });
  });
  const routePoints = useMemo(() => legs.flatMap((leg, index) => index === 0 ? [{ latitude: leg.departure_latitude, longitude: leg.departure_longitude }, { latitude: leg.arrival_latitude, longitude: leg.arrival_longitude }] : [{ latitude: leg.arrival_latitude, longitude: leg.arrival_longitude }]), [legs]);
  const submit = async event => { event.preventDefault(); setSaving(true); setError(""); try { await api.createClientBookingRequest({ ...form, legs, num_passengers: form.num_passengers ? Number(form.num_passengers) : null }); onSaved(); } catch (reason) { setError(reason.message || t("client.requestError")); } finally { setSaving(false); } };
  return <DialogShell title={t("client.newRequest")} subtitle={t("client.newRequestHint")} onClose={onClose}><form className="project-form client-booking-form" onSubmit={submit}><p className="client-request-draft">{t("client.requestDraft")}</p><div className="project-form-grid"><label className="project-form-full">{t("client.requestTitle")}<input required name="title" maxLength="180" value={form.title} onChange={update} /></label></div><section className="client-route-editor" aria-label={t("forms.route")}>{legs.map((leg, index) => <fieldset key={index} className="client-route-leg"><legend>{t("forms.route")} {index + 1}</legend><div className="project-form-grid"><label>{t("forms.departureDate")}<input required type="date" min={index === 0 ? today : legs[index - 1].arrival_date} value={leg.departure_date} onChange={event => updateLeg(index, "departure_date", event.target.value)} /></label><label>{t("forms.departureTime")}<input required type="time" value={leg.departure_time} onChange={event => updateLeg(index, "departure_time", event.target.value)} /></label><label>{t("forms.arrivalDate")}<input required type="date" min={leg.departure_date} value={leg.arrival_date} onChange={event => updateLeg(index, "arrival_date", event.target.value)} /></label><label>{t("forms.arrivalTime")}<input required type="time" value={leg.arrival_time} onChange={event => updateLeg(index, "arrival_time", event.target.value)} /></label><label>{t("forms.departure")}<input required maxLength="180" value={leg.departure_meta} onChange={event => updateLeg(index, "departure_meta", event.target.value)} /></label><label>{t("forms.address")}<input maxLength="255" value={leg.departure_address} onChange={event => updateLeg(index, "departure_address", event.target.value)} /></label><RoutePointSearch point={{ place: leg.departure_meta, address: leg.departure_address }} onSelect={result => selectLocation(index, "departure", result)} /><label>{t("forms.arrival")}<input required maxLength="180" value={leg.arrival_meta} onChange={event => updateLeg(index, "arrival_meta", event.target.value)} /></label><label>{t("forms.address")}<input maxLength="255" value={leg.arrival_address} onChange={event => updateLeg(index, "arrival_address", event.target.value)} /></label><RoutePointSearch point={{ place: leg.arrival_meta, address: leg.arrival_address }} onSelect={result => selectLocation(index, "arrival", result)} /></div>{legs.length > 1 && <button type="button" className="route-remove-stop" onClick={() => setLegs(current => current.filter((_, legIndex) => legIndex !== index))}>{t("forms.removeStop")}</button>}</fieldset>)}</section><button type="button" className="route-add-stop client-add-stop" onClick={() => setLegs(current => [...current, emptyLeg(current.at(-1))])}>{t("forms.addStop")}</button><div className="project-form-grid"><label>{t("client.passengers")}<input name="num_passengers" type="number" min="1" max="999" value={form.num_passengers} onChange={update} /></label><label>{t("client.contactPhone")}<input name="contact_phone" maxLength="40" value={form.contact_phone} onChange={update} /></label><label className="project-form-full">{t("client.notes")}<textarea name="notes" maxLength="4000" rows="4" value={form.notes} onChange={update} /></label></div><p className="client-booking-map-hint">{t("forms.locationMapHint")}</p><TransferMap transfer={legs[0]} waypoints={routePoints} />{error && <p className="form-error" role="alert">{error}</p>}<div className="project-form-actions"><button type="button" onClick={onClose}>{t("forms.cancel")}</button><button className="project-create-button" disabled={saving}>{saving ? t("common.loading") : t("client.newRequest")}</button></div></form></DialogShell>;
}

function ClientAccountPanel({ profile, onSaved }) {
  const { t } = useI18n();
  const account = profile.data?.data;
  const [form, setForm] = useState(null), [saving, setSaving] = useState(false), [notice, setNotice] = useState("");
  useEffect(() => { if (account) setForm({ name: account.name || "", surname: account.surname || "", phone: account.phone || "", email: account.email || "" }); }, [account]);
  if (profile.loading || !form) return <p className="status">{t("common.loading")}</p>;
  if (profile.error) return <p className="status error">{profile.error}</p>;
  const update = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }));
  const save = async event => {
    event.preventDefault(); setSaving(true); setNotice("");
    try {
      const result = await api.updateClientProfile(form);
      setNotice(result.email_verification_sent ? t("client.verificationSent") : t("client.profileSaved"));
      onSaved();
    } catch (error) { setNotice(error.message || t("client.profileError")); }
    finally { setSaving(false); }
  };
  return <section className="client-profile-card"><h2>{t("client.accountDetails")}</h2><form className="project-form" onSubmit={save}>
    <div className="project-form-grid"><label>{t("client.username")}<input value={account.username} disabled /></label><label>{t("client.firstName")}<input required name="name" maxLength="32" value={form.name} onChange={update} /></label><label>{t("client.lastName")}<input required name="surname" maxLength="32" value={form.surname} onChange={update} /></label><label>{t("client.phone")}<input name="phone" maxLength="32" value={form.phone} onChange={update} /></label><label className="project-form-full">{t("client.email")}<input name="email" type="email" maxLength="140" value={form.email} onChange={update} /></label></div>
    <p className="client-form-hint">{t("client.secureProfileHint")}</p>{notice && <p className="client-form-notice" role="status">{notice}</p>}
    <div className="project-form-actions"><button className="project-create-button" disabled={saving}>{saving ? t("common.loading") : t("client.saveProfile")}</button></div>
  </form></section>;
}

function ClientApp() {
  const { t } = useI18n(); const auth = useAuth(); const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false), [selectedTransfer, setSelectedTransfer] = useState(null), [tripFilter, setTripFilter] = useState("upcoming"), [creatingRequest, setCreatingRequest] = useState(false);
  const response = useRequest(() => api.clientProjects(), []);
  const requests = useRequest(() => api.clientBookingRequests(), []);
  const profile = useRequest(() => api.clientProfile(), []);
  const transfers = response.data?.data || [];
  const projects = useMemo(() => { const grouped = new Map(); transfers.forEach(transfer => { if (!grouped.has(transfer.project_id)) grouped.set(transfer.project_id, { ...transfer, transfers: [] }); grouped.get(transfer.project_id).transfers.push(transfer); }); return [...grouped.values()]; }, [transfers]);
  const today = isoDate(); const upcoming = transfers.filter(transfer => transfer.departure_date >= today); const historic = transfers.filter(transfer => transfer.departure_date < today);
  const route = location.pathname.replace(/\/+$/, "") || "/client";
  const view = route === "/client/projects" ? "projects" : route === "/client/transfers" ? "trips" : route === "/client/requests" ? "requests" : route === "/client/organization" ? "organization" : route === "/client/account" ? "account" : "overview";
  const pages = { overview: [t("client.overview"), t("client.overviewDescription")], projects: [t("client.projects"), t("client.projectsDescription")], trips: [t("client.trips"), t("client.tripsDescription")], requests: [t("client.requests"), t("client.requestsDescription")], organization: [t("client.organization"), t("client.organizationDescription")], account: [t("client.account"), t("client.accountDescription")] };
  const nav = [["/client", "navigation.clientOverview"], ["/client/projects", "navigation.clientProjects"], ["/client/transfers", "navigation.clientTrips"], ["/client/requests", "navigation.clientRequests"], ["/client/organization", "navigation.clientOrganization"], ["/client/account", "navigation.clientAccount"]];
  const content = () => {
    if (view === "requests") return <ClientBookingRequestList state={requests} onCreate={() => setCreatingRequest(true)} />;
    if (response.loading && view !== "account" && view !== "organization") return <p className="status">{t("client.loading")}</p>;
    if (response.error && view !== "account" && view !== "organization") return <p className="status error">{response.error}</p>;
    if (view === "overview") return <><div className="client-summary"><section><b>{upcoming.length}</b><span>{t("client.tripCount")}</span></section><section><b>{projects.length}</b><span>{t("client.projectCount")}</span></section></div><h2 className="client-section-title">{t("client.overviewTitle")}</h2>{upcoming.length ? <div className="transfer-list">{upcoming.slice(0, 5).map(transfer => <ClientTransferRow key={transfer.transfer_id} transfer={transfer} onOpen={setSelectedTransfer} />)}</div> : <p className="empty-state">{t("client.noUpcoming")}</p>}<section className="client-request-card"><h2>{t("client.requestChange")}</h2><p>{t("client.requestChangeHint")}</p></section></>;
    if (view === "projects") return <ClientProjectList projects={projects} onOpen={setSelectedTransfer} />;
    if (view === "trips") { const visible = tripFilter === "upcoming" ? upcoming : tripFilter === "past" ? historic : transfers; return <><div className="client-trip-filters" role="group" aria-label={t("client.trips")}>{["upcoming", "past", "all"].map(value => <button key={value} type="button" className={tripFilter === value ? "active" : ""} onClick={() => setTripFilter(value)}>{t(`client.${value}`)}</button>)}</div>{visible.length ? <div className="transfer-list">{visible.map(transfer => <ClientTransferRow key={transfer.transfer_id} transfer={transfer} onOpen={setSelectedTransfer} />)}</div> : <p className="empty-state">{t("client.empty")}</p>}</>; }
    if (view === "organization") {
      const organization = profile.data?.data;
      if (profile.loading) return <p className="status">{t("common.loading")}</p>;
      if (profile.error) return <p className="status error">{profile.error}</p>;
      if (!organization?.organization_id) return <p className="empty-state">{t("client.noOrganization")}</p>;
      const contactName = [organization.supervisor_name, organization.supervisor_surname].filter(Boolean).join(" ") || organization.supervisor_signature || "";
      const contactEmail = organization.supervisor_email || organization.organization_contact_email;
      const contactPhone = organization.supervisor_phone || organization.organization_contact_phone;
      const activeProjects = projects.filter(project => project.status !== "CANCELLED").length;
      return <div className="client-organization-layout"><section className="client-organization-card"><span>{t("client.linkedOrganization")}</span><h2>{organization.organization_name}</h2><p>{organization.organization_signature ? `#${organization.organization_id} · ${organization.organization_signature}` : `#${organization.organization_id}`}</p><small>{t("client.organizationPrivacy")}</small></section><section className="client-organization-card"><span>{t("client.organizationContact")}</span>{(contactName || contactEmail || contactPhone) ? <dl className="client-org-contact"><div><dt>{t("client.operationalContact")}</dt><dd>{contactName || "—"}</dd></div>{contactEmail && <div><dt>{t("client.email")}</dt><dd><a href={`mailto:${contactEmail}`}>{contactEmail}</a></dd></div>}{contactPhone && <div><dt>{t("client.phone")}</dt><dd><a href={`tel:${contactPhone.replace(/\s/g, "")}`}>{contactPhone}</a></dd></div>}</dl> : <p>{t("client.noContactDetails")}</p>}</section><section className="client-organization-card client-organization-activity"><span>{t("client.activeProjects")}</span><b>{activeProjects}</b><small>{t("client.projectCount")}</small><span>{t("client.nextTransfer")}</span><strong>{upcoming[0] ? `${upcoming[0].departure_date} · ${shortTime(upcoming[0].departure_time)}` : "—"}</strong></section></div>;
    }
    return <ClientAccountPanel profile={profile} onSaved={profile.retry} />;
  };
  return <div className="app-shell client-workspace"><aside className={menuOpen ? "menu-open" : ""}><div className="mobile-nav-bar"><div className="brand"><Brand /></div><LanguageSwitcher compact /><button type="button" className="mobile-menu-toggle" aria-label={t("driver.toggleNavigation")} aria-expanded={menuOpen} onClick={() => setMenuOpen(value => !value)}><i /><i /><i /></button></div><SidebarProfile /><nav>{nav.map(([path, label]) => <NavLink key={path} to={path} end={path === "/client"} onClick={() => setMenuOpen(false)}>{t(label)}</NavLink>)}</nav>{auth.enabled && <button type="button" className="shell-logout mobile-only-logout" onClick={auth.logout}>{t("common.signOut")}</button>}</aside><main><Page title={pages[view][0]} description={pages[view][1]} guideKey="client" showFilters={false}>{content()}</Page>{selectedTransfer && <ClientTransferDetails transfer={selectedTransfer} onClose={() => setSelectedTransfer(null)} />}{creatingRequest && <ClientBookingRequestDialog onClose={() => setCreatingRequest(false)} onSaved={() => { setCreatingRequest(false); requests.retry(); }} />}</main></div>;
}
function ClientRegistration() {
  const [form, setForm] = useState({ username: "", name: "", surname: "", email: "", password: "", confirmPassword: "" });
  const [state, setState] = useState({ saving: false, error: "", created: false });
  const signIn = () => keycloakClient()?.login({ redirectUri: `${window.location.origin}/client` });
  // Public account creation is deliberately disabled in deployed DriverOS.
  // Keep this route as a helpful, safe destination for old bookmarks rather
  // than leaving an unauthenticated form which can never be submitted.
  if (process.env.REACT_APP_PUBLIC_REGISTRATION_ENABLED !== "true") {
    return <main className="registration-page registration-v2"><section className="registration-brand"><Link to="/dashboard" className="registration-logo"><Brand /></Link><p>Transport operations, clearly organised.</p></section><section className="registration-card" aria-labelledby="registration-title"><p className="registration-kicker">Secure access</p><h1 id="registration-title">Account access is managed by your organisation</h1><p>Ask your dispatcher or administrator to create an account and send a secure password setup link.</p><button className="registration-primary" onClick={signIn}>Sign in to DriverOS</button></section></main>;
  }
  const update = (event) => setForm(value => ({ ...value, [event.target.name]: event.target.value }));
  const resetPassword = () => keycloakClient()?.login({ action: "UPDATE_PASSWORD", redirectUri: window.location.origin });
  const submit = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirmPassword) { setState({ saving: false, error: "Passwords do not match.", created: false }); return; }
    setState({ saving: true, error: "", created: false });
    try {
      await api.publicRegister({ username: form.username, name: form.name, surname: form.surname, email: form.email, password: form.password });
      setState({ saving: false, error: "", created: true });
    } catch (error) { setState({ saving: false, error: error.message, created: false }); }
  };
  return <main className="registration-page registration-v2"><section className="registration-brand"><Link to="/dashboard" className="registration-logo"><Brand /></Link><p>Transport operations, clearly organised.</p><small>Secure access for clients, drivers and the operations team.</small></section><section className="registration-card" aria-labelledby="registration-title">{state.created ? <><p className="registration-kicker">Client account created</p><h1 id="registration-title">You’re ready to sign in</h1><p>Your account has Client access. Use your username and the password you just set.</p><button className="registration-primary" onClick={signIn}>Sign in to DriverOS</button></> : <><p className="registration-kicker">Client access</p><h1 id="registration-title">Create your account</h1><p>Client accounts can view their own projects and transfers. Office, driver and admin accounts are created by your organisation.</p><form onSubmit={submit}><div className="registration-grid"><label>First name<input required name="name" autoComplete="given-name" value={form.name} onChange={update} /></label><label>Surname<input required name="surname" autoComplete="family-name" value={form.surname} onChange={update} /></label><label className="registration-wide">Email<input required type="email" name="email" autoComplete="email" value={form.email} onChange={update} /></label><label className="registration-wide">Username<input required name="username" autoComplete="username" minLength="3" maxLength="32" value={form.username} onChange={update} /><small>3–32 letters, numbers, dots, hyphens or underscores.</small></label><label>Password<input required type="password" name="password" autoComplete="new-password" minLength="12" value={form.password} onChange={update} /><small>At least 12 characters, with upper/lowercase, number and special character.</small></label><label>Confirm password<input required type="password" name="confirmPassword" autoComplete="new-password" minLength="12" value={form.confirmPassword} onChange={update} /></label></div>{state.error && <p className="registration-error" role="alert">{state.error}</p>}<button className="registration-primary" disabled={state.saving}>{state.saving ? "Creating account…" : "Create client account"}</button></form><div className="registration-links"><button type="button" onClick={signIn}>Already have an account? Sign in</button><button type="button" onClick={resetPassword}>Forgot password?</button></div></>}</section></main>;
}

export default function App() {
  const location = useLocation();
  const auth = useAuth();
  if (location.pathname === '/register') return <ClientRegistration />;
  const driverView = location.pathname === '/driver-preview' || location.pathname.startsWith('/driver-preview/');
  const clientView = location.pathname === '/client' || location.pathname.startsWith('/client/');
  if (auth.enabled && auth.ready && auth.hasRole("CLIENT") && !auth.hasRole("ADMIN") && !auth.hasRole("OFFICE") && !clientView) return <Navigate to="/client" replace />;
  if (auth.enabled && auth.ready && auth.hasRole("DRIVER") && !auth.hasRole("ADMIN") && !auth.hasRole("OFFICE") && !driverView) return <Navigate to="/driver-preview" replace />;
  const roles = driverView ? ["DRIVER"] : clientView ? ["CLIENT"] : ["ADMIN", "OFFICE"];
  return <AuthGate roles={roles}>{driverView ? <DriverPreview /> : clientView ? <ClientApp /> : <AdminApp />}</AuthGate>;
}
